// src/provider/index.tsx
import { ThemeProvider } from "styled-components";
import {
  ThemeOrbita,
  ThemeOrbitaDark,
  ThemePayway,
  ThemePaywayDark,
  ThemePrisma,
  ThemePrismaDark,
  ThemeDI
} from "@orbita-ui/brand";
import { jsx } from "react/jsx-runtime";
var Provider = ({ children, variant = "theme-orbita", theme }) => {
  const themes = {
    "theme-orbita": ThemeOrbita,
    "theme-orbita-dark": ThemeOrbitaDark,
    "theme-prisma": ThemePrisma,
    "theme-prisma-dark": ThemePrismaDark,
    "theme-payway": ThemePayway,
    "theme-payway-dark": ThemePaywayDark,
    "theme-di": ThemeDI
  };
  return /* @__PURE__ */ jsx(ThemeProvider, {
    theme: theme || themes[variant],
    children
  });
};

// src/Hooks/useTheme.tsx
import { useTheme as useThemeStyled } from "styled-components";
var useTheme = () => useThemeStyled();

// src/Isotype/Isotype.tsx
import { useEffect, useState } from "react";
import styled from "styled-components";

// src/utils/getFromCDN.ts
var env = {
  test: "https://static-dev.prismamp.com/orbita/",
  develop: "https://static-dev.prismamp.com/orbita/",
  "pre-release": "https://static-pre.prismamp.com/orbita/",
  main: "https://static.prismamp.com/orbita/"
};
var getFromCDN = (path) => {
  const CDN_URL = env["main"];
  return fetch(CDN_URL + path);
};
var getCDNUrl = (path) => {
  const CDN_URL = env["main"];
  return CDN_URL + path;
};

// src/Isotype/getIso.ts
var getIso = (slug, inverted) => {
  const finalName = `${slug}Iso${inverted ? "Inv" : ""}`;
  return getCDNUrl("assets/images/brand/" + finalName + ".svg");
};

// src/Isotype/Isotype.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var isoSizes = {
  xsmall: 24,
  small: 32,
  medium: 40,
  large: 48
};
var IsoComponent = styled.img`
  width: ${({ size }) => `${isoSizes[size]}px`};
  height: ${({ size }) => `${isoSizes[size]}px`};
`;
var Isotype = ({ size = "medium", inverted, ...htmlProps }) => {
  const theme = useTheme();
  const [isoLink, setIsoLink] = useState("");
  const slug = theme.slug.replace("-dark", "");
  useEffect(() => {
    const link = getIso(slug, inverted);
    setIsoLink(link);
  }, [slug, inverted]);
  return /* @__PURE__ */ jsx2(IsoComponent, {
    src: isoLink,
    size,
    ...htmlProps
  });
};
Isotype.displayName = "Isotype";

// src/Icon/IconBase.tsx
import { useEffect as useEffect2, useState as useState2 } from "react";
import styled2 from "styled-components";

// src/Icon/getIcon.ts
var getParsedIcon = async (name) => {
  const file = await getFromCDN("assets/images/icons/" + name + ".svg");
  const fileStr = await file.text();
  return fileStr.toString().replace(/^.*<\?xml.*?\>/g, "").replace(/<svg.*?>/g, "").replace(/<\/svg>/g, "").replace(/<title.*?/, "").replace(/#3B4059/g, "currentColor").replace(/#373E45/g, "currentColor").trim();
};

// src/Icon/IconBase.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var iconSizes = {
  S: "16px",
  M: "24px",
  L: "32px"
};
var iconColors = {
  primary: { color: "primary", variant: "default" },
  primaryStronger: { color: "primary", variant: "stronger" },
  primaryStrong: { color: "primary", variant: "strong" },
  secondary: { color: "secondary", variant: "default" },
  secondaryStronger: { color: "secondary", variant: "stronger" },
  secondaryStrong: { color: "secondary", variant: "strong" },
  neutral: { color: "neutral", variant: "default" },
  neutralWeak: { color: "neutral", variant: "weak" },
  neutralWeaker: { color: "neutral", variant: "weaker" },
  neutralStrong: { color: "neutral", variant: "strong" },
  success: { color: "success", variant: "default" },
  danger: { color: "danger", variant: "default" },
  warning: { color: "warning", variant: "default" },
  info: { color: "info", variant: "default" },
  purple: { color: "purple", variant: 5 },
  orange: { color: "orange", variant: 6 },
  pink: { color: "pink", variant: 5 },
  lightblue: { color: "lightblue", variant: 5 }
};
var getIconColor = (color, theme) => {
  if (["purple", "orange", "pink", "lightblue"].includes(color)) {
    return theme.colors[iconColors[color].color][iconColors[color].variant];
  } else {
    return theme.colors[iconColors[color].color].ic[iconColors[color].variant];
  }
};
var IconBaseComponent = styled2.svg.attrs({
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg",
  xmlnsXlink: "http://www.w3.org/1999/xlink",
  viewBox: "0 0 24 24"
})`
  width: ${({ size }) => iconSizes[size != null ? size : "M"]};
  height: ${({ size }) => iconSizes[size != null ? size : "M"]};
  color: ${({ theme, color }) => getIconColor(color, theme)};
`;
var Icon = ({ size, color = "primary", name, ...htmlProps }) => {
  const [icon, setIcon] = useState2("");
  useEffect2(() => {
    const getIc = async () => {
      const ic = await getParsedIcon(name);
      setIcon(ic.trim());
    };
    getIc();
  }, [name]);
  return /* @__PURE__ */ jsx3(IconBaseComponent, {
    size,
    color,
    ...htmlProps,
    dangerouslySetInnerHTML: { __html: icon },
    alt: name
  });
};
Icon.displayName = "Icon";

// src/Text/Text.tsx
import styled3 from "styled-components";
import { jsx as jsx4 } from "react/jsx-runtime";
var textColors = {
  primaryStronger: { color: "primary", variant: "stronger" },
  primaryStrong: { color: "primary", variant: "strong" },
  primary: { color: "primary", variant: "default" },
  primaryWeak: { color: "primary", variant: "weak" },
  secondaryStronger: { color: "secondary", variant: "stronger" },
  secondaryStrong: { color: "secondary", variant: "strong" },
  secondary: { color: "secondary", variant: "default" },
  secondaryWeak: { color: "secondary", variant: "weak" },
  neutralStrong: { color: "neutral", variant: "strong" },
  neutral: { color: "neutral", variant: "default" },
  neutralWeak: { color: "neutral", variant: "weak" },
  neutralWeaker: { color: "neutral", variant: "weaker" },
  success: { color: "success", variant: "default" },
  danger: { color: "danger", variant: "default" },
  warning: { color: "warning", variant: "default" },
  info: { color: "info", variant: "default" }
};
var getContrast = (variant, bold, underline, uppercase) => {
  switch (variant) {
    case "body":
      return bold ? "highContrast" : underline ? "midContrast-underline" : "midContrast";
    case "caption":
      return bold ? "highContrast" : "midContrast";
    case "overline":
      return uppercase ? "highContrast-uppercase" : "highContrast";
  }
};
var TextComponent = styled3.p`
  /* Using theme */
  color: ${({ theme, color }) => theme.colors[textColors[color].color].text[textColors[color].variant]};
  font-family: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].fontFamily};
  font-size: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].fontSize};
  letter-spacing: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].letterSpacing};
  line-height: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].lineHeight};
  font-weight: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].fontWeight};
  text-decoration: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].underline ? "underline" : ""};
  text-transform: ${({ theme, variant, size, bold, underline, uppercase }) => theme.fonts[variant][variant === "body" ? size : "default"][getContrast(variant, bold, underline, uppercase)].uppercase ? "uppercase" : ""};
`;
var Text = ({
  as = "p",
  bold = false,
  children,
  size = "s",
  underline = false,
  uppercase = false,
  variant = "body",
  color = "neutral",
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsx4(TextComponent, {
    as,
    ...htmlProps,
    variant,
    bold,
    size,
    underline,
    uppercase,
    color,
    children
  });
};
Text.displayName = "Text";

// src/Heading/Heading.tsx
import styled4 from "styled-components";
import { jsx as jsx5 } from "react/jsx-runtime";
var textColors2 = {
  primaryStronger: { color: "primary", variant: "stronger" },
  primaryStrong: { color: "primary", variant: "strong" },
  primary: { color: "primary", variant: "default" },
  primaryWeak: { color: "primary", variant: "weak" },
  secondaryStronger: { color: "secondary", variant: "stronger" },
  secondaryStrong: { color: "secondary", variant: "strong" },
  secondary: { color: "secondary", variant: "default" },
  secondaryWeak: { color: "secondary", variant: "weak" },
  neutralStrong: { color: "neutral", variant: "strong" },
  neutral: { color: "neutral", variant: "default" },
  neutralWeak: { color: "neutral", variant: "weak" },
  neutralWeaker: { color: "neutral", variant: "weaker" },
  success: { color: "success", variant: "default" },
  danger: { color: "danger", variant: "default" },
  warning: { color: "warning", variant: "default" },
  info: { color: "info", variant: "default" }
};
var defautlTag = {
  xs: "h6",
  s: "h5",
  m: "h4",
  l: "h3",
  xl: "h2",
  xxl: "h1"
};
var getContrast2 = (bold, size) => {
  if (size === "s" || size === "xs")
    return bold ? "highContrast" : "midContrast";
  else
    return bold ? "highContrast" : "lowContrast";
};
var HeadingComponent = styled4.h1`
  /* Using theme */
  color: ${({ theme, color }) => theme.colors[textColors2[color].color].text[textColors2[color].variant]};
  font-family: ${({ theme, size, bold }) => theme.fonts.title[size][getContrast2(bold, size)].fontFamily};
  font-size: ${({ theme, size, bold }) => theme.fonts.title[size][getContrast2(bold, size)].fontSize};
  letter-spacing: ${({ theme, size, bold }) => theme.fonts.title[size][getContrast2(bold, size)].letterSpacing};
  line-height: ${({ theme, size, bold }) => theme.fonts.title[size][getContrast2(bold, size)].lineHeight};
  font-weight: ${({ theme, size, bold }) => theme.fonts.title[size][getContrast2(bold, size)].fontWeight};
`;
var Heading = ({
  children,
  as = void 0,
  size = "m",
  bold = false,
  color = "neutral",
  ...restProps
}) => {
  return /* @__PURE__ */ jsx5(HeadingComponent, {
    ...restProps,
    bold,
    size,
    as: as != null ? as : defautlTag[size],
    color,
    children
  });
};
Heading.displayName = "Heading";

// src/TextLink/TextLink.tsx
import styled5 from "styled-components";
import { jsx as jsx6, jsxs } from "react/jsx-runtime";
var TextLinkComponnet = styled5.a`
  cursor: pointer;
  transition: all 0.8s;
  font-family: ${({ theme, size }) => theme.fonts.body[size]["midContrast"].fontFamily};
  font-size: ${({ theme, size }) => theme.fonts.body[size]["midContrast"].fontSize};
  letter-spacing: ${({ theme, size }) => theme.fonts.body[size]["midContrast"].letterSpacing};
  line-height: ${({ theme, size }) => theme.fonts.body[size]["midContrast"].lineHeight};
  font-weight: ${({ theme, size }) => theme.fonts.body[size]["midContrast"].fontWeight};
  color: ${({ theme, inverted, variant }) => inverted ? theme.colors.neutral.text.weaker : variant === "info" ? theme.colors.info.text.default : theme.colors.neutral.text.default};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  flex-direction: ${({ iconAlign }) => iconAlign && iconAlign === "left" ? "row-reverse" : "row"};

  .icon-color {
    color: ${({ theme, inverted, variant }) => inverted ? theme.colors.neutral.ic.weaker : variant === "info" ? theme.colors.info.ic.default : theme.colors.neutral.ic.default};
  }

  :hover {
    text-decoration: ${({ theme, size }) => theme.fonts.body[size]["midContrast-underline"].underline && "underline"};
  }

  :focus {
    border-radius: ${({ theme }) => theme.borderRadius.xsmall};
    outline: 1px solid;
  }

  :focus-visible {
    box-shadow: ${({ theme }) => theme.focusStroke.default};
    border-radius: ${({ theme }) => theme.borderRadius.xsmall};
    outline: 1px solid;
  }

  :visited {
    color: ${({ theme, inverted, variant }) => inverted ? theme.colors.neutral.text.weak : variant === "info" ? theme.colors.blue[5] : theme.colors.neutral.text.strong};
    .icon-color {
      color: ${({ theme, inverted, variant }) => inverted ? theme.colors.neutral.text.weak : variant === "info" ? theme.colors.blue[5] : theme.colors.neutral.text.default};
    }
  }
`;
var TextLink = ({
  children,
  size = "s",
  href,
  inverted = false,
  isExternal = false,
  icon = void 0,
  iconAlign = "right",
  variant = "info",
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsxs(TextLinkComponnet, {
    size,
    inverted,
    target: isExternal ? "_blank" : void 0,
    iconAlign,
    href,
    variant,
    ...htmlProps,
    children: [
      children,
      icon && /* @__PURE__ */ jsx6(Icon, {
        name: icon,
        size: size.toUpperCase(),
        className: "icon-color"
      })
    ]
  });
};
TextLink.displayName = "TextLink";

// src/RadioButton/Radiobutton.tsx
import styled6, { css } from "styled-components";
import { jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
var Fill = styled6.div`
  width: 12px;
  height: 12px;
  border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  background-color: ${({ theme }) => theme.colors.primary.bg.default};
`;
var RadiobuttonComponent = styled6.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  vertical-align: middle;
  flex-direction: ${({ textAlign }) => textAlign === "left" ? "row-reverse" : "row"};
  gap: ${({ theme }) => theme.spacing.xsmall};

  .radio-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    background: ${({ theme }) => theme.colors.neutral.bg.weaker};
    width: 20px;
    height: 20px;
    outline: 0px;
    border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
    cursor: pointer;
    border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
    appearance: none;
  }

  ${({ isChecked, theme }) => isChecked && css`
      .radio-container {
        border: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
      }
      .label-text {
        color: ${({ theme: theme2 }) => theme2.colors.neutral.text.strong} !important;
      }
    `};

  ${({ invalid, isChecked }) => invalid && !isChecked && css`
      .radio-container {
        border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`};
      }
      .label-text {
        color: ${({ theme }) => theme.colors.neutral.text.strong} !important;
      }
    `};

  ${({ isDisabled, isChecked, invalid }) => !isDisabled && css`
      :hover {
        .radio-container {
          background-color: ${({ theme }) => isChecked ? theme.colors.primary.bg.weak : invalid ? theme.colors.neutral.bg.weak : theme.colors.primary.bg.weak};
          border: ${({ theme }) => isChecked ? `${theme.borderStroke.small} solid ${theme.colors.primary.border.default}` : invalid ? `${theme.borderStroke.small} solid ${theme.colors.danger.border.default}` : `${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
        }
      }
    `}

  ${({ isDisabled, invalid }) => !isDisabled && css`
      :focus-visible {
        outline: none;
        .radio-container {
          box-shadow: ${({ theme }) => invalid ? theme.focusStroke.error : theme.focusStroke.default};
          ${Fill} {
            background-color: ${({ theme }) => theme.colors.primary.bg.defautl};
          }
        }
      }
    `}

    ${({ isDisabled, invalid }) => isDisabled && css`
      cursor: default !important;
      .radio-container {
        background-color: ${({ theme }) => theme.colors.neutral.bg.weak};
        border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};
        display: ${() => invalid ? "none" : "inherit"};
      }
      ${Fill} {
        background-color: ${({ theme }) => theme.colors.neutral.border.default};
      }
      .label-text {
        display: ${() => invalid ? "none" : "inherit"};
        color: ${({ theme }) => theme.colors.neutral.text.weak} !important;
      }
    `}
  

  input {
    cursor: inherit;
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    opacity: 0;
  }

  .label-text {
    color: ${({ theme }) => theme.colors.neutral.text.default};
  }
`;
var Radiobutton = ({
  isChecked,
  label,
  onChange,
  isDisabled = false,
  className,
  textAlign = "right",
  invalid = false,
  name,
  value,
  ...htmlprops
}) => {
  return /* @__PURE__ */ jsxs2(RadiobuttonComponent, {
    className,
    isChecked,
    textAlign,
    invalid,
    isDisabled,
    ...htmlprops,
    children: [
      /* @__PURE__ */ jsxs2("span", {
        className: "radio-container",
        children: [
          /* @__PURE__ */ jsx7("input", {
            type: "radio",
            checked: isChecked,
            onChange: !invalid ? onChange : void 0,
            disabled: isDisabled,
            name,
            value
          }),
          isChecked && /* @__PURE__ */ jsx7(Fill, {})
        ]
      }),
      label && /* @__PURE__ */ jsx7(Text, {
        as: "span",
        className: "label-text",
        children: label
      })
    ]
  });
};
Radiobutton.displayName = "Radiobutton";

// src/RadioButton/RadioGroup.tsx
import styled7 from "styled-components";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var RadioGroupWrapper = styled7.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing["xsmall"]};

  .icon-invalid {
    vertical-align: middle;
  }
`;
var RadioGroupContainer = styled7.div`
  display: flex;
  flex-direction: ${({ variant }) => variant === "vertical" ? "column" : "row"};
  align-items: flex-start;
  vertical-align: middle;
  min-height: ${({ theme }) => theme.spacing.large};
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: ${({ theme, variant }) => variant === "vertical" ? theme.spacing["xsmall"] : theme.spacing.large};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
`;
var RadioGroup = ({
  label,
  required = false,
  optional = false,
  helperText = void 0,
  invalid = false,
  fullWidth = false,
  variant = "horizontal",
  onChange,
  value,
  options
}) => {
  return /* @__PURE__ */ jsxs3(RadioGroupWrapper, {
    children: [
      /* @__PURE__ */ jsxs3(Text, {
        variant: "caption",
        as: "span",
        children: [
          label,
          " ",
          required && /* @__PURE__ */ jsx8(Text, {
            variant: "caption",
            as: "span",
            color: "neutralWeak",
            children: "(Obligatorio)"
          }),
          optional && /* @__PURE__ */ jsx8(Text, {
            variant: "caption",
            as: "span",
            color: "neutralWeak",
            children: "(Opcional)"
          })
        ]
      }),
      /* @__PURE__ */ jsx8(RadioGroupContainer, {
        fullWidth,
        variant,
        children: options.map((item, index) => /* @__PURE__ */ jsx8(Radiobutton, {
          label: item.name,
          isChecked: value === item.value,
          onChange,
          name: item.value,
          value: item.value
        }, item.name + index))
      }),
      /* @__PURE__ */ jsxs3(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        className: "helper-text",
        children: [
          invalid && helperText && /* @__PURE__ */ jsx8(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          helperText
        ]
      })
    ]
  });
};

// src/Checkbox/Checkbox.tsx
import styled8, { css as css2 } from "styled-components";
import { Fragment, jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var CheckboxComponent = styled8.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  vertical-align: middle;
  flex-direction: ${({ textAlign }) => textAlign === "left" ? "row-reverse" : "row"};
  gap: ${({ theme }) => theme.spacing.xsmall};

  .check-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    background: ${({ theme }) => theme.colors.neutral.bg.weaker};
    width: ${({ theme, size }) => size === "m" ? "20px" : theme.spacing.medium};
    height: ${({ theme, size }) => size === "m" ? "20px" : theme.spacing.medium};
    outline: 0px;
    border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
    cursor: pointer;
    border-radius: ${({ theme }) => theme.borderRadius["xsmall"]};
    appearance: none;
  }

  ${({ isChecked, isIndeterminate, theme }) => (isChecked || isIndeterminate) && css2`
      .check-container {
        background: ${theme.colors.primary.bg.default};
        border: ${({ theme: theme2 }) => `${theme2.borderStroke.small} solid ${theme2.colors.primary.border.default}`};
      }
      .label-text {
        color: ${({ theme: theme2 }) => theme2.colors.neutral.text.strong} !important;
      }
    `};

  ${({ invalid, isChecked, isIndeterminate }) => invalid && !isChecked && !isIndeterminate && css2`
      .check-container {
        border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`};
      }
      .label-text {
        color: ${({ theme }) => theme.colors.neutral.text.strong} !important;
      }
    `};

  ${({ isDisabled, isChecked, invalid, isIndeterminate }) => !isDisabled && css2`
      :hover {
        .check-container {
          background-color: ${({ theme }) => isChecked || isIndeterminate ? theme.colors.primary.bg.strong : invalid ? theme.colors.neutral.bg.weak : theme.colors.primary.bg.weak};
          border: ${({ theme }) => isChecked || isIndeterminate ? `${theme.borderStroke.small} solid ${theme.colors.primary.border.strong}` : invalid ? `${theme.borderStroke.small} solid ${theme.colors.danger.border.default}` : `${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
        }
      }
    `}

  ${({ isDisabled, isChecked, invalid, isIndeterminate }) => !isDisabled && css2`
      :focus-visible {
        outline: none;
        .check-container {
          background-color: ${({ theme }) => isChecked || isIndeterminate ? theme.colors.primary.bg.strong : invalid ? theme.colors.neutral.bg.weak : theme.colors.primary.bg.weak};
          border: ${({ theme }) => isChecked || isIndeterminate ? `${theme.borderStroke.small} solid ${theme.colors.primary.border.strong}` : invalid ? `${theme.borderStroke.small} solid ${theme.colors.danger.border.default}` : `${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
          box-shadow: ${({ theme }) => invalid ? theme.focusStroke.error : theme.focusStroke.default};
        }
      }
    `}

    ${({ isDisabled, invalid }) => isDisabled && css2`
      cursor: default;
      .check-container {
        background-color: ${({ theme }) => theme.colors.neutral.bg.default};
        border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};
        display: ${() => invalid ? "none" : "inherit"};
        cursor: default;
      }
      .icon-styles {
        color: ${({ theme }) => theme.colors.neutral.ic.default} !important;
      }
      .label-text {
        display: ${() => invalid ? "none" : "inherit"};
        color: ${({ theme }) => theme.colors.neutral.text.weak} !important;
      }
      input {
        cursor: default !important;
      }
    `}
  

  input {
    cursor: inherit;
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    opacity: 0;
  }

  .icon-styles {
    user-select: none;
    display: inline-block;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.gray[1]};
  }

  .label-text {
    color: ${({ theme }) => theme.colors.neutral.text.default};
  }

  .required-text {
    color: ${({ theme }) => theme.colors.danger.text.default};
  }
`;
var Checkbox = ({
  label = void 0,
  isChecked,
  isDisabled = false,
  isRequired = false,
  isIndeterminate = false,
  invalid = false,
  className,
  size = "s",
  textAlign = "right",
  onChange,
  ...htmlprops
}) => {
  return /* @__PURE__ */ jsxs4(CheckboxComponent, {
    className,
    isChecked,
    size,
    isIndeterminate,
    textAlign,
    invalid,
    isDisabled,
    ...htmlprops,
    children: [
      /* @__PURE__ */ jsxs4("span", {
        className: "check-container",
        children: [
          /* @__PURE__ */ jsx9("input", {
            type: "checkbox",
            defaultChecked: isChecked,
            onChange: !invalid ? onChange : void 0,
            required: isRequired,
            disabled: isDisabled
          }),
          isChecked && /* @__PURE__ */ jsx9(Icon, {
            size: "S",
            name: "CheckIcon",
            className: "icon-styles"
          }),
          isIndeterminate && !isChecked && /* @__PURE__ */ jsx9(Icon, {
            size: "S",
            name: "MinusIcon",
            className: "icon-styles"
          })
        ]
      }),
      label && /* @__PURE__ */ jsx9(Text, {
        as: "span",
        className: "label-text",
        children: /* @__PURE__ */ jsxs4(Fragment, {
          children: [
            label,
            isRequired && /* @__PURE__ */ jsx9(Text, {
              as: "span",
              className: "required-text",
              children: "*"
            })
          ]
        })
      })
    ]
  });
};
Checkbox.displayName = "Checkbox";

// src/Button/Button.tsx
import styled11, { css as css3 } from "styled-components";
import { fontSize, fontWeight, letterSpacing, lineHeight } from "@orbita-ui/brand";

// src/Button/dictionary.ts
import { hexToRgba } from "@orbita-ui/brand";
var buttonSizes = {
  small: {
    padding: "2.5px 12px",
    heigth: "32px"
  },
  medium: {
    padding: "6.5px 16px",
    heigth: "40px"
  },
  large: {
    padding: "10.5px 16px",
    heigth: "48px"
  },
  fullWidth: {
    padding: "10.5px 16px",
    heigth: "48px"
  }
};
var buttonColors = (theme) => {
  const { colors: palette, opacity: opacity2 } = theme;
  return {
    filled: {
      primary: {
        default: {
          background: palette.primary.bg.default,
          border: palette.primary.border.default,
          color: palette.gray[1],
          icon: palette.gray[1]
        },
        hover: {
          background: palette.primary.bg.stronger,
          border: palette.primary.border.stronger,
          color: palette.gray[1],
          icon: palette.gray[1]
        },
        pressed: {
          background: palette.primary.bg.stronger,
          border: palette.primary.border.stronger,
          color: palette.gray[1],
          icon: palette.gray[1]
        },
        disabled: {
          background: palette.neutral.bg.default,
          border: palette.neutral.border.weak,
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      },
      destructive: {
        default: {
          background: palette.danger.bg.default,
          border: "transparent",
          color: palette.gray[1],
          icon: palette.gray[1]
        },
        hover: {
          background: palette.danger.bg.strong,
          border: "transparent",
          color: palette.gray[1],
          icon: palette.gray[1]
        },
        pressed: {
          background: palette.danger.bg.strong,
          border: "transparent",
          color: palette.gray[1],
          icon: palette.gray[1]
        },
        disabled: {
          background: palette.neutral.bg.default,
          border: palette.neutral.border.weak,
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      },
      inverted: {
        default: {
          background: palette.neutral.bg.weaker,
          border: palette.neutral.bg.weaker,
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        hover: {
          background: palette.neutral.bg.default,
          border: "transparent",
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        pressed: {
          background: palette.neutral.bg.strong,
          border: "transparent",
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        disabled: {
          background: palette.neutral.bg.strong,
          border: "transparent",
          color: palette.neutral.text.default,
          icon: palette.neutral.ic.default
        }
      }
    },
    outline: {
      primary: {
        default: {
          background: "transparent",
          border: hexToRgba(palette.primary.border.default, opacity2[20]),
          color: palette.primary.text.default,
          icon: palette.primary.ic.default
        },
        hover: {
          background: "transparent",
          border: palette.primary.border.strong,
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        pressed: {
          background: palette.primary.bg.weak,
          border: palette.primary.border.stronger,
          color: palette.primary.text.stronger,
          icon: palette.primary.ic.stronger
        },
        disabled: {
          background: "transparent",
          border: palette.neutral.border.default,
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      },
      destructive: {
        default: {
          background: "transparent",
          border: hexToRgba(palette.danger.border.default, opacity2[20]),
          color: palette.danger.text.default,
          icon: palette.danger.ic.default
        },
        hover: {
          background: "transparent",
          border: palette.danger.border.default,
          color: palette.danger.text.default,
          icon: palette.danger.ic.default
        },
        pressed: {
          background: palette.danger.bg.weak,
          border: palette.danger.border.default,
          color: palette.danger.text.default,
          icon: palette.danger.ic.default
        },
        disabled: {
          background: "transparent",
          border: palette.neutral.border.default,
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      },
      inverted: {
        default: {
          background: "transparent",
          border: hexToRgba(palette.neutral.border.default, opacity2[40]),
          color: palette.neutral.text.weaker,
          icon: palette.neutral.ic.weak
        },
        hover: {
          background: "transparent",
          border: palette.neutral.border.weak,
          color: palette.neutral.text.weaker,
          icon: palette.neutral.ic.weaker
        },
        pressed: {
          background: palette.neutral.bg.strong,
          border: palette.neutral.border.weak,
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        disabled: {
          background: "transparent",
          border: palette.neutral.border.strong,
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      }
    },
    ghost: {
      primary: {
        default: {
          background: "transparent",
          border: "transparent",
          color: palette.primary.text.default,
          icon: palette.primary.ic.default
        },
        hover: {
          background: palette.primary.bg.weak,
          border: "transparent",
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        pressed: {
          background: palette.primary.bg.weak,
          border: "transparent",
          color: palette.secondary.text.stronger,
          icon: palette.secondary.ic.stronger
        },
        disabled: {
          background: "transparent",
          border: "transparent",
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      },
      destructive: {
        default: {
          background: "transparent",
          border: "transparent",
          color: palette.danger.text.default,
          icon: palette.danger.ic.default
        },
        hover: {
          background: palette.danger.bg.weak,
          border: "transparent",
          color: palette.danger.text.default,
          icon: palette.danger.ic.default
        },
        pressed: {
          background: palette.danger.bg.weak,
          border: "transparent",
          color: palette.danger.text.default,
          icon: palette.danger.ic.default
        },
        disabled: {
          background: "transparent",
          border: "transparent",
          color: palette.neutral.text.weak,
          icon: palette.neutral.ic.weak
        }
      },
      inverted: {
        default: {
          background: "transparent",
          border: "transparent",
          color: palette.neutral.text.weaker,
          icon: palette.neutral.ic.weaker
        },
        hover: {
          background: palette.neutral.bg.default,
          border: "transparent",
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        pressed: {
          background: palette.neutral.bg.strong,
          border: "transparent",
          color: palette.primary.text.strong,
          icon: palette.primary.ic.strong
        },
        disabled: {
          background: palette.neutral.bg.strong,
          border: "transparent",
          color: palette.neutral.text.default,
          icon: palette.neutral.ic.default
        }
      }
    }
  };
};

// src/Spinner/Leap.tsx
import styled9, { keyframes } from "styled-components";

// src/Spinner/dictionary.ts
var SpinnerDictionary = {
  leap: {
    small: {
      size: "20px",
      spacing: "2xsmall"
    },
    medium: {
      size: "40px",
      spacing: "2xsmall"
    },
    large: {
      size: "56px",
      spacing: "3xsmall"
    }
  },
  ring: {
    small: {
      size: "20px",
      spacing: "3xsmall",
      border: "2px"
    },
    medium: {
      size: "40px",
      spacing: "2xsmall",
      border: "4px"
    },
    large: {
      size: "56px",
      spacing: "2xsmall",
      border: "6px"
    }
  }
};

// src/Spinner/Leap.tsx
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var LeapWrapper = styled9.div`
  padding: ${({ theme, size }) => size === "small" ? theme.spacing["3xsmall"] : theme.spacing["2xsmall"]};
`;
var LeapContainer = styled9.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${({ size }) => SpinnerDictionary.leap[size].size};
  height: ${({ size }) => SpinnerDictionary.leap[size].size};
`;
var leapFrog = (size) => keyframes`
  0% {
    transform: translateX(0) rotate(0deg);
  }

  33.333% {
    transform: translateX(0) rotate(180deg);
  }

  66.666% {
    transform: translateX(calc(${size} * -0.4)) rotate(180deg);
  }

  99.999% {
    transform: translateX(calc(${size} * -0.8)) rotate(180deg);
  }

  100% {
    transform: translateX(0) rotate(0deg);
  }
`;
var LeapDot = styled9.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  margin: 0px ${({ theme, size }) => theme.spacing[SpinnerDictionary.leap[size].spacing]};

  &::before {
    content: '';
    display: block;
    height: calc(${({ size }) => SpinnerDictionary.leap[size].size} * 0.2);
    width: calc(${({ size }) => SpinnerDictionary.leap[size].size} * 0.2);
    border-radius: 50%;
    background-color: ${({ theme, inverted }) => inverted ? theme.colors.gray[1] : theme.colors.primary.bg.default};
  }

  :nth-child(1) {
    animation: ${({ size }) => leapFrog(SpinnerDictionary.leap[size].size)} 2.5s ease
      infinite;
  }

  :nth-child(2) {
    transform: translateX(calc(${({ size }) => SpinnerDictionary.leap[size].size} * 0.4));
    animation: ${({ size }) => leapFrog(SpinnerDictionary.leap[size].size)} 2.5s ease
      calc(40s / -1.5) infinite;
  }

  :nth-child(3) {
    transform: translateX(calc(${({ size }) => SpinnerDictionary.leap[size].size} * 0.8))
      rotate(0deg);
    animation: ${({ size }) => leapFrog(SpinnerDictionary.leap[size].size)} 2.5s ease
      calc(2.5s / -3) infinite;
  }
`;
var Leap = ({ size, inverted }) => /* @__PURE__ */ jsx10(LeapWrapper, {
  size,
  children: /* @__PURE__ */ jsxs5(LeapContainer, {
    size,
    children: [
      /* @__PURE__ */ jsx10(LeapDot, {
        size,
        inverted
      }),
      /* @__PURE__ */ jsx10(LeapDot, {
        size,
        inverted
      }),
      /* @__PURE__ */ jsx10(LeapDot, {
        size,
        inverted
      })
    ]
  })
});

// src/Spinner/Ring.tsx
import styled10, { keyframes as keyframes2 } from "styled-components";
import { jsx as jsx11 } from "react/jsx-runtime";
var motion = () => keyframes2`
  0% {
      transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;
var Wrapper = styled10.div`
  display: inline-block;
  position: relative;
  height: ${({ size }) => SpinnerDictionary.ring[size].size};
  width: ${({ size }) => SpinnerDictionary.ring[size].size};
  padding: ${({ theme, size }) => size === "small" ? theme.spacing["3xsmall"] : theme.spacing["2xsmall"]};
`;
var RingSpinner = styled10.div`
  box-sizing: border-box;
  display: block;
  position: absolute;
  height: ${({ size }) => SpinnerDictionary.ring[size].size};
  width: ${({ size }) => SpinnerDictionary.ring[size].size};
  border: ${({ theme, size, inverted }) => `${SpinnerDictionary.ring[size].border} solid ${inverted ? theme.colors.gray[1] : theme.colors.primary.bg.default}`};
  border-radius: 50%;
  animation: ${motion()} 1.2s cubic-bezier(0.5, 0.5, 0.5, 0.5) infinite;
  border-color: ${({ theme, inverted }) => `${inverted ? theme.colors.gray[1] : theme.colors.primary.bg.default} ${theme.colors.gray[3]} ${theme.colors.gray[3]} ${theme.colors.gray[3]}`};
  :nth-child(1) {
    animation-delay: -0.45s;
  }
  :nth-child(2) {
    animation-delay: -0.3s;
  }
  :nth-child(3) {
    animation-delay: -0.15s;
  }
`;
var Ring = ({ size, inverted }) => /* @__PURE__ */ jsx11(Wrapper, {
  size,
  children: /* @__PURE__ */ jsx11(RingSpinner, {
    size,
    inverted
  })
});

// src/Spinner/Spinner.tsx
import { Fragment as Fragment2, jsx as jsx12 } from "react/jsx-runtime";
var Spinner = ({
  size = "large",
  variant = "ring",
  inverted = false
}) => {
  return /* @__PURE__ */ jsx12(Fragment2, {
    children: variant === "ring" ? /* @__PURE__ */ jsx12(Ring, {
      size,
      inverted
    }) : /* @__PURE__ */ jsx12(Leap, {
      size,
      inverted
    })
  });
};
Spinner.displayName = "Spinner";

// src/Button/Button.tsx
import { Fragment as Fragment3, jsx as jsx13, jsxs as jsxs6 } from "react/jsx-runtime";
var ButtonComponent = styled11.button`
  cursor: pointer;
  display: inline-flex;
  flex-direction: ${({ iconAlign }) => iconAlign === "right" ? "row-reverse" : "row"};
  justify-content: center;
  justify-items: center;
  align-items: center;
  gap: ${({ theme, size }) => size === "small" ? theme.spacing["2xsmall"] : theme.spacing.xsmall};
  height: ${({ size }) => buttonSizes[size].heigth};
  width: ${({ size }) => size === "fullWidth" ? "100%" : "auto"};
  min-width: ${({ onlyIcon, size }) => !onlyIcon ? "46px" : buttonSizes[size].heigth};
  padding: ${({ size }) => buttonSizes[size].padding};
  border-radius: 100px;

  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  font-size: ${({ size }) => size === "small" ? fontSize.medium : fontSize.large};
  letter-spacing: ${letterSpacing.medium};
  line-height: ${lineHeight.medium};
  font-weight: ${fontWeight.midContrast};

  background: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].default.background};
  background: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].default.background};
  border: ${({ theme, variant, color }) => `${theme.borderStroke.medium} solid ${buttonColors(theme)[variant][color].default.border} `};
  color: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].default.color};
  transition: all 0.8s;

  .icon-styles {
    color: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].default.icon};
    vertical-align: middle;
    transition: all 0.8s;
  }

  :hover {
    background: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].hover.background};
    border: ${({ theme, variant, color }) => `${theme.borderStroke.medium} solid ${buttonColors(theme)[variant][color].hover.border} `};
    color: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].hover.color};

    .icon-styles {
      color: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].hover.icon};
    }
  }

  :active {
    background: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].pressed.background};
    border: ${({ theme, variant, color }) => `${theme.borderStroke.medium} solid ${buttonColors(theme)[variant][color].pressed.border} `};
    color: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].pressed.color};

    .icon-styles {
      color: ${({ theme, variant, color }) => buttonColors(theme)[variant][color].pressed.icon};
    }
  }

  :focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusStroke.default};
  }

  ${({ disabled, variant, color }) => disabled && css3`
      :disabled {
        cursor: default;
        background: ${({ theme }) => buttonColors(theme)[variant][color].disabled.background};
        border: ${({ theme }) => `${theme.borderStroke.medium} solid ${buttonColors(theme)[variant][color].disabled.border} `};
        color: ${({ theme }) => buttonColors(theme)[variant][color].disabled.color};

        .icon-styles {
          color: ${({ theme }) => buttonColors(theme)[variant][color].disabled.icon};
        }
      }
    `}

  ${({ onlyIcon, size, theme }) => onlyIcon && theme.slug !== "orb" && css3`
      padding: ${buttonSizes[size].padding.split(" ")[0]};
    `}
`;
var Button = ({
  children,
  size = "medium",
  color = "primary",
  variant = "filled",
  icon,
  iconAlign = "left",
  disabled = false,
  loading: loading2 = false,
  type,
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsx13(ButtonComponent, {
    size,
    color,
    variant,
    iconAlign,
    disabled: disabled || loading2,
    type,
    onlyIcon: icon && !children,
    ...htmlProps,
    children: loading2 ? /* @__PURE__ */ jsx13(Spinner, {
      size: "small"
    }) : /* @__PURE__ */ jsxs6(Fragment3, {
      children: [
        icon && /* @__PURE__ */ jsx13(Icon, {
          name: icon,
          size: ["large", "fullWidth"].includes(size) ? "M" : "S",
          className: "icon-styles"
        }),
        children
      ]
    })
  });
};
Button.displayName = "Button";

// src/Avatar/Avatar.tsx
import styled13 from "styled-components";

// src/Avatar/dictionary.ts
var avatarSizes = {
  small: "32px",
  medium: "40px",
  large: "56px"
};
var avatarColorToken = {
  purple: 5,
  red: 4,
  orange: 5,
  yellow: 4,
  green: 4,
  lightblue: 4,
  blue: 5,
  pink: 5,
  gray: 7
};

// src/Dot/Dot.tsx
import styled12 from "styled-components";

// src/Dot/dictionary.ts
var dotColors = {
  red: 4,
  orange: 4,
  yellow: 3,
  green: 5,
  lightblue: 4,
  blue: 4,
  purple: 4,
  pink: 4,
  gray: 5
};

// src/Dot/Dot.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var getSizeBorder = (size) => {
  switch (size) {
    case "medium":
      return { size: "12px", border: "medium" };
    case "large":
      return { size: "16px", border: "large" };
    default:
      return { size: "8px", border: "small" };
  }
};
var StyledDot = styled12.div`
  height: ${({ size }) => getSizeBorder(size).size};
  width: ${({ size }) => getSizeBorder(size).size};
  background-color: ${({ theme, color }) => theme.colors[color][dotColors[color]]};
  border: ${({ theme, size }) => `${theme.borderStroke[getSizeBorder(size).border]} solid ${theme.colors.gray[1]}`};
  border-radius: 50px;
`;
var Dot = ({ color = "red", size = "small", ...htmlProps }) => {
  return /* @__PURE__ */ jsx14(StyledDot, {
    color,
    size,
    ...htmlProps
  });
};
Dot.displayName = "Dot";

// src/Avatar/Avatar.tsx
import { fontSize as fontSize2, fontWeight as fontWeight2, letterSpacing as letterSpacing2, lineHeight as lineHeight2 } from "@orbita-ui/brand";
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var getTextSizes = (size) => {
  switch (size) {
    case "small":
      return {
        fontSize: fontSize2["xsmall"],
        lineHeight: lineHeight2["2xsmall"],
        letterSpacing: letterSpacing2["large"]
      };
    case "medium":
      return {
        fontSize: fontSize2["large"],
        lineHeight: lineHeight2["medium"],
        letterSpacing: letterSpacing2["medium"]
      };
    case "large":
      return {
        fontSize: fontSize2["2xlarge"],
        lineHeight: lineHeight2["large"],
        letterSpacing: letterSpacing2["small"]
      };
  }
};
var getInitials = (fullName) => fullName == null ? "" : fullName.split(/\s/).reduce((acc, name) => `${acc}${name.slice(0, 1)}`, "").toUpperCase().substring(0, 2);
var AvatarText = styled13.span`
  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  font-size: ${({ size }) => getTextSizes(size).fontSize};
  letter-spacing: ${({ size }) => getTextSizes(size).letterSpacing};
  line-height: ${({ size }) => getTextSizes(size).lineHeight};
  font-weight: ${fontWeight2.highContrast};
  color: ${({ theme, color }) => color === "yellow" ? theme.colors.yellow[7] : theme.colors.gray[1]};
`;
var AvatarComponent = styled13.div`
  background: ${({ theme, color, variant }) => variant !== "photo" && theme.colors[color][avatarColorToken[color]]};
  width: ${({ size }) => avatarSizes[size]};
  height: ${({ size }) => avatarSizes[size]};
  border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .icon-styles {
    color: ${({ theme, color }) => color === "yellow" ? theme.colors.yellow[7] : theme.colors.gray[1]};
  }

  img {
    width: 100%;
    height: 100%;
    text-align: center;
    object-fit: cover;
    color: transparent;
    border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
    display: block;
    border-style: none;
  }

  .badge-styles {
    position: absolute;
    right: 0;
    bottom: 0;
  }
`;
var Avatar = ({
  color = "gray",
  size = "medium",
  withBadge = false,
  badgeColor = "green",
  variant,
  imageUrl,
  alt,
  text,
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsxs7(AvatarComponent, {
    "data-testid": "test-avatar",
    color,
    size,
    variant,
    ...htmlProps,
    children: [
      variant === "icon" && /* @__PURE__ */ jsx15(Icon, {
        name: "UserIcon",
        size: size[0].toUpperCase(),
        className: "icon-styles"
      }),
      variant === "photo" && /* @__PURE__ */ jsx15("img", {
        src: imageUrl,
        alt
      }),
      variant === "text" && /* @__PURE__ */ jsx15(AvatarText, {
        size,
        color,
        children: getInitials(text)
      }),
      withBadge && /* @__PURE__ */ jsx15(Dot, {
        color: badgeColor,
        size,
        className: "badge-styles"
      })
    ]
  });
};
Avatar.displayName = "Avatar";

// src/Brand/Brand.tsx
import styled14 from "styled-components";
import { useEffect as useEffect3, useState as useState3 } from "react";

// src/Brand/getBrand.ts
var getBrand = (slug, inverted) => {
  const finalName = `${slug}Brand${inverted ? "Inv" : ""}`;
  return getCDNUrl("assets/images/brand/" + finalName + ".svg");
};

// src/Brand/sizes.ts
var brandSizes = {
  orb: {
    xlarge: {
      w: 136,
      h: 56
    },
    large: {
      w: 117,
      h: 48
    },
    medium: {
      w: 97,
      h: 40
    },
    small: {
      w: 78,
      h: 32
    }
  },
  di: {
    xlarge: {
      w: 278,
      h: 56
    },
    large: {
      w: 238,
      h: 48
    },
    medium: {
      w: 198,
      h: 40
    },
    small: {
      w: 159,
      h: 32
    }
  },
  pw: {
    xlarge: {
      w: 200,
      h: 56
    },
    large: {
      w: 156,
      h: 48
    },
    medium: {
      w: 126,
      h: 40
    },
    small: {
      w: 104,
      h: 32
    }
  },
  pri: {
    xlarge: {
      w: 152,
      h: 56
    },
    large: {
      w: 130,
      h: 48
    },
    medium: {
      w: 108,
      h: 40
    },
    small: {
      w: 86,
      h: 32
    }
  }
};

// src/Brand/Brand.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var BrandComponent = styled14.img`
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
`;
var Brand = ({
  inverted = false,
  size = "medium",
  ...hmtlProps
}) => {
  const theme = useTheme();
  const [brandLink, setBrandLink] = useState3("");
  const slug = theme.slug.replace("-dark", "");
  useEffect3(() => {
    const link = getBrand(slug, inverted);
    setBrandLink(link);
  }, [slug, inverted]);
  return /* @__PURE__ */ jsx16(BrandComponent, {
    src: brandLink,
    width: brandSizes[slug][size].w,
    height: brandSizes[slug][size].h,
    ...hmtlProps
  });
};
Brand.displayName = "Brand";

// src/Badge/styles.ts
import styled15 from "styled-components";

// src/Badge/dictionary.ts
var badgeColors = (theme) => {
  const { colors: palette } = theme;
  return {
    red: {
      low: {
        bg: "transparent",
        border: palette.red[2],
        color: palette.red[5],
        ic: palette.red[5]
      },
      medium: {
        bg: palette.red[1],
        border: palette.red[1],
        color: palette.red[6],
        ic: palette.red[6]
      },
      high: {
        bg: palette.red[4],
        border: palette.red[4],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    orange: {
      low: {
        bg: "transparent",
        border: palette.orange[2],
        color: palette.orange[5],
        ic: palette.orange[5]
      },
      medium: {
        bg: palette.orange[1],
        border: palette.orange[1],
        color: palette.orange[6],
        ic: palette.orange[6]
      },
      high: {
        bg: palette.orange[4],
        border: palette.orange[4],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    yellow: {
      low: {
        bg: "transparent",
        border: palette.yellow[2],
        color: palette.yellow[6],
        ic: palette.yellow[6]
      },
      medium: {
        bg: palette.yellow[1],
        border: palette.yellow[1],
        color: palette.yellow[6],
        ic: palette.yellow[6]
      },
      high: {
        bg: palette.yellow[3],
        border: palette.yellow[3],
        color: palette.yellow[7],
        ic: palette.yellow[7]
      }
    },
    green: {
      low: {
        bg: "transparent",
        border: palette.green[2],
        color: palette.green[5],
        ic: palette.green[5]
      },
      medium: {
        bg: palette.green[1],
        border: palette.green[1],
        color: palette.green[6],
        ic: palette.green[6]
      },
      high: {
        bg: palette.green[5],
        border: palette.green[5],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    lightblue: {
      low: {
        bg: "transparent",
        border: palette.lightblue[2],
        color: palette.lightblue[5],
        ic: palette.lightblue[5]
      },
      medium: {
        bg: palette.lightblue[1],
        border: palette.lightblue[1],
        color: palette.lightblue[6],
        ic: palette.lightblue[6]
      },
      high: {
        bg: palette.lightblue[4],
        border: palette.lightblue[4],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    blue: {
      low: {
        bg: "transparent",
        border: palette.blue[2],
        color: palette.blue[4],
        ic: palette.blue[4]
      },
      medium: {
        bg: palette.blue[1],
        border: palette.blue[1],
        color: palette.blue[6],
        ic: palette.blue[6]
      },
      high: {
        bg: palette.blue[4],
        border: palette.blue[4],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    purple: {
      low: {
        bg: "transparent",
        border: palette.purple[2],
        color: palette.purple[5],
        ic: palette.purple[5]
      },
      medium: {
        bg: palette.purple[1],
        border: palette.purple[1],
        color: palette.purple[6],
        ic: palette.purple[6]
      },
      high: {
        bg: palette.purple[4],
        border: palette.purple[4],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    pink: {
      low: {
        bg: "transparent",
        border: palette.pink[2],
        color: palette.pink[5],
        ic: palette.pink[5]
      },
      medium: {
        bg: palette.pink[1],
        border: palette.pink[1],
        color: palette.pink[6],
        ic: palette.pink[6]
      },
      high: {
        bg: palette.pink[4],
        border: palette.pink[4],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    },
    gray: {
      low: {
        bg: "transparent",
        border: palette.gray[2],
        color: palette.gray[5],
        ic: palette.gray[5]
      },
      medium: {
        bg: palette.gray[2],
        border: palette.gray[2],
        color: palette.gray[6],
        ic: palette.gray[6]
      },
      high: {
        bg: palette.gray[5],
        border: palette.gray[5],
        color: palette.gray[1],
        ic: palette.gray[1]
      }
    }
  };
};

// src/Badge/styles.ts
var StyledContainer = styled15.div`
  border-radius: ${({ theme }) => theme.borderRadius.xsmall};
  border: ${({ theme, color, emphasis }) => `${theme.borderStroke.small} solid ${badgeColors(theme)[color][emphasis].border}`};
  background-color: ${({ theme, color, emphasis }) => badgeColors(theme)[color][emphasis].bg};
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 8px;
  gap: 4px;
  width: fit-content;
  height: 24px;

  .icon-styles {
    color: ${({ theme, color, emphasis }) => badgeColors(theme)[color][emphasis].ic};
  }

  .text-styles {
    color: ${({ theme, emphasis, color }) => badgeColors(theme)[color][emphasis].color};
    padding: ${({ theme }) => `${theme.spacing["3xsmall"]} ${theme.spacing["3xsmall"]}`};
  }
`;

// src/Badge/Badge.tsx
import { jsx as jsx17, jsxs as jsxs8 } from "react/jsx-runtime";
var Badge = ({ emphasis, label, color, icon }) => {
  return /* @__PURE__ */ jsxs8(StyledContainer, {
    emphasis,
    color,
    children: [
      icon && /* @__PURE__ */ jsx17(Icon, {
        name: icon,
        size: "S",
        className: "icon-styles"
      }),
      /* @__PURE__ */ jsx17(Text, {
        variant: "overline",
        className: "text-styles",
        as: "pre",
        uppercase: true,
        children: label
      })
    ]
  });
};

// src/NotificationBadge/NotificationBadge.tsx
import styled16 from "styled-components";
import { jsx as jsx18 } from "react/jsx-runtime";
var StyledNotificationBadge = styled16.div`
  height: 1rem;
  min-width: 1rem;
  background-color: ${({ theme }) => theme.colors.red[4]};
  border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  padding: ${({ theme }) => `${theme.spacing["3xsmall"]} ${theme.spacing["3xsmall"]}`};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: absolute;
  box-sizing: border-box;
`;
var shortenNumber = (number) => {
  if (number < 99)
    return number;
  return "+99";
};
var NotificationBadge = ({ number = 1 }) => {
  return /* @__PURE__ */ jsx18(StyledNotificationBadge, {
    number,
    children: /* @__PURE__ */ jsx18(Text, {
      variant: "overline",
      color: "neutralWeaker",
      children: shortenNumber(number)
    })
  });
};
NotificationBadge.displayName = "NotificationBadge";

// src/Chip/Chip.tsx
import styled17, { css as css4 } from "styled-components";
import { Fragment as Fragment4, jsx as jsx19, jsxs as jsxs9 } from "react/jsx-runtime";
var ChipContainer = styled17.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 0.5rem;
  gap: ${({ size }) => size === "medium" ? "0.5rem" : "0.25rem"};
  height: ${({ size }) => size === "medium" ? "2rem" : "1.5rem"};
  width: fit-content;
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};
  border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  cursor: default;
  box-sizing: border-box;
  transition: all 0.8s;

  ${(props) => props.disabled && css4`
      background-color: ${({ theme }) => theme.colors.neutral.bg.default};
      border: ${({ theme }) => `${theme.borderStroke.small} solid transparent`};
      .text-styles {
        color: ${({ theme }) => theme.colors.neutral.text.weak} !important;
      }
      .icon-styles {
        color: ${({ theme }) => theme.colors.neutral.ic.weak} !important;
      }
    `}
  ${(props) => props.selected && css4`
      background-color: ${({ theme }) => theme.colors.primary.bg.weak};
      border: ${({ theme }) => `${theme.borderStroke.small} solid transparent`};
      .text-styles {
        color: ${({ theme }) => theme.colors.primary.text.strong} !important;
      }
      .icon-styles {
        color: ${({ theme }) => theme.colors.primary.ic.strong} !important;
      }
    `}
      ${(props) => !props.disabled && css4`
      :hover {
        background-color: ${({ theme }) => theme.colors.neutral.bg.weak};
        border: ${({ theme }) => `${theme.borderStroke.small} solid transparent`};
        cursor: pointer;

        .text-styles {
          color: ${({ theme }) => theme.colors.neutral.text.strong} !important;
        }

        .icon-styles {
          color: ${({ theme }) => theme.colors.neutral.ic.strong} !important;
        }
      }

      :focus-visible {
        outline: none;
        box-shadow: ${({ theme }) => theme.focusStroke.default};
      }
    `}
    .text-styles {
    color: ${({ theme }) => theme.colors.neutral.text.default};
    padding: 0 0.25rem;
  }
  .icon-styles {
    color: ${({ theme }) => theme.colors.neutral.ic.default};
  }
`;
var SelectZone = styled17.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
var Chip = ({
  key = void 0,
  text,
  icon = void 0,
  disabled = false,
  selected = false,
  handleDelete = void 0,
  handleSelectChip = void 0,
  size = "medium",
  ...restProps
}) => {
  return /* @__PURE__ */ jsxs9(ChipContainer, {
    disabled,
    selected,
    handleSelectChip: handleSelectChip !== void 0,
    size,
    ...restProps,
    children: [
      /* @__PURE__ */ jsx19(SelectZone, {
        onClick: () => !disabled && handleSelectChip && handleSelectChip(text, key),
        "aria-label": "select-zone",
        children: /* @__PURE__ */ jsxs9(Fragment4, {
          children: [
            size === "medium" && icon && /* @__PURE__ */ jsx19(Icon, {
              name: icon,
              size: "S",
              className: "icon-styles"
            }),
            /* @__PURE__ */ jsx19(Text, {
              as: "span",
              className: "text-styles",
              variant: size === "medium" ? "body" : "caption",
              children: text
            })
          ]
        })
      }),
      handleDelete && /* @__PURE__ */ jsx19(Icon, {
        "aria-label": "close-icon",
        name: "CrossIcon",
        size: "S",
        className: "icon-styles",
        onClick: () => handleDelete(text, key)
      })
    ]
  }, key);
};
Chip.displayName = "Chip";

// src/Toogle/Toogle.tsx
import styled18, { css as css5 } from "styled-components";
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
var ButtonOn = styled18.div`
  width: 16px;
  height: 16px;
  background: ${({ theme }) => theme.colors.gray[1]};
  margin-top: ${({ theme }) => theme.spacing["3xsmall"]};
  margin-left: ${({ theme }) => theme.spacing["3xsmall"]};
  margin-right: auto;
  margin-bottom: ${({ theme }) => theme.spacing["3xsmall"]};
  border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  box-shadow: ${({ theme }) => theme.elevation["20"]};
`;
var ToogleComponent = styled18.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  vertical-align: middle;
  flex-direction: row-reverse;
  gap: ${({ theme }) => theme.spacing.small};

  .toogle-container {
    width: 34px;
    height: 20px;
    background: ${({ theme }) => theme.colors.neutral.bg.default};
    border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
    position: relative;
    outline: none;
    display: inline-flex;
    box-sizing: border-box;
    transition: 0.5s linear;

    input {
      cursor: inherit;
      position: absolute;
      z-index: 1;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    .label-text {
      color: ${({ theme }) => theme.colors.neutral.text.strong};
    }
  }

  :focus-visible {
    outline: none;
    .toogle-container {
      outline: none;
      box-shadow: ${({ theme }) => theme.focusStroke.default};
      background: ${({ theme, isChecked }) => isChecked ? theme.colors.primary.bg.default : theme.colors.neutral.bg.weak};
    }
  }

  :hover {
    .toogle-container {
      background: ${({ theme, isChecked }) => isChecked ? theme.colors.primary.bg.strong : theme.colors.neutral.bg.strong};
    }
  }

  ${({ isChecked }) => isChecked && css5`
      .toogle-container {
        background: ${({ theme }) => theme.colors.primary.bg.default};
      }
      ${ButtonOn} {
        transform: translateX(calc(34px - 16px - 4px));
      }
    `}

  ${({ isDisabled }) => !!isDisabled && css5`
      cursor: default !important;
      .toogle-container {
        background: ${({ theme }) => theme.colors.neutral.bg.default} !important;
      }
      ${ButtonOn} {
        background: ${({ theme }) => theme.colors.neutral.bg.weak} !important;
      }
      .label-text {
        color: ${({ theme }) => theme.colors.neutral.text.weak};
      }
    `}
`;
var Toogle = ({
  label = void 0,
  isChecked,
  isDisabled = false,
  onChange,
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsxs10(ToogleComponent, {
    isChecked,
    isDisabled,
    ...htmlProps,
    "data-testid": "label-switch",
    children: [
      /* @__PURE__ */ jsxs10("span", {
        className: "toogle-container",
        "data-testid": "input-container",
        children: [
          /* @__PURE__ */ jsx20("input", {
            type: "checkbox",
            defaultChecked: isChecked,
            onChange,
            disabled: isDisabled
          }),
          /* @__PURE__ */ jsx20(ButtonOn, {})
        ]
      }),
      label && /* @__PURE__ */ jsx20(Text, {
        className: "label-text",
        as: "span",
        children: label
      })
    ]
  });
};
Toogle.displayName = "Toogle";

// src/TextInput/TextInput.tsx
import styled20, { css as css6 } from "styled-components";

// src/Popover/Popover.tsx
import React2, {
  useContext,
  useState as useState4,
  useRef,
  useEffect as useEffect5,
  useImperativeHandle
} from "react";

// src/Popover/styles.ts
import styled19 from "styled-components";

// src/Popover/functions.tsx
var returnPopoverPositioning = (triggerWidth, triggerHeight, popoverWidth, popoverHeight, position, arrowOffset, designOffset, distanceFromTrigger) => {
  let positionObject = { x: 0, y: 0 };
  const topPositioning = -(popoverHeight + triggerHeight);
  const rightAlign = triggerWidth + arrowOffset;
  const leftAlign = -popoverWidth - arrowOffset;
  const centerXPosition = -(popoverWidth / 2) + triggerWidth / 2;
  const centerYPosition = -(popoverHeight / 2 + triggerHeight / 2);
  switch (position) {
    case "topLeft":
      positionObject = {
        x: -popoverWidth + arrowOffset + triggerWidth / 2 + designOffset + 3,
        y: topPositioning - arrowOffset
      };
      break;
    case "topCenter":
      positionObject = {
        x: centerXPosition,
        y: topPositioning - arrowOffset
      };
      break;
    case "topRight":
      positionObject = {
        x: triggerWidth / 2 - designOffset - 3,
        y: topPositioning - arrowOffset
      };
      break;
    case "leftTop":
      {
        positionObject = {
          x: leftAlign,
          y: -popoverHeight - triggerHeight / 4 + designOffset
        };
      }
      break;
    case "leftCenter":
      {
        positionObject = {
          x: leftAlign,
          y: centerYPosition
        };
      }
      break;
    case "leftBottom":
      {
        positionObject = {
          x: leftAlign,
          y: -triggerHeight + triggerHeight / 4 - designOffset
        };
      }
      break;
    case "bottomLeft":
      {
        positionObject = {
          x: -popoverWidth + arrowOffset + triggerWidth / 2 + designOffset,
          y: arrowOffset
        };
      }
      break;
    case "bottomCenter":
      {
        positionObject = {
          x: centerXPosition,
          y: arrowOffset + distanceFromTrigger
        };
      }
      break;
    case "bottomRight":
      {
        positionObject = {
          x: triggerWidth / 2 - designOffset - 8,
          y: arrowOffset
        };
      }
      break;
    case "rightBottom":
      {
        positionObject = {
          x: rightAlign,
          y: -triggerHeight + triggerHeight / 4 - designOffset
        };
      }
      break;
    case "rightCenter":
      {
        positionObject = {
          x: rightAlign,
          y: centerYPosition
        };
      }
      break;
    case "rightTop":
      {
        positionObject = {
          x: rightAlign,
          y: -popoverHeight - triggerHeight / 4 + designOffset
        };
      }
      break;
  }
  return `translate(${positionObject.x}px , ${positionObject.y}px)`;
};
var returnArrowPositioning = (triggerWidth, triggerHeight, popoverWidth, popoverHeight, position, arrowOffset, distanceFromTrigger) => {
  let positionObject = {
    x: 0,
    y: 0,
    rotation: 0
  };
  switch (position) {
    case "leftTop":
    case "leftCenter":
    case "leftBottom": {
      positionObject = {
        x: -arrowOffset,
        y: -triggerHeight / 2 - 13,
        rotation: 0
      };
      break;
    }
    case "bottomLeft":
    case "bottomCenter":
    case "bottomRight": {
      positionObject = {
        x: triggerWidth / 2,
        y: 1 + distanceFromTrigger,
        rotation: -90
      };
      break;
    }
    case "rightTop":
    case "rightCenter":
    case "rightBottom": {
      positionObject = {
        x: triggerWidth + arrowOffset / 2,
        y: -triggerHeight / 2 - 13,
        rotation: 180
      };
      break;
    }
    case "topLeft":
    case "topCenter":
    case "topRight": {
      positionObject = {
        x: triggerWidth / 2,
        y: -triggerHeight - arrowOffset - 7,
        rotation: 90
      };
    }
  }
  return `translate(${positionObject.x}px , ${positionObject.y}px) rotate(${positionObject.rotation}deg)`;
};

// src/Popover/styles.ts
var ArrowContainer = styled19.div`
  position: absolute;
  opacity: ${({ isShown }) => isShown ? "100" : "0"};

  z-index: ${({ theme }) => theme.zindex[30]};
  transform: ${({ triggerRect, contentRect, preferredPosition }) => triggerRect && contentRect && preferredPosition && returnArrowPositioning(
  triggerRect.width,
  triggerRect.height,
  contentRect.width,
  contentRect.height,
  preferredPosition,
  15,
  0
)};
  > svg {
    filter: drop-shadow(0px 4px 16px rgba(59, 64, 89, 0.08));
    z-index: ${({ theme }) => theme.zindex[30]};
    > path {
      fill: ${({ variant, theme }) => variant === "normal" ? theme.colors.neutral.bg.weaker : theme.colors.blue[4]};
    }
  }
`;
var PopoverContainer = styled19.div`
  position: absolute;
  box-sizing: border-box;
  transform: ${({ triggerRect, contentRect, preferredPosition }) => triggerRect && contentRect && preferredPosition && returnPopoverPositioning(
  triggerRect.width,
  triggerRect.height,
  contentRect.width,
  contentRect.height,
  preferredPosition,
  15,
  10,
  0
)};
  opacity: ${({ isShown }) => isShown ? "100" : "0"};
  visibility: ${({ isShown }) => isShown ? "inherit" : "hidden"};
  display: flex;
  flex-direction: row;
  width: ${({ width }) => width ? width : "320px"};
  padding: ${({ theme }) => theme.spacing.medium};
  background-color: ${({ theme, variant }) => variant === "normal" ? theme.colors.neutral.bg.weaker : theme.colors.blue[4]};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  box-shadow: ${({ theme }) => theme.elevation["40"]};
  z-index: ${({ theme }) => theme.zindex[30]};
`;
var ContentContainer = styled19.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
`;
var CrossIconContainer = styled19.div`
  margin-left: auto;
  cursor: pointer;
`;
var ButtonContainer = styled19.div`
  margin-left: -${({ theme }) => theme.spacing.small};
`;
var ButtonListContainer = styled19.div`
  display: flex;
  flex-direction: row;
  margin-left: -${({ theme }) => theme.spacing.small};
  gap: 8px;
`;
var TriggerContainer = styled19.div`
  width: fit-content;
  cursor: pointer !important;
`;
var HeaderContainer = styled19.div``;

// src/Popover/indicatorShape.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var IndicatorShape = ({ testid }) => /* @__PURE__ */ jsx21("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: 8,
  height: 18,
  fill: "none",
  children: /* @__PURE__ */ jsx21("path", {
    fill: "white",
    d: "M8 9 .586 1.586A2 2 0 0 1 0 .172v17.656a2 2 0 0 1 .586-1.414L8 9Z",
    "data-testid": testid
  })
});
var indicatorShape_default = IndicatorShape;

// src/utils/useClickOutside.ts
import { useEffect as useEffect4 } from "react";
var useOutsideAlerter = (ref1, ref2, isOpen, setOpen) => {
  useEffect4(() => {
    function handleClickOutside(event) {
      if (isOpen && ref1.current && ref2.current && !ref1.current.contains(event.target) && !ref2.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref1, ref2, isOpen]);
};

// src/Popover/Popover.tsx
import { Fragment as Fragment5, jsx as jsx22, jsxs as jsxs11 } from "react/jsx-runtime";
var PopoverContext = React2.createContext({
  isShown: false,
  setIsShown: void 0,
  setContentReference: () => {
    throw new Error("shouldProvideContext for trigger");
  },
  setTriggerReference: () => {
    throw new Error("shouldProvideContext for trigger");
  },
  triggerReference: null,
  contentReference: null
});
var Popover = ({ children }) => {
  const [isShown, setIsShown] = useState4(true);
  const [contentReference, setContentReference] = useState4(null);
  const [triggerReference, setTriggerReference] = useState4(null);
  const contextValue = {
    isShown,
    setIsShown,
    setContentReference,
    setTriggerReference,
    triggerReference,
    contentReference
  };
  return /* @__PURE__ */ jsx22(PopoverContext.Provider, {
    value: contextValue,
    children: /* @__PURE__ */ jsx22("div", {
      children
    })
  });
};
var PopoverTrigger = ({
  children,
  disableAutoTrigger
}) => {
  const { setIsShown, setTriggerReference } = useContext(PopoverContext);
  const ref = useRef(null);
  useEffect5(() => {
    setTriggerReference(ref.current);
  }, [ref]);
  const onClick = (e) => {
    !disableAutoTrigger && setIsShown && setIsShown((isShown) => !isShown);
  };
  const childrenToTriggerPopover = React2.cloneElement(children, {
    onClick
  });
  useImperativeHandle(ref, () => ref.current);
  return /* @__PURE__ */ jsx22(TriggerContainer, {
    ref,
    children: childrenToTriggerPopover
  });
};
var PopoverContent = ({
  variant = "normal",
  title,
  subtitle,
  button,
  withCloseIcon = true,
  onCloseIcon,
  preferredPosition = "leftTop",
  isShownByDefault,
  buttonsList,
  headerText,
  width,
  disCrossIconDefaultBehaviour = false
}) => {
  const { isShown, setIsShown, setContentReference, triggerReference } = useContext(PopoverContext);
  const contentRef = useRef(null);
  const { variant: ThemeVariant2 } = useTheme();
  useEffect5(() => {
    setContentReference(contentRef.current);
  }, []);
  useEffect5(() => {
    if (setIsShown && !disCrossIconDefaultBehaviour) {
      setIsShown(isShownByDefault ? isShownByDefault : false);
    }
  }, [isShownByDefault]);
  const triggerRect = triggerReference == null ? void 0 : triggerReference.getBoundingClientRect();
  const contentRect = contentRef && contentRef.current && contentRef.current.getBoundingClientRect();
  useOutsideAlerter(contentRef, contentRef, isShown || false, setIsShown);
  return /* @__PURE__ */ jsxs11(Fragment5, {
    children: [
      /* @__PURE__ */ jsx22(ArrowContainer, {
        triggerRect,
        contentRect,
        preferredPosition,
        variant,
        isShown,
        "data-testid": "popover-arrow-container",
        children: /* @__PURE__ */ jsx22(indicatorShape_default, {
          testid: "popover-arrow"
        })
      }),
      /* @__PURE__ */ jsxs11(PopoverContainer, {
        isShown,
        ref: contentRef,
        triggerRect,
        contentRect,
        preferredPosition,
        variant,
        "data-testid": "popover-container",
        width,
        children: [
          /* @__PURE__ */ jsxs11(ContentContainer, {
            "data-testid": "popover-content-container",
            children: [
              headerText && variant !== "promotional" && /* @__PURE__ */ jsx22(HeaderContainer, {
                children: /* @__PURE__ */ jsx22(Text, {
                  size: "s",
                  variant: "caption",
                  color: "neutralWeak",
                  children: headerText
                })
              }),
              title && /* @__PURE__ */ jsx22(Text, {
                color: variant === "normal" ? "neutralStrong" : ThemeVariant2 === "light" ? "neutralWeaker" : "neutralStrong",
                size: "m",
                bold: true,
                "data-testid": "popover-title",
                children: title
              }),
              /* @__PURE__ */ jsx22(Text, {
                color: variant === "normal" ? "neutral" : ThemeVariant2 === "light" ? "neutralWeaker" : "neutralStrong",
                size: "s",
                "data-testid": "popover-subtitle",
                children: subtitle
              }),
              button && !buttonsList && /* @__PURE__ */ jsx22(ButtonContainer, {
                children: /* @__PURE__ */ jsx22(Button, {
                  variant: "ghost",
                  color: variant === "normal" ? "primary" : "inverted",
                  onClick: button && button.onClick,
                  size: "small",
                  "data-testid": "popover-button",
                  children: button.label
                })
              }),
              buttonsList && /* @__PURE__ */ jsx22(ButtonListContainer, {
                children: buttonsList && buttonsList[0] && buttonsList.map((button2, index) => /* @__PURE__ */ jsx22(Button, {
                  variant: button2.variant,
                  color: button2.color,
                  onClick: button2.onClick,
                  size: "small",
                  "data-testid": `popover-button-${index}`,
                  children: button2.label
                }))
              })
            ]
          }),
          withCloseIcon && /* @__PURE__ */ jsx22(CrossIconContainer, {
            "data-testid": "popover-crossicon-container",
            children: /* @__PURE__ */ jsx22(Icon, {
              onClick: () => {
                if (setIsShown && !disCrossIconDefaultBehaviour) {
                  setIsShown(false);
                }
                onCloseIcon && onCloseIcon();
              },
              name: "CrossIcon",
              color: variant === "normal" ? "neutral" : ThemeVariant2 === "light" ? "neutralWeaker" : "neutralStrong",
              size: "M",
              "data-testid": "popover-crossicon"
            })
          })
        ]
      })
    ]
  });
};

// src/TextInput/TextInput.tsx
import { jsx as jsx23, jsxs as jsxs12 } from "react/jsx-runtime";
var InputWrapper = styled20.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  .icon-invalid {
    vertical-align: middle;
  }
`;
var InputContainer = styled20.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  height: 40px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: 10px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius["3xlarge"]};
  padding: 7px 11px;
  transition: all 0.8s;

  ${({ readOnly, theme, invalid }) => readOnly ? css6`
          border: none;
        ` : css6`
          &:hover {
            background: ${theme.colors.neutral.bg.weak};
            input {
              background: ${theme.colors.neutral.bg.weak};
            }
          }

          &:focus-within {
            border: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
          }

          &:focus-visible {
            outline: none;
            box-shadow: ${invalid ? theme.focusStroke.error : theme.focusStroke.default};
          }
        `}

  ${({ invalid, theme }) => invalid && css6`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`} !important;
    `}

  ${({ disabled, theme }) => disabled && css6`
      border: none;
      background: ${theme.colors.neutral.bg.weak};
    `}
`;
var InputComponent = styled20.input`
  width: 100%;
  border: none;
  appearance: none;
  max-height: 24px;
  font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
  font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
  line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
  font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
  padding: 0;
  color: ${({ theme }) => theme.colors.neutral.text.strong};
  caret-color: ${({ theme }) => theme.colors.primary.border.default};
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  transition: all 0.8s;

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral.text.weak};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background: none;
  }
`;
var LabelContainer = styled20.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  transition: all 0.8s;
`;
var TextInput = ({
  label,
  helperText = void 0,
  aditionalHelp = void 0,
  aditionalHelpButton,
  errorText = void 0,
  placeholder = void 0,
  onChange,
  defaultValue,
  invalid = false,
  verified = false,
  tabIndex,
  disabled = false,
  readOnly = false,
  required = false,
  optional = false,
  fullWidth = false,
  maxLength,
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsxs12(InputWrapper, {
    children: [
      /* @__PURE__ */ jsxs12(LabelContainer, {
        children: [
          /* @__PURE__ */ jsxs12(Text, {
            variant: "caption",
            as: "label",
            color: "neutral",
            children: [
              label,
              " ",
              required && /* @__PURE__ */ jsx23(Text, {
                variant: "caption",
                as: "span",
                color: "neutralWeak",
                children: "(Obligatorio)"
              }),
              optional && /* @__PURE__ */ jsx23(Text, {
                variant: "caption",
                as: "span",
                color: "neutralWeak",
                children: "(Opcional)"
              })
            ]
          }),
          label && aditionalHelp && /* @__PURE__ */ jsxs12(Popover, {
            children: [
              /* @__PURE__ */ jsx23(PopoverTrigger, {
                children: /* @__PURE__ */ jsx23(Icon, {
                  name: "HelpIcon",
                  size: "S",
                  color: "neutralWeak"
                })
              }),
              /* @__PURE__ */ jsx23(PopoverContent, {
                subtitle: aditionalHelp,
                preferredPosition: "rightBottom",
                button: aditionalHelpButton
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs12(InputContainer, {
        invalid,
        tabIndex,
        disabled,
        readOnly,
        fullWidth,
        children: [
          /* @__PURE__ */ jsx23(InputComponent, {
            placeholder,
            type: "text",
            onChange,
            defaultValue,
            disabled,
            readOnly,
            required,
            maxLength,
            ...htmlProps
          }),
          verified && /* @__PURE__ */ jsx23(Icon, {
            name: "CheckIcon",
            color: "success"
          })
        ]
      }),
      /* @__PURE__ */ jsxs12(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && errorText && /* @__PURE__ */ jsx23(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
TextInput.displayName = "TextInput";

// src/PasswordInput/PasswordInput.tsx
import { useEffect as useEffect6, useState as useState5 } from "react";
import styled21, { css as css7 } from "styled-components";
import { jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
var InputWrapper2 = styled21.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  .icon-invalid {
    vertical-align: middle;
  }
`;
var PasswordInputContainer = styled21.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  height: 40px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: 10px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius["3xlarge"]};
  padding: 7px 11px;
  transition: all 0.8s;

  .icon-styles {
    margin-left: auto;
    color: ${({ theme }) => theme.colors.neutral.ic.default};
    cursor: pointer;
  }

  ${({ readOnly, theme, invalid }) => readOnly ? css7`
          border: none;
          .icon-styles {
            color: ${({ theme: theme2 }) => theme2.colors.neutral.ic.weak};
            cursor: default;
          }
        ` : css7`
          &:hover {
            background: ${theme.colors.neutral.bg.weak};
            input {
              background: ${theme.colors.neutral.bg.weak};
            }
          }

          &:focus-within {
            border: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
          }

          &:focus-visible {
            outline: none;
            box-shadow: ${invalid ? theme.focusStroke.error : theme.focusStroke.default};
          }
        `}

  ${({ invalid, theme }) => invalid && css7`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`} !important;
      input {
        caret-color: ${theme.colors.danger.border.default};
      }
    `}

  ${({ disabled, theme }) => disabled && css7`
      border: none;
      background: ${theme.colors.neutral.bg.weak};
      .icon-styles {
        color: ${({ theme: theme2 }) => theme2.colors.neutral.ic.weak};
        cursor: default;
      }
    `}
`;
var InputComponent2 = styled21.input`
  width: 100%;
  border: none;
  appearance: none;
  max-height: 24px;
  font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
  font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
  line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
  font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
  padding: 0;
  color: ${({ theme }) => theme.colors.neutral.text.strong};
  caret-color: ${({ theme }) => theme.colors.primary.border.default};
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  transition: all 0.8s;

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral.text.weak};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background: none;
  }
`;
var LabelContainer2 = styled21.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
`;
var PasswordInput = ({
  label,
  helperText = void 0,
  aditionalHelp,
  aditionalHelpButton,
  errorText = void 0,
  onChange,
  defaultValue,
  invalid = false,
  tabIndex,
  disabled = false,
  readOnly = false,
  fullWidth = false,
  defaultShowPass,
  showPass,
  onClickVisibilityIcon,
  ...htmlProps
}) => {
  const [showPassState, setShowPassState] = useState5(false);
  useEffect6(() => {
    setShowPassState(Boolean(defaultShowPass));
  }, []);
  useEffect6(() => {
    setShowPassState(Boolean(showPass));
  }, [showPass]);
  return /* @__PURE__ */ jsxs13(InputWrapper2, {
    children: [
      /* @__PURE__ */ jsxs13(LabelContainer2, {
        children: [
          /* @__PURE__ */ jsx24(Text, {
            variant: "caption",
            as: "span",
            color: "neutral",
            children: label
          }),
          label && aditionalHelp && /* @__PURE__ */ jsxs13(Popover, {
            children: [
              /* @__PURE__ */ jsx24(PopoverTrigger, {
                children: /* @__PURE__ */ jsx24(Icon, {
                  name: "HelpIcon",
                  size: "S",
                  color: "neutralWeak"
                })
              }),
              /* @__PURE__ */ jsx24(PopoverContent, {
                subtitle: aditionalHelp,
                preferredPosition: "rightBottom",
                button: aditionalHelpButton
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs13(PasswordInputContainer, {
        invalid,
        tabIndex,
        disabled,
        readOnly,
        fullWidth,
        children: [
          /* @__PURE__ */ jsx24(InputComponent2, {
            placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
            type: showPassState ? "text" : "password",
            onChange,
            defaultValue,
            disabled,
            readOnly,
            ...htmlProps
          }),
          /* @__PURE__ */ jsx24(Icon, {
            name: showPassState ? "ViewIcon" : "HideIcon",
            onClick: () => onClickVisibilityIcon ? !disabled && !readOnly && onClickVisibilityIcon() : !disabled && !readOnly && setShowPassState(!showPassState),
            className: "icon-styles",
            "data-testid": "eye-icon"
          })
        ]
      }),
      /* @__PURE__ */ jsxs13(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && errorText && /* @__PURE__ */ jsx24(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
PasswordInput.displayName = "PasswordInput";

// src/TextArea/TextArea.tsx
import { useState as useState6 } from "react";
import styled22, { css as css8 } from "styled-components";
import { jsx as jsx25, jsxs as jsxs14 } from "react/jsx-runtime";
var InputWrapper3 = styled22.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  .icon-invalid {
    vertical-align: middle;
  }
`;
var InputContainer2 = styled22.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  height: ${({ heigth }) => heigth ? heigth : "88px"};
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: 10px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: 7px 11px;

  ${({ readOnly, theme, invalid }) => readOnly ? css8`
          border: none;
        ` : css8`
          &:hover {
            background: ${theme.colors.neutral.bg.weak};
            textarea {
              background: ${theme.colors.neutral.bg.weak};
            }
          }

          &:focus-within {
            border: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
          }

          &:focus-visible {
            outline: none;
            box-shadow: ${invalid ? theme.focusStroke.error : theme.focusStroke.default};
          }
        `}

  ${({ invalid, theme }) => invalid && css8`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`} !important;
    `}

  ${({ disabled, theme }) => disabled && css8`
      border: none;
      background: ${theme.colors.neutral.bg.weak};
    `}
`;
var TextAreaComponent = styled22.textarea`
  width: 100%;
  height: 100%;
  border: none;
  appearance: none;
  font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
  font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
  line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
  font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
  padding: 0;
  color: ${({ theme }) => theme.colors.neutral.text.strong};
  caret-color: ${({ theme }) => theme.colors.primary.border.default};
  resize: none;
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral.text.weak};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background: none;
  }
`;
var TextWrap = styled22.div`
  display: flex;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};

  .counter-text {
    margin-left: auto;
    color: ${({ theme, invalid }) => invalid ? theme.colors.danger.text.default : theme.colors.neutral.text.weak};
  }
`;
var LabelContainer3 = styled22.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
`;
var TextArea = ({
  label,
  helperText = void 0,
  aditionalHelp = void 0,
  aditionalHelpButton,
  errorText = void 0,
  placeholder = void 0,
  onChange,
  defaultValue,
  invalid = false,
  verified = false,
  tabIndex,
  disabled = false,
  readOnly = false,
  required = false,
  optional = false,
  fullWidth = false,
  heigth,
  maxLength = 240,
  ...htmlProps
}) => {
  const [characterCount, setCharacterCount] = useState6(0);
  const handleChage = (event) => {
    onChange && onChange(event);
    setCharacterCount(event.target.value.length);
  };
  return /* @__PURE__ */ jsxs14(InputWrapper3, {
    children: [
      /* @__PURE__ */ jsxs14(TextWrap, {
        fullWidth,
        invalid,
        children: [
          /* @__PURE__ */ jsxs14(LabelContainer3, {
            children: [
              /* @__PURE__ */ jsxs14(Text, {
                variant: "caption",
                as: "span",
                children: [
                  label,
                  " ",
                  required && /* @__PURE__ */ jsx25(Text, {
                    variant: "caption",
                    as: "span",
                    color: "neutralWeak",
                    children: "(Obligatorio)"
                  }),
                  optional && /* @__PURE__ */ jsx25(Text, {
                    variant: "caption",
                    as: "span",
                    color: "neutralWeak",
                    children: "(Opcional)"
                  })
                ]
              }),
              label && aditionalHelp && /* @__PURE__ */ jsxs14(Popover, {
                children: [
                  /* @__PURE__ */ jsx25(PopoverTrigger, {
                    children: /* @__PURE__ */ jsx25(Icon, {
                      name: "HelpIcon",
                      size: "S",
                      color: "neutralWeak"
                    })
                  }),
                  /* @__PURE__ */ jsx25(PopoverContent, {
                    subtitle: aditionalHelp,
                    preferredPosition: "rightBottom",
                    button: aditionalHelpButton
                  })
                ]
              })
            ]
          }),
          !readOnly && /* @__PURE__ */ jsx25(Text, {
            variant: "caption",
            as: "span",
            className: "counter-text",
            children: `${characterCount} / ${maxLength}`
          })
        ]
      }),
      /* @__PURE__ */ jsxs14(InputContainer2, {
        invalid,
        tabIndex,
        disabled,
        readOnly,
        fullWidth,
        maxLength,
        heigth,
        children: [
          /* @__PURE__ */ jsx25(TextAreaComponent, {
            "data-testid": "test-textarea",
            placeholder,
            onChange: handleChage,
            defaultValue,
            disabled,
            readOnly,
            required,
            maxLength,
            ...htmlProps
          }),
          verified && /* @__PURE__ */ jsx25(Icon, {
            name: "CheckIcon",
            color: "success"
          })
        ]
      }),
      /* @__PURE__ */ jsxs14(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && errorText && /* @__PURE__ */ jsx25(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
TextArea.displayName = "TextArea";

// src/Header/Header.tsx
import styled23 from "styled-components";
import { Fragment as Fragment6, jsx as jsx26, jsxs as jsxs15 } from "react/jsx-runtime";
var HeaderComponent = styled23.header`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  padding: 10px 24px;
  height: 64px;
  width: 100%;
  box-sizing: border-box;
  z-index: ${({ theme }) => theme.zindex[10]};

  background: ${({ theme, variant }) => variant && variant === "trasnparent" ? "transparent" : theme.colors.neutral.bg.weaker};
`;
var HeaderContent = styled23.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;

  .icon-style {
    cursor: pointer;
  }

  .cursor-active {
    cursor: pointer;
  }
`;
var ProfileContainer = styled23.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
`;
var Divider = styled23.div`
  width: 1px;
  height: 40px;
  background: ${({ theme }) => theme.colors.neutral.border.weak};
`;
var BadgeContainer = styled23.div`
  position: relative;
  left: 12px;
  top: -40px;
  width: min-content;
`;
var formatName = (name) => {
  if (!name)
    return;
  else {
    const mySentence = name.trim().toLowerCase();
    const words = mySentence.split(" ");
    return words.map((word) => {
      return word && typeof word === "string" && word[0].toUpperCase() + word.substring(1);
    }).join(" ");
  }
};
var Header = ({
  variant = "filled",
  avatarColor = "purple",
  notificationLength = 0,
  hideNotifications = false,
  userName,
  onLogout,
  onViewProfile
}) => {
  return /* @__PURE__ */ jsx26(HeaderComponent, {
    variant,
    children: /* @__PURE__ */ jsxs15(HeaderContent, {
      children: [
        !hideNotifications && /* @__PURE__ */ jsxs15(Fragment6, {
          children: [
            /* @__PURE__ */ jsxs15("div", {
              children: [
                /* @__PURE__ */ jsx26(Icon, {
                  name: "NotificationIcon",
                  color: "neutral",
                  className: "icon-style"
                }),
                notificationLength && notificationLength > 0 ? /* @__PURE__ */ jsx26(BadgeContainer, {
                  children: /* @__PURE__ */ jsx26(NotificationBadge, {
                    number: notificationLength
                  })
                }) : null
              ]
            }),
            /* @__PURE__ */ jsx26(Divider, {})
          ]
        }),
        /* @__PURE__ */ jsxs15(ProfileContainer, {
          className: onViewProfile ? "cursor-active" : "",
          onClick: onViewProfile,
          children: [
            /* @__PURE__ */ jsx26(Avatar, {
              variant: "icon",
              color: avatarColor
            }),
            /* @__PURE__ */ jsx26(Text, {
              children: formatName(userName)
            })
          ]
        }),
        /* @__PURE__ */ jsx26(Icon, {
          name: "LogOutIcon",
          color: "neutral",
          className: "icon-style",
          onClick: onLogout
        })
      ]
    })
  });
};
Header.displayName = "Header";

// src/Menu/Menu.tsx
import { useEffect as useEffect7, useState as useState7 } from "react";

// src/Menu/styled.tsx
import styled24, { css as css9 } from "styled-components";
var MenuComponent = styled24.div`
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  width: 90px;
  height: 100vh;

  position: sticky;
  left: 0;
  top: 0;
  overflow-y: auto;
  overflow-x: hidden;
  transition: all 0.3s ease-in-out;
  z-index: ${({ theme }) => theme.zindex[10]};

  ${({ isOpen }) => isOpen && css9`
      width: 240px;
    `}

  .logo-container {
    padding: 12px 8px;
  }

  .logo-container-center {
    padding: 12px 8px;
    align-items: center;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    width: 100%;
    transition: all 0.3s ease-in-out;
  }
`;
var MenuItemContainer = styled24.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  isolation: isolate;
  width: 100%;
  box-sizing: border-box;
  padding: 4px 8px;
`;
var ItemComponent = styled24.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  min-height: 40px;
  padding: 8px;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all 0.3s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral.bg.weak};

    .item-text {
      color: ${({ theme }) => theme.colors.neutral.text.strong};
    }

    .item-left-icon {
      color: ${({ theme }) => theme.colors.neutral.ic.default};
    }
  }

  ${({ selected }) => selected && selected === true && css9`
      background: ${({ theme }) => theme.colors.primary.bg.weak};

      .item-text {
        color: ${({ theme }) => theme.colors.primary.text.strong};
      }

      .item-left-icon {
        color: ${({ theme }) => theme.colors.primary.ic.strong};
      }
    `}

  ${({ menuOpen }) => !menuOpen && css9`
      justify-content: center;

      .item-text {
        display: none;
      }
    `}

.item-text {
    color: ${({ theme }) => theme.colors.neutral.text.default};
    width: 100%;
  }
`;
var SubItemComponent = styled24.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 8px 8px 40px;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.3s ease-in-out;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral.bg.weak};

    .item-text-subitem {
      color: ${({ theme }) => theme.colors.neutral.text.strong};
    }
  }

  ${({ selected }) => selected && css9`
      .item-text {
        color: ${({ theme }) => theme.colors.primary.text.default};
      }
    `}

  .item-text-subitem {
    color: ${({ theme }) => theme.colors.neutral.text.default};
  }
`;
var TitleComponent = styled24.li`
  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  font-size: ${({ theme }) => theme.fonts.caption.default.highContrast.fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.caption.default.highContrast.letterSpacing};
  line-height: ${({ theme }) => theme.fonts.caption.default.highContrast.lineHeight};
  font-weight: ${({ theme }) => theme.fonts.caption.default.highContrast.fontWeight};
  color: ${({ theme }) => theme.colors.neutral.text.weak};

  padding: 8px 8px 4px;
  box-sizing: border-box;
`;
var DividerComponent = styled24.li`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.neutral.border.weak};
  margin: 8px 0;
`;

// src/Menu/Menu.tsx
import { jsx as jsx27, jsxs as jsxs16 } from "react/jsx-runtime";
var MenuItem = ({
  itemType,
  text,
  iconName,
  action,
  selected,
  menuOpen,
  subItems,
  abbr
}) => {
  const paddings = {
    divider: "8px 8px",
    title: "12px 8px 4px",
    menuItem: "4px 8px"
  };
  const [subItemOpen, setSubItemOpen] = useState7(false);
  const [subItemSelected, setSubItemSelected] = useState7(false);
  useEffect7(() => {
    if (!subItems)
      return;
    const selecteds = subItems.filter((item) => item.selected);
    setSubItemSelected(selecteds.length > 0);
  }, [subItems]);
  return /* @__PURE__ */ jsxs16(MenuItemContainer, {
    style: { padding: paddings[itemType] },
    children: [
      itemType === "divider" && /* @__PURE__ */ jsx27(DividerComponent, {}),
      itemType === "title" && /* @__PURE__ */ jsx27(TitleComponent, {
        children: !menuOpen && abbr ? abbr : text
      }),
      itemType === "menuItem" && /* @__PURE__ */ jsxs16(ItemComponent, {
        selected: selected || subItemSelected,
        menuOpen,
        onClick: subItems ? () => setSubItemOpen(!subItemOpen) : action,
        children: [
          iconName && /* @__PURE__ */ jsx27(Icon, {
            name: iconName,
            className: "item-left-icon",
            color: "neutralWeak"
          }),
          /* @__PURE__ */ jsx27(Text, {
            as: "span",
            className: "item-text",
            children: text
          }),
          subItems && /* @__PURE__ */ jsx27(Icon, {
            name: subItemOpen ? "ChevronUpSmallIcon" : "ChevronDownSmallIcon",
            className: "item-left-icon",
            color: "neutralWeak",
            "data-testid": "icon-subitem"
          })
        ]
      }),
      subItems && /* @__PURE__ */ jsx27("ul", {
        style: { display: subItemOpen ? "initial" : "none" },
        children: subItems.map((subItem, index) => {
          return /* @__PURE__ */ jsx27(SubItemComponent, {
            selected: subItem.selected,
            onClick: subItem.action,
            children: /* @__PURE__ */ jsx27(Text, {
              bold: subItem.selected,
              as: "span",
              className: "item-text-subitem",
              children: menuOpen ? subItem.text : subItem.text.substring(0, 1)
            })
          }, `sub-item${text}${index}`);
        })
      })
    ]
  });
};
var Menu = ({ menu }) => {
  const [menuOpen, setMenuOpen] = useState7(false);
  const theme = useTheme();
  return /* @__PURE__ */ jsxs16(MenuComponent, {
    isOpen: menuOpen,
    children: [
      /* @__PURE__ */ jsx27(MenuItemContainer, {
        className: menuOpen ? "logo-container" : "logo-container-center",
        children: menuOpen ? /* @__PURE__ */ jsx27(Brand, {
          inverted: theme.variant === "dark"
        }) : /* @__PURE__ */ jsx27(Isotype, {
          inverted: theme.variant === "dark"
        })
      }),
      /* @__PURE__ */ jsx27(MenuItemContainer, {
        className: "icon-container",
        onClick: () => setMenuOpen(!menuOpen),
        "data-testid": "open-button",
        children: /* @__PURE__ */ jsx27(ItemComponent, {
          menuOpen,
          children: /* @__PURE__ */ jsx27(Icon, {
            name: menuOpen ? "ArrowLeftIcon" : "ArrowRightIcon",
            color: "neutralWeak"
          })
        })
      }),
      /* @__PURE__ */ jsx27("ul", {
        children: menu && menu.map((item, index) => {
          return /* @__PURE__ */ jsx27(MenuItem, {
            ...item,
            menuOpen
          }, "item" + index);
        })
      })
    ]
  });
};
Menu.displayName = "Menu";

// src/Select/Select.tsx
import { useRef as useRef2, useState as useState9, useEffect as useEffect9 } from "react";

// src/CardFlag/CardFlag.tsx
import styled25 from "styled-components";
import { useEffect as useEffect8, useState as useState8 } from "react";

// src/CardFlag/cardFlags.ts
var CardFlags = [
  "AmericanExpress",
  "Ank",
  "Argencard",
  "BBVA",
  "BNAPlus",
  "Bimo",
  "Brubank",
  "Cabal",
  "CabalDebito",
  "Confiable",
  "Crediguia",
  "CuentaDNI",
  "Diners",
  "Discover",
  "InstantPayment",
  "Maestro",
  "Mas",
  "Mastercard",
  "MastercardDebito",
  "MastercardPrepaid",
  "MercadoPago",
  "Milenia",
  "Modo",
  "Naranja",
  "NaranjaX",
  "PaymentCard",
  "Payway",
  "Sucredito",
  "TarjetaSol",
  "Tuya",
  "Uala",
  "Unionpay",
  "Visa",
  "VisaDebito",
  "VisaPrepaid"
];

// src/CardFlag/getCardFlag.ts
var getCardFlag = (name, color) => {
  const checkedName = CardFlags.includes(name) ? name : "PaymentCard";
  const finalName = color === "inverted" ? `${checkedName}Inverted` : color === true ? checkedName : `${checkedName}Gray`;
  return getCDNUrl("assets/images/card-flags/" + finalName + ".svg");
};

// src/CardFlag/CardFlag.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var cardFlagSizes = {
  S: { w: "32px", h: "24px" },
  M: { w: "48px", h: "32px" }
};
var ImageComponent = styled25.img`
  width: ${({ size }) => cardFlagSizes[size != null ? size : "M"].w};
  height: ${({ size }) => cardFlagSizes[size != null ? size : "M"].h};
  min-width: ${({ size }) => cardFlagSizes[size != null ? size : "M"].w};
  min-height: ${({ size }) => cardFlagSizes[size != null ? size : "M"].h};
`;
var CardFlag = ({ size, name, color = true }) => {
  const [cardFlag, setCardFlag] = useState8("");
  if (!name)
    return null;
  useEffect8(() => {
    const link = getCardFlag(name, color);
    setCardFlag(link);
  }, [name]);
  return /* @__PURE__ */ jsx28(ImageComponent, {
    src: cardFlag,
    size,
    alt: name
  });
};
CardFlag.displayName = "CardFlag";

// src/Select/styles.tsx
import styled26, { css as css10 } from "styled-components";
var MultiselectWrapper = styled26.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  position: relative;

  .icon-invalid {
    vertical-align: middle;
  }
`;
var MultiselectContainer = styled26.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  height: 40px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: 10px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius["3xlarge"]};
  padding: 7px 11px;
  cursor: pointer;
  transition: all 0.8s;

  .icon-styles {
    min-width: 24px;
    margin-left: auto;
    color: ${({ theme }) => theme.colors.neutral.ic.default};
  }

  .placeholder-text {
    vertical-align: middle;
    margin: auto;
  }

  ${({ isOpen, theme }) => isOpen && css10`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`} !important;
    `}

  ${({ theme, invalid }) => css10`
      &:hover {
        background: ${theme.colors.neutral.bg.weak};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${invalid ? theme.focusStroke.error : theme.focusStroke.default};
      }
    `}

  ${({ invalid, theme }) => invalid && css10`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`} !important;
    `}

  ${({ disabled, theme }) => disabled && css10`
      border: none;
      background: ${theme.colors.neutral.bg.weak};
      cursor: default;
      .icon-styles {
        color: ${({ theme: theme2 }) => theme2.colors.neutral.ic.weak};
      }
    `}
`;
var MultiselectDropdownStyle = styled26.div`
  position: absolute;
  top: ${({ hasHelperText }) => hasHelperText ? "80%" : "100%"};
  max-height: 280px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  transition: max-height 0.3s ease;
  border-radius: ${({ theme }) => theme.borderRadius.xsmall};
  box-shadow: ${({ theme }) => theme.elevation["30"]};
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  z-index: ${({ theme }) => theme.zindex[20]};

  ${(p) => p.isVisible !== true && css10`
      max-height: 0px;
      display: none;
    `}
`;
var MultiselectDropDownList = styled26.ul`
  padding: 8px;
  margin: 0;
  max-height: ${({ searchable }) => searchable ? "160px" : "200px"};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
    margin-right: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.neutral.bg.weaker};
    border-radius: ${({ theme }) => theme.opacity[0]};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.neutral.bg.strong};
    border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  }
`;
var ListItem = styled26.li`
  list-style: none;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border-radius: ${({ theme }) => theme.borderRadius["2xsmall"]};
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.5s;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral.bg.weak};
  }

  ${({ disabled }) => disabled && css10`
      cursor: default;
    `}

  .text-display {
    display: flex;
    flex-direction: column;
  }

  .check-item {
    margin-top: 4px;
  }
`;
var ButtonsContainer = styled26.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;

  .clean-button {
    margin-right: auto;
  }
`;
var SelectWrapper = styled26.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  position: relative;

  .icon-invalid {
    vertical-align: middle;
  }
`;
var SelectValueWrapper = styled26.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
var SelectContainer = styled26.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  height: 40px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: 10px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius["3xlarge"]};
  padding: 7px 11px;
  cursor: pointer;
  transition: all 0.8s;

  .icon-styles {
    min-width: 24px;
    margin-left: auto;
    color: ${({ theme }) => theme.colors.neutral.ic.default};
  }

  .placeholder-text {
    vertical-align: middle;
    margin: auto;
  }

  ${({ isOpen, theme }) => isOpen && css10`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`} !important;
    `}

  ${({ theme, invalid }) => css10`
      &:hover {
        background: ${theme.colors.neutral.bg.weak};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${invalid ? theme.focusStroke.error : theme.focusStroke.default};
      }
    `}

  ${({ invalid, theme }) => invalid && css10`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`} !important;
    `}

  ${({ disabled, theme }) => disabled && css10`
      border: none;
      background: ${theme.colors.neutral.bg.weak};
      cursor: default;
      .icon-styles {
        color: ${({ theme: theme2 }) => theme2.colors.neutral.ic.weak};
      }
    `}
`;
var SelectDropdownStyle = styled26.div`
  position: absolute;
  top: ${({ hasHelperText }) => hasHelperText ? "80%" : "100%"};
  max-height: 256px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  transition: max-height 0.3s ease;
  overflow-y: auto;
  border-radius: ${({ theme }) => theme.borderRadius.xsmall};
  box-shadow: ${({ theme }) => theme.elevation["30"]};
  z-index: ${({ theme }) => theme.zindex[20]};

  &::-webkit-scrollbar {
    width: 4px;
    margin-right: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.neutral.bg.weaker};
    border-radius: ${({ theme }) => theme.opacity[0]};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.neutral.bg.strong};
    border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  }

  ${(p) => p.isVisible !== true && css10`
      max-height: 0px;
      visibility: hidden;
    `}
`;
var SelectDropDownList = styled26.ul`
  padding: 8px;
  margin: 0;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
`;
var LabelContainer4 = styled26.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
`;

// src/Select/Select.tsx
import { jsx as jsx29, jsxs as jsxs17 } from "react/jsx-runtime";
var SelectOption = (item, index, selectedValue, onChangeValue) => {
  const { label, subLabel, value, brand, disabled } = item;
  const theme = useTheme();
  return /* @__PURE__ */ jsxs17(ListItem, {
    role: "option",
    disabled,
    onClick: () => !disabled && onChangeValue(item),
    children: [
      brand && /* @__PURE__ */ jsx29(CardFlag, {
        name: brand,
        size: "S",
        color: theme.variant === "dark" && !disabled ? "inverted" : !disabled
      }),
      /* @__PURE__ */ jsxs17("div", {
        className: "text-display",
        children: [
          /* @__PURE__ */ jsx29(Text, {
            color: disabled ? "neutralWeak" : "neutral",
            size: "s",
            as: "span",
            bold: (selectedValue == null ? void 0 : selectedValue.value) === value,
            children: label
          }),
          /* @__PURE__ */ jsx29(Text, {
            color: "neutralWeak",
            bold: (selectedValue == null ? void 0 : selectedValue.value) === value,
            size: "s",
            as: "span",
            children: subLabel
          })
        ]
      })
    ]
  }, index);
};
var Select = ({
  label,
  helperText = void 0,
  aditionalHelp,
  aditionalHelpButton,
  errorText = void 0,
  onChange,
  defaultValue,
  invalid = false,
  tabIndex,
  disabled = false,
  placeholder,
  fullWidth = false,
  name,
  options,
  required,
  optional,
  onBlur
}) => {
  const [isOpen, setIsOpen] = useState9(false);
  const [selectedValue, setSelectedValue] = useState9(
    options.find((item) => item.value === defaultValue)
  );
  const dropDownRef = useRef2(null);
  const containerRef = useRef2(null);
  const [onblurFlag, setonblurFlag] = useState9(false);
  useOutsideAlerter(dropDownRef, containerRef, isOpen, setIsOpen);
  const togging = () => setIsOpen(!isOpen);
  const onChangeValue = (value) => {
    setSelectedValue(value);
    onChange && onChange({ target: { value: value.value, name } });
    isOpen && togging();
  };
  useEffect9(() => {
    if (!onblurFlag) {
      setonblurFlag(true);
    } else {
      if (!isOpen) {
        onBlur && onBlur();
      }
    }
  }, [isOpen]);
  return /* @__PURE__ */ jsxs17(SelectWrapper, {
    children: [
      /* @__PURE__ */ jsxs17(LabelContainer4, {
        children: [
          /* @__PURE__ */ jsxs17(Text, {
            variant: "caption",
            as: "span",
            children: [
              label,
              required && /* @__PURE__ */ jsx29(Text, {
                variant: "caption",
                as: "span",
                color: "neutralWeak",
                children: "(Obligatorio)"
              }),
              optional && /* @__PURE__ */ jsx29(Text, {
                variant: "caption",
                as: "span",
                color: "neutralWeak",
                children: "(Opcional)"
              })
            ]
          }),
          label && aditionalHelp && /* @__PURE__ */ jsxs17(Popover, {
            children: [
              /* @__PURE__ */ jsx29(PopoverTrigger, {
                children: /* @__PURE__ */ jsx29(Icon, {
                  name: "HelpIcon",
                  size: "S",
                  color: "neutralWeak"
                })
              }),
              /* @__PURE__ */ jsx29(PopoverContent, {
                subtitle: aditionalHelp,
                preferredPosition: "rightBottom",
                button: aditionalHelpButton
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs17(SelectContainer, {
        role: "combobox",
        invalid,
        tabIndex,
        disabled,
        fullWidth,
        isOpen,
        onClick: () => !disabled && togging(),
        ref: containerRef,
        "data-testid": "toogle-dropdown",
        children: [
          /* @__PURE__ */ jsx29(SelectValueWrapper, {
            children: /* @__PURE__ */ jsx29(Text, {
              color: selectedValue ? "neutral" : "neutralWeak",
              as: "span",
              className: "placeholder-text",
              children: (selectedValue == null ? void 0 : selectedValue.value) ? `${selectedValue.label} ${selectedValue.subLabel ? "| " + selectedValue.subLabel : ""}` : placeholder
            })
          }),
          /* @__PURE__ */ jsx29(Icon, {
            name: isOpen ? "ChevronUpSmallIcon" : "ChevronDownSmallIcon",
            className: "icon-styles"
          }),
          /* @__PURE__ */ jsx29("input", {
            type: "hidden",
            value: selectedValue == null ? void 0 : selectedValue.value
          })
        ]
      }),
      /* @__PURE__ */ jsx29(SelectDropdownStyle, {
        isVisible: isOpen,
        fullWidth,
        hasHelperText: !!helperText || !!errorText,
        ref: dropDownRef,
        children: /* @__PURE__ */ jsx29(SelectDropDownList, {
          role: "listbox",
          children: options.map((opt, index) => SelectOption(opt, index, selectedValue, onChangeValue))
        })
      }),
      /* @__PURE__ */ jsxs17(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && /* @__PURE__ */ jsx29(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
Select.displayName = "Select";

// src/Select/Multiselect.tsx
import { useEffect as useEffect10, useRef as useRef3, useState as useState10 } from "react";

// src/Select/SearchInput.tsx
import styled27 from "styled-components";
import { jsx as jsx30, jsxs as jsxs18 } from "react/jsx-runtime";
var SearchInputContainer = styled27.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  margin: 12px 12px 0 12px;
  padding: 0 12px;
  height: 40px;
  gap: 12px;

  background: ${({ theme }) => theme.colors.neutral.bg.weak};
  border-radius: 48px;

  input {
    width: 100%;
    background: transparent;
    border: none;
    font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
    font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
    letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
    line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
    font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
    color: ${({ theme }) => theme.colors.neutral.text.default};
    caret-color: ${({ theme }) => theme.colors.primary.border.default};

    ::placeholder {
      color: ${({ theme }) => theme.colors.neutral.text.weak};
    }

    :focus {
      border: none;
      outline: none;
    }
    :active {
      border: none;
      outline: none;
    }
  }

  .hidden-icon {
    visibility: hidden;
  }

  .clean-icon {
    cursor: pointer;
  }
`;
var SearchInput = ({
  placeholder,
  defaultValue,
  onChange,
  handleClear
}) => {
  return /* @__PURE__ */ jsxs18(SearchInputContainer, {
    children: [
      /* @__PURE__ */ jsx30(Icon, {
        name: "SearchIcon",
        color: "neutralWeak"
      }),
      /* @__PURE__ */ jsx30("input", {
        placeholder,
        onChange,
        value: defaultValue,
        "data-testid": "search-input"
      }),
      /* @__PURE__ */ jsx30(Icon, {
        name: "CrossIcon",
        color: "neutralWeak",
        className: defaultValue ? "clean-icon" : "hidden-icon",
        onClick: handleClear,
        "data-testid": "search-clear"
      })
    ]
  });
};

// src/Select/Multiselect.tsx
import { jsx as jsx31, jsxs as jsxs19 } from "react/jsx-runtime";
var MultiselectOption = ({ label, value, brand, disabled, subLabel }, index, tempValues, onChangeValue, conditionSelectAll) => {
  const theme = useTheme();
  return /* @__PURE__ */ jsxs19(ListItem, {
    role: "option",
    disabled,
    onClick: () => !disabled && onChangeValue(value),
    children: [
      /* @__PURE__ */ jsx31(Checkbox, {
        isChecked: index === "select-all" ? !!conditionSelectAll : tempValues.includes(value),
        onChange: () => !disabled && onChangeValue(value),
        isDisabled: disabled,
        size: "s",
        className: "check-item"
      }),
      brand && /* @__PURE__ */ jsx31(CardFlag, {
        name: brand,
        size: "S",
        color: theme.variant === "dark" && !disabled ? "inverted" : !disabled
      }),
      /* @__PURE__ */ jsxs19("div", {
        className: "text-display",
        children: [
          /* @__PURE__ */ jsx31(Text, {
            color: disabled ? "neutralWeak" : "neutral",
            size: "s",
            as: "span",
            bold: tempValues.includes(value),
            children: label
          }),
          /* @__PURE__ */ jsx31(Text, {
            color: "neutralWeak",
            size: "s",
            as: "span",
            children: subLabel
          })
        ]
      })
    ]
  }, index);
};
var Multiselect = ({
  label,
  helperText = void 0,
  aditionalHelp,
  aditionalHelpButton,
  errorText = void 0,
  onChange,
  defaultValue,
  searchable = false,
  invalid = false,
  tabIndex,
  disabled = false,
  placeholder,
  fullWidth = false,
  name,
  options,
  required = false,
  optional = false,
  selectAll = true,
  selectAllLabel = "Todos",
  clearText = "Borrar",
  onBlur
}) => {
  const [isOpen, setIsOpen] = useState10(false);
  const [multiselectedValue, setMultiselectedValue] = useState10(defaultValue != null ? defaultValue : []);
  const [tempValues, setTempValues] = useState10(multiselectedValue);
  const [searchText, setSearchText] = useState10("");
  const [filteredItems, setFilteredItems] = useState10(options);
  const [onblurFlag, setonblurFlag] = useState10(false);
  const dropDownRef = useRef3(null);
  const containerRef = useRef3(null);
  useEffect10(() => {
    if (!onblurFlag) {
      setonblurFlag(true);
    } else {
      if (!isOpen) {
        onBlur && onBlur();
      }
    }
  }, [isOpen]);
  const togging = () => setIsOpen(!isOpen);
  useEffect10(() => {
    async function filterItems() {
      const result = await options.filter(
        (item) => {
          var _a;
          return item.label.toLowerCase().includes(searchText.toLowerCase()) || ((_a = item.subLabel) == null ? void 0 : _a.toLowerCase().includes(searchText.toLowerCase()));
        }
      );
      setFilteredItems(result);
    }
    if (searchText.length > 2) {
      filterItems();
    } else {
      setFilteredItems(options);
    }
  }, [searchText]);
  useEffect10(() => {
    setFilteredItems(options);
    setMultiselectedValue(defaultValue != null ? defaultValue : []);
    setTempValues(multiselectedValue);
    setSearchText("");
  }, [options]);
  const onSelectAll = () => {
    if (JSON.stringify(tempValues) === JSON.stringify(options.filter((opt) => opt.disabled !== true).map((opt) => opt.value))) {
      setTempValues([]);
    } else {
      setTempValues(options.filter((opt) => opt.disabled !== true).map((opt) => opt.value));
    }
  };
  const onChangeValue = (value) => {
    let newArray = [];
    tempValues.includes(value) ? newArray = tempValues.filter((item) => item !== value) : newArray = [...tempValues, value];
    setTempValues(newArray);
  };
  const acceptChanges = () => {
    setMultiselectedValue(tempValues);
    isOpen && togging();
  };
  useEffect10(() => {
    multiselectedValue.length > 0 && onChange && onChange({ target: { value: multiselectedValue, name } });
  }, [multiselectedValue]);
  const discardChanges = (state) => {
    setTempValues(multiselectedValue);
    setIsOpen(state);
  };
  const cleanSelection = () => {
    setMultiselectedValue([]);
    setTempValues([]);
    onChange({ target: { value: [], name } });
  };
  useOutsideAlerter(dropDownRef, containerRef, isOpen, discardChanges);
  return /* @__PURE__ */ jsxs19(MultiselectWrapper, {
    children: [
      /* @__PURE__ */ jsxs19(LabelContainer4, {
        children: [
          /* @__PURE__ */ jsxs19(Text, {
            variant: "caption",
            as: "span",
            children: [
              label,
              required && /* @__PURE__ */ jsx31(Text, {
                variant: "caption",
                as: "span",
                color: "neutralWeak",
                children: "(Obligatorio)"
              }),
              optional && /* @__PURE__ */ jsx31(Text, {
                variant: "caption",
                as: "span",
                color: "neutralWeak",
                children: "(Opcional)"
              })
            ]
          }),
          label && aditionalHelp && /* @__PURE__ */ jsxs19(Popover, {
            children: [
              /* @__PURE__ */ jsx31(PopoverTrigger, {
                children: /* @__PURE__ */ jsx31(Icon, {
                  name: "HelpIcon",
                  size: "S",
                  color: "neutralWeak"
                })
              }),
              /* @__PURE__ */ jsx31(PopoverContent, {
                subtitle: aditionalHelp,
                preferredPosition: "rightBottom",
                button: aditionalHelpButton
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs19(MultiselectContainer, {
        role: "combobox",
        invalid,
        tabIndex,
        disabled,
        fullWidth,
        isOpen,
        onClick: () => !disabled && togging(),
        ref: containerRef,
        "data-testid": "toogle-dropdown",
        children: [
          /* @__PURE__ */ jsx31(SelectValueWrapper, {
            children: /* @__PURE__ */ jsx31(Text, {
              color: multiselectedValue.length > 0 ? "neutral" : "neutralWeak",
              as: "span",
              className: "placeholder-text",
              children: multiselectedValue.length > 0 ? `${placeholder} (${multiselectedValue.length.toString()})` : placeholder
            })
          }),
          /* @__PURE__ */ jsx31(Icon, {
            name: isOpen ? "ChevronUpSmallIcon" : "ChevronDownSmallIcon",
            className: "icon-styles"
          }),
          /* @__PURE__ */ jsx31("input", {
            type: "hidden",
            value: multiselectedValue
          })
        ]
      }),
      /* @__PURE__ */ jsxs19(MultiselectDropdownStyle, {
        isVisible: isOpen,
        fullWidth,
        hasHelperText: !!helperText || !!errorText,
        ref: dropDownRef,
        children: [
          searchable && /* @__PURE__ */ jsx31(SearchInput, {
            placeholder,
            defaultValue: searchText,
            onChange: (e) => setSearchText(e.currentTarget.value),
            handleClear: () => setSearchText("")
          }),
          /* @__PURE__ */ jsxs19(MultiselectDropDownList, {
            role: "listbox",
            searchable,
            children: [
              selectAll && MultiselectOption(
                { label: selectAllLabel, value: "all" },
                "select-all",
                tempValues,
                onSelectAll,
                JSON.stringify(tempValues) === JSON.stringify(
                  options.filter((opt) => opt.disabled !== true).map((opt) => opt.value)
                )
              ),
              filteredItems.map(
                (opt, index) => MultiselectOption(opt, index, tempValues, onChangeValue)
              )
            ]
          }),
          /* @__PURE__ */ jsxs19(ButtonsContainer, {
            children: [
              /* @__PURE__ */ jsx31(Button, {
                size: "small",
                variant: "ghost",
                onClick: cleanSelection,
                disabled: multiselectedValue.length < 1,
                children: clearText
              }),
              /* @__PURE__ */ jsx31(Button, {
                size: "small",
                onClick: acceptChanges,
                children: "Aplicar"
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs19(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && /* @__PURE__ */ jsx31(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
Multiselect.displayName = "Multiselect";

// src/Container/Container.tsx
import styled28, { css as css11 } from "styled-components";
import { jsx as jsx32 } from "react/jsx-runtime";
var ContainerComponent = styled28.div`
  display: ${({ display }) => display};
  min-width: 160px;
  min-height: ${({ minHeight }) => minHeight};
  width: 100%;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  padding: ${({ theme }) => theme.spacing.large};
  box-sizing: border-box;
  transition: box-shadow ease-in-out 0.3s;
  border-radius: ${({ theme }) => theme.borderRadius.medium};

  box-shadow: ${({ theme, elevation }) => theme.elevation[elevation]};

  ${({ hoverAnimation, hoverElevation }) => hoverAnimation && css11`
      :hover {
        box-shadow: ${({ theme }) => theme.elevation[hoverElevation]};
      }
    `}
`;
var Container = ({
  elevation = "10",
  hoverAnimation = false,
  hoverElevation = "30",
  minHeight = "160px",
  display = "block",
  ...htmlProps
}) => {
  return /* @__PURE__ */ jsx32(ContainerComponent, {
    ...htmlProps,
    hoverAnimation,
    elevation,
    hoverElevation,
    minHeight,
    display
  });
};
Container.displayName = "Container";

// src/SideSheet/SideSheet.tsx
import { useEffect as useEffect11, useState as useState11 } from "react";

// src/SideSheet/styled.ts
import styled29 from "styled-components";
var SideSheetContainer = styled29.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.large};
  z-index: ${({ theme }) => theme.zindex[60]};
  position: ${(props) => props.position};
  right: 0%;
  top: 0%;
  bottom: 0%;
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border-left: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  min-width: ${(props) => props.width};
  max-width: ${(props) => props.width};
  box-shadow: ${({ theme }) => theme.elevation[30]};
  transform: ${(props) => props.isShown ? "translateX(0%)" : `translateX(100%)`};
  transition: 0.3s ease-in-out;
  overflow-y: auto;
`;
var PageMask = styled29.div`
  display: block;
  background: ${({ theme, isShown }) => isShown ? theme.colors.neutral.bg.scrim : "transparent"};
  z-index: ${({ isShown, theme }) => isShown ? theme.zindex[50] : "-1"};
  position: ${(props) => props.position};
  top: 0px;
  left: 0px;
  height: 100%;
  right: 0px;
  transition: all 0.3s ease-in-out;
`;
var CrossIconContainer2 = styled29.div`
  display: flex;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 24px;
`;
var SideSheetContent = styled29.div`
  height: 100%;
`;
var FullContainer = styled29.div``;

// src/SideSheet/SideSheet.tsx
import { Fragment as Fragment7, jsx as jsx33, jsxs as jsxs20 } from "react/jsx-runtime";
var SideSheet = ({
  children,
  isShown,
  width = "360px",
  onClose,
  position = "fixed"
}) => {
  const [isShownProps, setIsShownProps] = useState11(false);
  useEffect11(() => {
    setIsShownProps(isShown);
  }, [isShown]);
  return /* @__PURE__ */ jsxs20(Fragment7, {
    children: [
      /* @__PURE__ */ jsx33(PageMask, {
        isShown: isShownProps,
        onClick: () => {
          setIsShownProps(false);
          onClose && onClose();
        },
        "data-testid": "sidesheet-page-mask",
        position
      }),
      /* @__PURE__ */ jsxs20(SideSheetContainer, {
        isShown: isShownProps,
        width,
        "data-testid": "sidesheet-sidesheet-container",
        position,
        children: [
          /* @__PURE__ */ jsx33(CrossIconContainer2, {
            "data-testid": "sidesheet-cross-icon-container",
            onClick: () => {
              setIsShownProps(false);
              onClose && onClose();
            },
            children: /* @__PURE__ */ jsx33(Icon, {
              size: "M",
              name: "CrossIcon",
              color: "neutral"
            })
          }),
          /* @__PURE__ */ jsx33(SideSheetContent, {
            children
          })
        ]
      })
    ]
  });
};
var SideSheet_default = SideSheet;

// src/Tooltip/Tooltip.tsx
import { useState as useState12 } from "react";

// src/Tooltip/styles.tsx
import styled30, { css as css12 } from "styled-components";
var TooltipWrapper = styled30.div`
  position: relative;
  display: inline-flex;
`;
var TooltipTarget = styled30.div`
  border: none;
  background: inherit;
  padding: 5px;
  margin: -1px;
  font-size: inherit;
  color: inherit;
  cursor: inherit;
  display: flex;
  ${({ showOnFocus }) => !showOnFocus && css12`
      outline: none;
    `};
`;
var CenterContainer = styled30.div`
  position: absolute;
  width: 300px;
  margin-left: -150px;
  display: flex;
  justify-content: center;
  align-items: center;
  left: 50%;
  bottom: calc(100% + 5px);
  pointer-events: none;
  z-index: ${({ theme }) => theme.zindex[40]};

  ${({ position }) => {
  switch (position) {
    case "bottom":
      return css12`
          bottom: unset !important;
          top: calc(100% + 5px);
        `;
    case "left":
      return css12`
          margin-right: 0;
          left: unset;
          top: 50%;
          right: calc(100% + 5px);
          width: max-content;
        `;
    case "right":
      return css12`
          margin-left: 0;
          top: 50%;
          left: calc(100% + 5px);
          width: max-content;
        `;
    default:
      return css12`
          bottom: calc(100% + 5px);
        `;
  }
}}
`;
var TooltipBox = styled30.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.neutral.bg.stronger};
  border-radius: ${({ theme }) => theme.borderRadius.xsmall};
  box-sizing: border-box;
  padding: 8px 12px;
  display: flex;
  max-width: ${({ maxWidth }) => maxWidth};
  ${({ position }) => {
  switch (position) {
    case "right":
      return css12`
          color: #000;
        `;
    default:
      return css12``;
  }
}}
  &:after {
    content: '';
    position: absolute;
    width: 1px;
    height: 1px;
    border-width: 4px;
    border-style: solid;
    border-color: ${({ theme }) => theme.colors.neutral.bg.stronger} transparent transparent
      transparent;
    left: calc(50% - 4.5px);
    top: 100%;
  }
  ${({ position }) => {
  switch (position) {
    case "bottom":
      return css12`
          &:after {
            border-color: transparent transparent ${({ theme }) => theme.colors.neutral.bg.stronger}
              transparent;
            top: unset;
            width: 1px;
            bottom: 100%;
            left: calc(50% - 4px);
          }
        `;
    case "left":
      return css12`
          &:after {
            border-color: transparent transparent transparent
              ${({ theme }) => theme.colors.neutral.bg.stronger};
            left: 100%;
            top: calc(50% - 4px);
          }
        `;
    case "right":
      return css12`
          &:after {
            border-color: transparent ${({ theme }) => theme.colors.neutral.bg.stronger} transparent
              transparent;
            right: 100%;
            left: unset;
            top: calc(50% - 4px);
          }
        `;
    default:
      return css12``;
  }
}}
`;

// src/Tooltip/Tooltip.tsx
import { jsx as jsx34, jsxs as jsxs21 } from "react/jsx-runtime";
var Tooltip = ({
  position = "right",
  text,
  children,
  openTooltip,
  maxWidth = "300px"
}) => {
  const [isHovered, setIsHovered] = useState12(false);
  const [isFocused, setIsFocused] = useState12(false);
  return /* @__PURE__ */ jsxs21(TooltipWrapper, {
    children: [
      /* @__PURE__ */ jsx34(TooltipTarget, {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        showOnFocus: isFocused,
        "data-testid": "tooltip-container",
        children
      }),
      (isFocused || isHovered || openTooltip) && /* @__PURE__ */ jsx34(CenterContainer, {
        position,
        children: /* @__PURE__ */ jsx34(TooltipBox, {
          position,
          maxWidth,
          children: /* @__PURE__ */ jsx34(Text, {
            as: "span",
            variant: "caption",
            color: "neutralWeaker",
            children: text
          })
        })
      })
    ]
  });
};
Tooltip.displayName = "Tooltip";

// src/Grid/styled.tsx
import styled31 from "styled-components";

// src/utils/gridFunctions.ts
var returnPercentageForItemWidth = (num, columnCount = 12) => {
  return `${num / columnCount * 100}%`;
};
var addResponsiveClasses = (props) => {
  let returnString = props.xs ? `xs-${props.xs}` : "";
  returnString += props.sm ? ` sm-${props.sm}` : "";
  returnString += props.md ? ` md-${props.md}` : "";
  returnString += props.lg ? ` lg-${props.lg}` : "";
  returnString += props.xl ? ` xl-${props.xl}` : "";
  return returnString;
};
var createGridItemSizeClasses = (size, columns, props) => {
  let i;
  let returnString = size === "xs" ? `.grid-item {
    box-sizing: border-box;
    -webkit-box-flex: 0;
    padding: ${props.theme.breakpoints.xs.gutter / 2}px;
    flex-grow: ${props.grow || props.autoWidth ? 1 : 0};
    max-width: ${props.autoWidth || props.grow ? `unset !important` : ""};
    flex-basis: ${props.autoWidth ? `0px !important` : ""};
  }` : ` .grid-item {
    padding: ${props.theme.breakpoints[size].gutter / 2}px;
  }`;
  for (i = 0; i < columns; i++) {
    returnString += `.grid-item.${size}-${i + 1} {
      flex-basis: ${returnPercentageForItemWidth(i + 1, size === "xs" ? 4 : 12)};
      max-width: ${returnPercentageForItemWidth(i + 1, size === "xs" ? 4 : 12)};
    }`;
  }
  return returnString;
};

// src/Grid/styled.tsx
var GridContainer = styled31.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-content: flex-start;
  justify-content: ${(props) => props.justifyContent || "start"};
  flex-wrap: ${(props) => props.wrap || "wrap"};
  align-items: ${(props) => props.align || "normal"};

  /* xs */
  ${(props) => createGridItemSizeClasses("xs", 4, props)}

  /* sm */
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.sm.width}px) {
    ${(props) => createGridItemSizeClasses("sm", 12, props)}
  }
  /* md */
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.md.width}px) {
    ${(props) => createGridItemSizeClasses("md", 12, props)}
  }
  /* lg */
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg.width}px) {
    ${(props) => createGridItemSizeClasses("lg", 12, props)}
  }

  /* xl */
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl.width}px) {
    ${(props) => createGridItemSizeClasses("xl", 12, props)}
  }
`;

// src/Grid/Item/styled.tsx
import styled32 from "styled-components";
var StyledGridItem = styled32.div.attrs((props) => ({
  className: `grid-item ` + addResponsiveClasses(props)
}))``;

// src/Grid/Item/GridItem.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var GridItem = ({ children, xs, sm, md, lg, xl }) => {
  return /* @__PURE__ */ jsx35(StyledGridItem, {
    xs,
    sm,
    md,
    lg,
    xl,
    "data-testid": "grid-item-test-id",
    children
  });
};
var GridItem_default = GridItem;

// src/Grid/Grid.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
var Grid = ({
  children,
  justifyContent,
  wrap,
  align,
  grow,
  autoWidth
}) => {
  return /* @__PURE__ */ jsx36(GridContainer, {
    justifyContent,
    wrap,
    align,
    grow,
    autoWidth,
    "data-testid": "grid-component-id",
    children
  });
};
Grid.Item = GridItem_default;
var Grid_default = Grid;

// src/Accordion/Accordion.tsx
import { Children, cloneElement } from "react";
import styled33 from "styled-components";

// src/Accordion/AccordionContext.tsx
import { createContext, useState as useState13 } from "react";
import { jsx as jsx37 } from "react/jsx-runtime";
var AccordionContext = createContext({
  selected: null,
  saveOpen: null
});
var AccordionProvider = ({ children }) => {
  const [selected, setSelected] = useState13(null);
  const saveOpen = (item) => {
    setSelected(selected === item ? null : item);
  };
  return /* @__PURE__ */ jsx37(AccordionContext.Provider, {
    value: { selected, saveOpen },
    children
  });
};
var AccordionContext_default = AccordionProvider;

// src/Accordion/Accordion.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var AccordionWrapper = styled33.div`
  width: 100%;
  min-width: 300px;
`;
var Accordion = ({ children, variant = "regular" }) => {
  return /* @__PURE__ */ jsx38(AccordionWrapper, {
    variant,
    children: /* @__PURE__ */ jsx38(AccordionContext_default, {
      children: Children.map(children, (child) => {
        return cloneElement(child, { variant });
      })
    })
  });
};
Accordion.displayName = "Accordion";

// src/Accordion/AccordionItem.tsx
import { useContext as useContext2, useRef as useRef4, useState as useState14 } from "react";
import styled34 from "styled-components";
import { jsx as jsx39, jsxs as jsxs22 } from "react/jsx-runtime";
var statusDict = {
  pending: ["PENDIENTE", "gray"],
  inProgress: ["EN PROGRESO", "orange"],
  completed: ["COMPLETO", "green"]
};
var AccordionContainer = styled34.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ theme, variant }) => variant === "regular" ? "28px 8px 28px 0px" : `${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.small} 0px`};
  gap: ${({ open }) => open ? "8px" : "0px"};
  transition: gap 0.2s linear;
  width: 100%;
  box-sizing: border-box;

  border-bottom: ${({ theme, variant }) => variant === "regular" ? `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}` : `none`}; // que color va acá?

  .title {
    width: 100%;
    text-align: left;
  }

  .chevron-rotation {
    transition: all 0.25s ease-in-out;
    transform: ${({ open }) => open && "rotate(180deg)"};
  }
`;
var AccordionButton = styled34.button`
  width: 100%;
  gap: ${({ theme }) => theme.spacing.xsmall};
  background-color: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: row;
`;
var AccordionContent = styled34.div`
  width: 100%;
  max-height: ${({ maxHeight }) => `${maxHeight}px`};
  transition: max-height 0.2s linear;
  overflow: hidden;
`;
var SmallAccordionTitle = styled34(Text)`
  font-weight: ${({ theme }) => theme.fonts.body.m.highContrast.fontWeight};
`;
var AccordionItem = ({
  title,
  children,
  status,
  id,
  variant = "regular"
}) => {
  const { selected, saveOpen } = useContext2(AccordionContext);
  const contentRef = useRef4(null);
  const [buttonHover, setButtonHover] = useState14(false);
  const contentHeight = id === selected && contentRef.current ? contentRef.current.scrollHeight : 0;
  return /* @__PURE__ */ jsxs22(AccordionContainer, {
    open: id === selected,
    variant,
    children: [
      /* @__PURE__ */ jsxs22(AccordionButton, {
        onClick: () => saveOpen && saveOpen(id),
        onMouseEnter: () => setButtonHover(true),
        onMouseLeave: () => setButtonHover(false),
        children: [
          variant != "small" ? /* @__PURE__ */ jsx39(Heading, {
            size: "xs",
            className: "title",
            color: buttonHover ? "primary" : "neutral",
            children: title
          }) : /* @__PURE__ */ jsx39(SmallAccordionTitle, {
            size: "s",
            className: "title",
            color: buttonHover ? "primary" : "neutral",
            variant: "body",
            "data-testid": "small-accordion-title",
            children: title
          }),
          status && variant !== "small" && /* @__PURE__ */ jsx39(Badge, {
            emphasis: "medium",
            label: statusDict[status][0],
            color: statusDict[status][1],
            "data-testid": "accordion-badge"
          }),
          /* @__PURE__ */ jsx39(Icon, {
            size: "M",
            name: "ChevronDownSmallIcon",
            color: buttonHover ? "primary" : "neutral",
            className: "chevron-rotation"
          })
        ]
      }),
      /* @__PURE__ */ jsx39(AccordionContent, {
        ref: contentRef,
        maxHeight: contentHeight,
        children: typeof children === "string" ? /* @__PURE__ */ jsx39(Text, {
          size: "s",
          color: "neutralWeak",
          children
        }) : children
      })
    ]
  });
};
AccordionItem.displayName = "AccordionItem";

// src/Alert/styles.tsx
import styled35 from "styled-components";

// src/Alert/functions.tsx
var colorFromVariant = (theme, variant) => {
  let returnValue = "";
  switch (variant) {
    case "info":
      returnValue = `8px 0 ${theme.colors.info.bg.default} inset `;
      break;
    case "warning":
      returnValue = `8px 0 ${theme.colors.warning.bg.default} inset `;
      break;
    case "success":
      returnValue = `8px 0 ${theme.colors.success.bg.default} inset `;
      break;
    case "error":
      returnValue = `8px 0 ${theme.colors.danger.bg.default} inset `;
      break;
    default:
      returnValue = `8px 0 ${theme.colors.blue[4]} inset `;
      break;
  }
  return returnValue;
};
var iconTypeFromVariant = (variant) => {
  let returnValue;
  switch (variant) {
    case "info":
      returnValue = { name: "InfoFilledIcon", color: variant };
      break;
    case "warning":
      returnValue = { name: "WarningFilledIcon", color: variant };
      break;
    case "success":
      returnValue = { name: `CheckCircleFilledIcon`, color: variant };
      break;
    case "error":
      returnValue = { name: `WarningTriangleFilledIcon`, color: "danger" };
      break;
    default:
      returnValue = { name: "InfoFilledIcon", color: "info" };
      break;
  }
  return returnValue;
};

// src/Alert/styles.tsx
var AlertContainer = styled35.div`
  position: relative;
  display: ${({ isShown }) => isShown ? "flex" : "none"};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium} ${theme.spacing.small} ${theme.spacing.medium}`};
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.medium} solid ${theme.colors.neutral.border.weak}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  max-width: ${({ fullWidth, maxWidth }) => fullWidth ? "100%" : maxWidth};
  overflow: hidden;

  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5%;
    height: 100%;
    border-radius: 1.5px;
    box-shadow: ${({ theme, variant }) => colorFromVariant(theme, variant)};
  }
`;
var IconContainer = styled35.div`
  display: inline-flex;
`;
var TopContentContainer = styled35.div`
  display: inline-flex;
  align-items: center;
`;
var TitleContainer = styled35.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing["2xsmall"]};
`;
var SubtitleAndButtonContainer = styled35.div`
  button {
    margin-top: ${({ theme }) => theme.spacing.small};
    margin-left: -${({ theme }) => theme.spacing.small};
  }
`;
var ContentContainer2 = styled35.div`
  display: flex;
  flex-direction: column;
  padding-left: ${({ theme }) => theme.spacing.small};
`;
var CrossComponentContainer = styled35.div`
  margin-left: auto;
  cursor: pointer;
`;

// src/Alert/Alert.tsx
import { jsx as jsx40, jsxs as jsxs23 } from "react/jsx-runtime";
var Alert = ({
  title,
  subtitle,
  variant = "info",
  fullWidth = false,
  button,
  maxWidth = "425px",
  withCloseIcon,
  closeIconOnclick,
  isShown = true,
  hideLeftIcon = false
}) => {
  return /* @__PURE__ */ jsxs23(AlertContainer, {
    fullWidth,
    variant,
    maxWidth,
    "data-testid": "alert-container",
    isShown,
    id: "alert-container-id",
    withCloseIcon,
    children: [
      !hideLeftIcon && /* @__PURE__ */ jsx40(IconContainer, {
        "data-testid": "icon-container",
        children: /* @__PURE__ */ jsx40(Icon, {
          name: iconTypeFromVariant(variant).name,
          color: iconTypeFromVariant(variant).color,
          "data-testid": "alert-icon"
        })
      }),
      /* @__PURE__ */ jsxs23(ContentContainer2, {
        children: [
          title && /* @__PURE__ */ jsx40(TitleContainer, {
            "data-testid": "title-container",
            children: /* @__PURE__ */ jsx40(Heading, {
              size: "xs",
              bold: true,
              color: "neutralStrong",
              children: title
            })
          }),
          /* @__PURE__ */ jsxs23(SubtitleAndButtonContainer, {
            "data-testid": "subtitle-and-button-container",
            children: [
              /* @__PURE__ */ jsx40(Text, {
                size: "s",
                children: subtitle
              }),
              button && button.label && /* @__PURE__ */ jsx40(Button, {
                color: "primary",
                variant: "ghost",
                onClick: () => button.onClick(),
                "data-testid": "alert-button",
                size: "small",
                children: button.label
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsx40(CrossComponentContainer, {
        onClick: closeIconOnclick ? () => closeIconOnclick() : void 0,
        "data-testid": "cross-component-container",
        children: withCloseIcon && /* @__PURE__ */ jsx40(Icon, {
          name: "CrossIcon",
          size: "M",
          "data-testid": "alert-cross-icon",
          color: "neutral"
        })
      })
    ]
  });
};
var Alert_default = Alert;

// src/Toast/ToastPortal.tsx
import {
  useState as useState16,
  useLayoutEffect,
  forwardRef,
  useEffect as useEffect14,
  useImperativeHandle as useImperativeHandle2
} from "react";
import { createPortal } from "react-dom";

// src/utils/utilFunctions.ts
var uuid = () => {
  let dt = new Date().getTime();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === "x" ? r : r & 3 | 8).toString(16);
  });
};

// src/Toast/Toast.tsx
import { useEffect as useEffect13, useState as useState15 } from "react";

// src/Toast/functions.ts
var returnIconNameFromVariant = (variant) => {
  let returnValue;
  switch (variant) {
    case "error":
      returnValue = "WarningTriangleFilledIcon";
      break;
    case "success":
      returnValue = "CheckCircleFilledIcon";
      break;
    default:
      returnValue = "InfoFilledIcon";
      break;
  }
  return returnValue;
};
var returnBGColorFromVariant = (variant, theme) => {
  let returnValue;
  switch (variant) {
    case "error":
      returnValue = theme.colors.danger.bg.default;
      break;
    case "success":
      returnValue = theme.colors.success.bg.default;
      break;
    case "neutral":
      returnValue = theme.colors.neutral.bg.stronger;
      break;
    default:
      returnValue = theme.colors.info.bg.default;
      break;
  }
  return returnValue;
};

// src/Toast/styles.tsx
import styled36 from "styled-components";
var StyledPortal = styled36.div`
  position: fixed;
  top: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zindex[70]};
`;
var ToastContainer = styled36.div`
  @media screen and (max-width: 760px) {
    position: fixed;
    bottom: ${({ theme }) => theme.spacing.xlarge};
    left: ${({ theme }) => theme.spacing.xsmall};
    min-width: calc(100% - 16px);
    box-sizing: border-box;
    top: auto;
  }

  :hover {
    opacity: ${({ dismissHasBeenClicked }) => dismissHasBeenClicked ? 0 : 1};
    display: flex;
  }

  box-shadow: ${({ theme }) => theme.elevation["40"]};
  display: ${({ isHidden }) => isHidden ? "none" : "flex"};
  position: relative;
  opacity: ${({ isShown }) => isShown ? 1 : 0};
  z-index: ${({ theme }) => theme.zindex[70]};
  right: ${({ theme }) => theme.spacing.xlarge};
  top: ${({ theme }) => theme.spacing.medium};
  align-items: center;
  max-width: 361px;
  min-width: 361px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
  background-color: ${({ variant, theme }) => returnBGColorFromVariant(variant, theme)};
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ease-in-out 0.3s;
`;
var IconContainer2 = styled36.div`
  align-self: baseline;
  margin-right: ${({ theme }) => theme.spacing.small};
  flex-basis: fit-content;
`;
var CenterContentContainer = styled36.div`
  margin-right: ${({ theme }) => theme.spacing.xsmall};
  flex-basis: min-content;
  flex-grow: 1;
`;
var ButtonContainer2 = styled36.div`
  flex-basis: fit-content;
`;
var BottomButtonContainer = styled36.div`
  margin-top: ${({ theme }) => theme.spacing.small};
`;
var CrossIconContainer3 = styled36.div`
  cursor: pointer;
  flex-basis: fit-content;
  margin-left: ${({ theme }) => theme.spacing.xsmall};
  align-self: ${({ buttonPosition }) => buttonPosition === "bottomAction" ? "baseline" : "center"};
`;

// src/Toast/Toast.tsx
import { jsx as jsx41, jsxs as jsxs24 } from "react/jsx-runtime";
var Toast = ({
  title,
  subtitle,
  variant = "info",
  withDismissIcon,
  isShown,
  dismissIconOnClick,
  button,
  buttonPosition = "rightAction",
  autoHideDuration = null,
  index
}) => {
  const [isShownState, setIsShownState] = useState15(true);
  const [isHidden, setIsHidden] = useState15(false);
  const [dismissHasBeenClicked, setDismissHasBeenClicked] = useState15(false);
  useEffect13(() => {
    if (autoHideDuration && autoHideDuration < 5e3) {
      autoHideDuration = 5e3;
    }
  }, [autoHideDuration]);
  useEffect13(() => {
    let timeOutRef = 0;
    if (autoHideDuration && isShown !== false) {
      timeOutRef = window.setTimeout(() => {
        setIsShownState(false);
      }, autoHideDuration);
    }
    return () => {
      window.clearTimeout(timeOutRef);
    };
  }, []);
  return /* @__PURE__ */ jsxs24(ToastContainer, {
    variant,
    isShown: isShownState,
    index,
    isHidden,
    onTransitionEnd: (e) => {
      e.propertyName === "opacity" && setIsHidden(true);
    },
    dismissHasBeenClicked,
    "data-testid": "toast-container",
    children: [
      /* @__PURE__ */ jsx41(IconContainer2, {
        children: variant !== "neutral" && /* @__PURE__ */ jsx41(Icon, {
          name: returnIconNameFromVariant(variant),
          color: "neutralWeaker",
          "data-testid": "toast-icon"
        })
      }),
      /* @__PURE__ */ jsxs24(CenterContentContainer, {
        children: [
          /* @__PURE__ */ jsx41(Text, {
            bold: true,
            size: "s",
            color: "neutralWeaker",
            children: title
          }),
          /* @__PURE__ */ jsx41(Text, {
            size: "s",
            color: "neutralWeaker",
            children: subtitle
          }),
          button && buttonPosition === "bottomAction" && /* @__PURE__ */ jsx41(BottomButtonContainer, {
            "data-testid": "toast-bottomActionButton-container",
            children: /* @__PURE__ */ jsx41(Button, {
              size: "small",
              color: "inverted",
              variant: "outline",
              onClick: button.onClick,
              children: button.label
            })
          })
        ]
      }),
      button && buttonPosition === "rightAction" && /* @__PURE__ */ jsx41(ButtonContainer2, {
        "data-testid": "toast-rightActionButton-container",
        children: /* @__PURE__ */ jsx41(Button, {
          size: "small",
          color: "inverted",
          variant: "outline",
          onClick: button.onClick,
          children: button.label
        })
      }),
      withDismissIcon && /* @__PURE__ */ jsx41(CrossIconContainer3, {
        buttonPosition,
        "data-testid": "toast-cross-icon-container",
        children: /* @__PURE__ */ jsx41(Icon, {
          onClick: () => {
            setIsShownState(false);
            setDismissHasBeenClicked(true);
            dismissIconOnClick && dismissIconOnClick();
          },
          name: "CrossIcon",
          color: "neutralWeaker",
          size: "M",
          "data-testid": "toast-cross-icon"
        })
      })
    ]
  });
};
var Toast_default = Toast;

// src/Toast/ToastPortal.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
var ToastPortal = forwardRef(({}, ref) => {
  const [portalElement, setPortalElement] = useState16(null);
  const [toasts, setToasts] = useState16([]);
  const [portalId, setPortalId] = useState16("");
  useEffect14(() => {
    setPortalId(uuid());
  }, []);
  useImperativeHandle2(ref, () => {
    {
      const id = uuid();
      return {
        addToast(toast) {
          const newToast = { ...toast, id };
          setToasts([...toasts, newToast]);
        }
      };
    }
  });
  useLayoutEffect(() => {
    let element = document.getElementById(portalId);
    let portalCreated = false;
    if (!element) {
      element = createWrapperAndAppendToBody(`portal-${portalId}`);
      portalCreated = true;
    }
    setPortalElement(element);
    return () => {
      if (portalCreated && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
  }, [portalId]);
  const createWrapperAndAppendToBody = (elementId) => {
    const element = document.createElement("div");
    element.setAttribute("id", elementId);
    document.body.appendChild(element);
    return element;
  };
  if (!portalElement)
    return null;
  return createPortal(
    /* @__PURE__ */ jsx42(StyledPortal, {
      children: toasts.map((t, index) => /* @__PURE__ */ jsx42(Toast_default, {
        id: t.id,
        autoHideDuration: t.autoHideDuration,
        title: t.title,
        subtitle: t.subtitle,
        button: t.button,
        variant: t.variant,
        dismissIconOnClick: t.dismissIconOnClick,
        withDismissIcon: t.withDismissIcon,
        buttonPosition: t.buttonPosition,
        index
      }, `toast-${index}`))
    }),
    portalElement
  );
});
var ToastPortal_default = ToastPortal;

// src/Modal/Modal.tsx
import { useCallback, useEffect as useEffect15, useRef as useRef5 } from "react";

// src/Modal/PortalModal.tsx
import { useState as useState17, useLayoutEffect as useLayoutEffect2 } from "react";
import { createPortal as createPortal2 } from "react-dom";
var PortalModal = ({ children, wrapperId }) => {
  const [portalElement, setPortalElement] = useState17(null);
  useLayoutEffect2(() => {
    let element = document.getElementById(wrapperId);
    let portalCreated = false;
    if (!element) {
      element = createWrapperAndAppendToBody(wrapperId);
      portalCreated = true;
    }
    setPortalElement(element);
    return () => {
      if (portalCreated && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
  }, [wrapperId]);
  const createWrapperAndAppendToBody = (elementId) => {
    const element = document.createElement("div");
    element.setAttribute("id", elementId);
    document.body.appendChild(element);
    return element;
  };
  if (!portalElement)
    return null;
  return createPortal2(children, portalElement);
};
var PortalModal_default = PortalModal;

// src/Modal/styled.tsx
import styled37, { keyframes as keyframes3 } from "styled-components";
var fadeModal = keyframes3`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;
var ModalOverlay = styled37.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0px;
  left: 0px;
  background-color: ${({ theme }) => theme.colors.neutral.bg.scrim};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  z-index: ${({ theme }) => theme.zindex[60]};
  backdrop-filter: blur(4px);
  box-sizing: border-box;

  @media (prefers-reduced-motion: no-preference) {
    animation-name: ${fadeModal};
    animation-fill-mode: backwards;
    animation-duration: 400ms;
  }
`;
var ModalContainer = styled37.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: ${({ width }) => width};
  min-height: 50px;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  padding: ${({ theme }) => theme.spacing.large};
  gap: ${({ theme }) => theme.spacing.xlarge};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.elevation[40]};
  box-sizing: border-box;
  transition: all 0.3s ease-in-out;
  overflow: hidden;
`;
var CloseContainer = styled37.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;

  svg {
    &:hover {
      cursor: pointer;
    }
  }
`;
var ModalContent = styled37.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  width: 100%;
`;

// src/Modal/Modal.tsx
import { Fragment as Fragment8, jsx as jsx43, jsxs as jsxs25 } from "react/jsx-runtime";
var Modal = ({ children, show, onClose, width = "500px" }) => {
  const modalRef = useRef5(null);
  useOutsideAlerter(modalRef, modalRef, show, onClose);
  const handleKeyPress = useCallback((event) => {
    if (event.key === "Escape")
      onClose(false);
  }, []);
  useEffect15(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyPress);
      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }
  }, [handleKeyPress, show]);
  return /* @__PURE__ */ jsx43(Fragment8, {
    children: show && /* @__PURE__ */ jsx43(PortalModal_default, {
      wrapperId: "modal-portal",
      children: /* @__PURE__ */ jsx43(ModalOverlay, {
        children: /* @__PURE__ */ jsx43(ModalContainer, {
          ref: modalRef,
          width,
          children: /* @__PURE__ */ jsxs25(ModalContent, {
            children: [
              /* @__PURE__ */ jsx43(CloseContainer, {
                children: /* @__PURE__ */ jsx43(Icon, {
                  name: "CrossIcon",
                  color: "neutral",
                  onClick: () => onClose(false),
                  role: "button",
                  "data-testid": "close-button"
                })
              }),
              children
            ]
          })
        })
      })
    })
  });
};
Modal.displayName = "Modal";

// src/ModalConfirmation/ModalConfirmation.tsx
import styled38 from "styled-components";
import { jsx as jsx44, jsxs as jsxs26 } from "react/jsx-runtime";
var ModalConfirmationContent = styled38.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  width: 100%;
`;
var TextContainer = styled38.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xsmall};
  width: 100%;
`;
var ButtonsContainer2 = styled38.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.medium};
`;
var IconWrapper = styled38.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  width: 48px;
  height: 48px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.danger.bg.weak};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  margin-top: ${({ theme }) => `-${theme.spacing.large}`};
`;
var StatusIcon = () => {
  return /* @__PURE__ */ jsx44(IconWrapper, {
    color: "danger",
    children: /* @__PURE__ */ jsx44(Icon, {
      name: "WarningTriangleIcon",
      color: "danger",
      size: "L"
    })
  });
};
var ModalConfirmation = ({
  title,
  subtitle,
  destructive,
  buttonPrimary,
  buttonSecondary = void 0
}) => {
  return /* @__PURE__ */ jsxs26(ModalConfirmationContent, {
    children: [
      /* @__PURE__ */ jsxs26(TextContainer, {
        children: [
          destructive && /* @__PURE__ */ jsx44(StatusIcon, {}),
          /* @__PURE__ */ jsx44(Heading, {
            size: "s",
            bold: true,
            color: "neutralStrong",
            children: title
          }),
          /* @__PURE__ */ jsx44(Text, {
            children: subtitle
          })
        ]
      }),
      /* @__PURE__ */ jsxs26(ButtonsContainer2, {
        children: [
          buttonSecondary && /* @__PURE__ */ jsx44(Button, {
            onClick: buttonSecondary.action,
            variant: "ghost",
            children: buttonSecondary.text
          }),
          /* @__PURE__ */ jsx44(Button, {
            onClick: buttonPrimary.action,
            color: destructive ? "destructive" : "primary",
            children: buttonPrimary.text
          })
        ]
      })
    ]
  });
};
ModalConfirmation.displayName = "ModalConfirmation";

// src/Tabs/Tabs.tsx
import * as React5 from "react";
import { borderStroke, fontSize as fontSize3, fontWeight as fontWeight3, lineHeight as lineHeight3, spacing } from "@orbita-ui/brand";
import styled39 from "styled-components";
import { jsx as jsx45, jsxs as jsxs27 } from "react/jsx-runtime";
var TabsContainer = styled39.div`
  display: flex;
  flex-direction: ${({ vertical }) => vertical ? "row" : "column"};
  width: 100%;
  height: 100%;
`;
var TabContainer = styled39.div`
  display: flex;
  flex-direction: ${({ vertical }) => vertical ? "column" : "row"};
  border-bottom: ${({ theme, vertical, divider }) => !vertical ? divider ? `${borderStroke.small} solid ${theme.colors.neutral.border.weak}` : "none" : "none"};
  border-left: ${({ theme, vertical, divider }) => vertical ? divider ? `${borderStroke.small} solid ${theme.colors.neutral.border.weak}` : "none" : "none"};
  min-width: fit-content;
  box-sizing: border-box;
`;
var TabButton = styled39.button`
  /* font */
  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  font-size: ${({ size }) => size === "small" ? fontSize3.small : fontSize3.medium};
  line-height: ${({ size }) => size === "small" ? lineHeight3.xsmall : lineHeight3.small};
  font-weight: ${fontWeight3.midContrast};
  transition: all 0.8s;

  color: ${({ theme, active }) => active ? theme.colors.primary.text.default : theme.colors.neutral.text.default};

  display: flex;
  align-items: center;
  padding: ${({ size }) => size === "small" ? spacing.small : `${spacing.small} ${spacing.medium}`};
  gap: ${spacing.xsmall};
  box-sizing: border-box;
  border: none;
  border-bottom: ${({ theme, active, vertical }) => !vertical ? active ? `${borderStroke.medium} solid ${theme.colors.primary.border.default}` : `${borderStroke.small} solid ${theme.colors.neutral.border.weak}` : "none"};
  border-left: ${({ theme, active, vertical }) => vertical ? active ? `${borderStroke.medium} solid ${theme.colors.primary.border.default}` : `${borderStroke.small} solid ${theme.colors.neutral.border.weak}` : "none"};

  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border-radius: ${({ vertical }) => vertical ? "0 8px 8px 0" : "8px 8px 0 0"};

  cursor: pointer;

  transition: all 0.8s;
  &:hover {
    background-color: ${({ theme, active }) => active ? theme.colors.primary.bg.weak : theme.colors.neutral.bg.weak};
  }

  &:disabled {
    cursor: default;
    color: ${({ theme }) => theme.colors.neutral.text.weak};
    background-color: ${({ theme }) => theme.colors.neutral.bg.weaker} !important;
    transition: all 0.8s;
  }
`;
var TabContent = styled39.div`
  padding: 1rem;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
`;
var Tabs = ({ tabs, size = "medium", vertical, divider }) => {
  const [activeTab, setActiveTab] = React5.useState(0);
  if (tabs.length > 6) {
    throw new Error("No podes ingresar mas de 6 elementos");
  }
  return /* @__PURE__ */ jsxs27(TabsContainer, {
    vertical,
    children: [
      /* @__PURE__ */ jsx45(TabContainer, {
        vertical,
        divider,
        children: tabs.map((tab, index) => /* @__PURE__ */ jsxs27(TabButton, {
          active: index === activeTab,
          onClick: () => setActiveTab(index),
          size,
          vertical,
          disabled: tab.disabled,
          children: [
            tab.icon && /* @__PURE__ */ jsx45(Icon, {
              name: tab.icon,
              size: "S",
              color: tab.disabled ? "neutralWeak" : index === activeTab ? "primary" : "neutral"
            }),
            tab.label,
            tab.dot && /* @__PURE__ */ jsx45(Dot, {
              color: tab.dot
            })
          ]
        }, index))
      }),
      /* @__PURE__ */ jsx45(TabContent, {
        children: tabs[activeTab].content
      })
    ]
  });
};
Tabs.displayName = "Tabs";

// src/CodeInput/CodeInput.tsx
import { useEffect as useEffect16, useState as useState19 } from "react";

// src/CodeInput/styles.ts
import styled40 from "styled-components";
var GlobalCodeInputContainer = styled40.div`
  display: flex;
  flex-direction: column;
`;
var CodeInputContainer = styled40.div`
  display: flex;
  justify-content: space-between;
  width: ${({ length }) => length === 4 ? "214px" : "326px"};
`;
var LabelContainer5 = styled40.div`
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  align-items: flex-end;
`;
var HelperContainer = styled40.div`
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  display: flex;
  flex-direction: row;
  svg {
    vertical-align: middle;
  }
`;
var StyledInput = styled40.input`
  :disabled {
    background-color: ${({ theme }) => theme.colors.neutral.bg.weak};
    border-color: ${({ theme }) => theme.colors.neutral.border.strong};
    color: ${({ theme }) => theme.colors.neutral.text.weak};
    font-weight: ${({ theme }) => theme.fonts.body.m.highContrast.fontWeight};
  }
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  color: ${({ theme }) => theme.colors.neutral.text.default};
  font-weight: ${({ theme }) => theme.fonts.body.m.highContrast.fontWeight};
  height: 56px;
  width: 43px;
  font-size: 24px;
  text-align: center;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, error }) => error ? theme.colors.danger.border.default : theme.colors.neutral.border.default};
  caret-color: ${({ error, theme }) => error ? theme.colors.danger.border.default : theme.colors.primary.border.default};
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  :focus {
    outline: none;
    border: ${({ error, theme }) => error ? `1px solid ${theme.colors.danger.border.default}` : `1.5px solid ${theme.colors.primary.border.default}`};
  }
`;

// src/CodeInput/CodeInput.tsx
import { jsx as jsx46, jsxs as jsxs28 } from "react/jsx-runtime";
var CodeInput = ({
  length = 4,
  autoFocus,
  disabled,
  error,
  errorMessage,
  label = "C\xF3digo de verificaci\xF3n",
  helperText,
  aditionalHelp,
  aditionalHelpButton,
  forceUpperCase,
  inputType = "alphanumerical",
  autoMoveFocus = true,
  mask,
  onChange,
  onComplete,
  defaultValue,
  pasteEnabled = true
}) => {
  const [valuesArray, setValuesArray] = useState19(
    Array.from({ length }, () => ({ value: void 0 }))
  );
  useEffect16(() => {
    if ((defaultValue == null ? void 0 : defaultValue.length) === length) {
      const tempValuesArray = Array.from({ length }, () => ({ value: void 0 }));
      for (let i = 0; i < length; i++) {
        tempValuesArray[i].value = defaultValue[i];
      }
      setValuesArray(tempValuesArray);
    } else if (defaultValue === "") {
      const tempValuesArray = Array.from({ length }, () => ({ value: void 0 }));
      setValuesArray(tempValuesArray);
    }
  }, [defaultValue]);
  useEffect16(() => {
    if (autoFocus) {
      const element = document.getElementById("code-input-0");
      element && element.focus();
    }
  }, [autoFocus]);
  const validateTypeAndSetValue = (value, index) => {
    let returnValue;
    const tempArray = [...valuesArray];
    switch (inputType) {
      case "text":
        if (/^[a-zA-Z]+$/.test(value))
          returnValue = forceUpperCase ? value.toLocaleUpperCase() : value;
        break;
      case "number":
        if (/^\d+$/.test(value))
          returnValue = value;
        break;
      case "alphanumerical":
        console.log("hola!", value, index);
        if (/^[a-zA-Z0-9]+$/.test(value))
          returnValue = forceUpperCase ? value.toLocaleUpperCase() : value;
        break;
    }
    tempArray[index].value = returnValue;
    setValuesArray(tempArray);
  };
  const handleOnPress = (e, index) => {
    if (e.key === "Backspace" && !valuesArray[index].value) {
      if (index > 0) {
        const element = document.getElementById(`code-input-${index - 1}`);
        element && element.focus();
      }
    } else if (e.key !== "Backspace" && valuesArray[index].value) {
      if (index < length - 1) {
        const element = document.getElementById(`code-input-${index + 1}`);
        element && element.focus();
      }
    }
  };
  useEffect16(() => {
    checkIfComplete();
    let concatenatedString = "";
    for (let i = 0; i < length; i++) {
      if (valuesArray[i].value !== void 0)
        concatenatedString += valuesArray[i].value;
    }
    onChange && onChange(concatenatedString);
  }, [valuesArray]);
  const checkIfComplete = () => {
    let isComplete = true;
    for (let i = 0; i < length; i++) {
      if (!valuesArray[i].value) {
        isComplete = false;
        break;
      }
    }
    if (isComplete && onComplete)
      onComplete();
  };
  const setCursorAtEndOfInput = (e) => {
    const input = e.target;
    const length2 = input.value.length;
    input.setSelectionRange(length2, length2);
  };
  const inputs = [];
  for (let i = 0; i < length; i++) {
    inputs.push(
      /* @__PURE__ */ jsx46(StyledInput, {
        "data-testid": "codeinput-styled-input",
        autoComplete: "off",
        error,
        disabled,
        onClick: setCursorAtEndOfInput,
        onChange: (e) => {
          validateTypeAndSetValue(e.target.value, i);
        },
        type: mask ? "password" : "text",
        maxLength: 1,
        value: valuesArray[i].value ? valuesArray[i].value : "",
        id: `code-input-${i}`,
        onKeyDown: autoMoveFocus ? (e) => handleOnPress(e, i) : void 0
      }, `styled-input-${i}`)
    );
  }
  useEffect16(() => {
    if (pasteEnabled) {
      window.addEventListener("paste", (e) => {
        var _a;
        const pasteData = (_a = e.clipboardData) == null ? void 0 : _a.getData("text");
        if ((pasteData == null ? void 0 : pasteData.length) === length) {
          let i = 0;
          for (i; i < length; i++) {
            validateTypeAndSetValue(pasteData[i], i);
          }
        }
      });
    }
  }, [pasteEnabled]);
  return /* @__PURE__ */ jsxs28(GlobalCodeInputContainer, {
    children: [
      /* @__PURE__ */ jsxs28(LabelContainer5, {
        children: [
          label && /* @__PURE__ */ jsx46(Text, {
            size: "s",
            color: "neutral",
            as: "span",
            children: label
          }),
          label && aditionalHelp && /* @__PURE__ */ jsxs28(Popover, {
            children: [
              /* @__PURE__ */ jsx46(PopoverTrigger, {
                children: /* @__PURE__ */ jsx46(Icon, {
                  name: "HelpIcon",
                  size: "S",
                  color: "neutralWeak"
                })
              }),
              /* @__PURE__ */ jsx46(PopoverContent, {
                subtitle: aditionalHelp,
                preferredPosition: "rightBottom",
                button: aditionalHelpButton
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsx46(CodeInputContainer, {
        "data-testid": "codeinput-container",
        length,
        children: inputs
      }),
      /* @__PURE__ */ jsx46(HelperContainer, {
        children: /* @__PURE__ */ jsxs28(Text, {
          variant: "caption",
          as: "span",
          color: error ? "danger" : "neutralWeak",
          "data-testid": "codeinput-helper-text",
          children: [
            error && errorMessage && /* @__PURE__ */ jsx46(Icon, {
              name: "WarningTriangleIcon",
              color: "danger",
              size: "S",
              className: "icon-invalid"
            }),
            " ",
            error ? errorMessage : helperText
          ]
        })
      })
    ]
  });
};
var CodeInput_default = CodeInput;

// src/AmountInput/AmountInput.tsx
import { useEffect as useEffect17, useState as useState20 } from "react";
import styled41, { css as css13 } from "styled-components";
import { fontSize as fontSize4, fontWeight as fontWeight4, letterSpacing as letterSpacing3, lineHeight as lineHeight4 } from "@orbita-ui/brand";
import { jsx as jsx47, jsxs as jsxs29 } from "react/jsx-runtime";
var InputWrapper4 = styled41.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};

  .icon-invalid {
    vertical-align: middle;
  }
`;
var Prefix = styled41.span`
  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  font-size: ${fontSize4["5xlarge"]};
  letter-spacing: ${letterSpacing3.small};
  line-height: ${lineHeight4["2xlarge"]};
  font-weight: ${fontWeight4.midContrast};
  color: ${({ theme }) => theme.colors.neutral.text.strong};
`;
var InputContainer3 = styled41.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border-bottom: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  padding: ${({ theme }) => theme.spacing.xsmall};

  ${({ readOnly, theme }) => readOnly ? css13`
          border-bottom: none;
          input {
            color: ${theme.colors.neutral.text.weak};
          }
          .prefix-color {
            color: ${theme.colors.neutral.text.weak};
          }
        ` : css13`
          &:focus-within {
            border-bottom: ${`${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
          }
        `}

  ${({ invalid, theme }) => invalid && css13`
      border-bottom: ${`${theme.borderStroke.small} solid ${theme.colors.danger.default}`} !important;
    `}

  ${({ disabled, theme }) => disabled && css13`
      border-bottom: ${`${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};

      input {
        color: ${theme.colors.neutral.text.weak};
      }

      .prefix-color {
        color: ${theme.colors.neutral.text.weak};
      }
    `}
`;
var WrapperPrefixContainer = styled41.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  max-width: 100%;
`;
var InputComponent3 = styled41.input`
  border: none;
  appearance: none;
  padding: 0;
  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  font-size: ${fontSize4["5xlarge"]};
  letter-spacing: ${letterSpacing3.small};
  line-height: ${lineHeight4["2xlarge"]};
  font-weight: ${fontWeight4.midContrast};
  color: ${({ theme }) => theme.colors.neutral.text.strong};
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  caret-color: ${({ theme }) => theme.colors.primary.border.default};
  max-width: 227px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral.text.default};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background: none;
  }
`;
var AmountInput = ({
  label,
  prefix = "$",
  helperText = void 0,
  errorText = void 0,
  placeholder = void 0,
  onChange,
  value,
  invalid = false,
  tabIndex,
  disabled = false,
  readOnly = false,
  fullWidth = false,
  ...htmlProps
}) => {
  const [myValue, setMyValue] = useState20("");
  useEffect17(() => {
    if (value && value > 0) {
      const formatComma = value.toString().replace(".", ",");
      formatCurrency(formatComma);
    }
  }, [value]);
  const formatCurrency = (inputValue) => {
    const numericValue = inputValue.replace(/[^0-9,]/g, "");
    const lastIsComma = inputValue[inputValue.length - 1] === ",";
    const [integerPart, decimalPart] = numericValue.split(",");
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const formattedDecimalPart = decimalPart ? `,${decimalPart.slice(0, 2)}` : lastIsComma ? "," : "";
    const formattedValue = formattedIntegerPart + formattedDecimalPart;
    setMyValue(formattedValue);
  };
  const handleChange = (event) => {
    formatCurrency(event.target.value);
  };
  useEffect17(() => {
    const replaceDot = myValue.replace(".", "");
    const finalValue = replaceDot.replace(",", ".");
    onChange(finalValue ? parseFloat(finalValue) : 0);
  }, [myValue]);
  return /* @__PURE__ */ jsxs29(InputWrapper4, {
    children: [
      /* @__PURE__ */ jsx47(Text, {
        variant: "caption",
        as: "label",
        color: "neutral",
        children: label
      }),
      /* @__PURE__ */ jsx47(InputContainer3, {
        invalid,
        tabIndex,
        disabled,
        readOnly,
        fullWidth,
        children: /* @__PURE__ */ jsxs29(WrapperPrefixContainer, {
          children: [
            /* @__PURE__ */ jsx47(Prefix, {
              className: "prefix-color",
              children: prefix
            }),
            /* @__PURE__ */ jsx47(InputComponent3, {
              placeholder,
              type: "text",
              size: myValue ? myValue.length : 1,
              onChange: handleChange,
              value: myValue,
              disabled,
              readOnly,
              ...htmlProps
            })
          ]
        })
      }),
      /* @__PURE__ */ jsxs29(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && errorText && /* @__PURE__ */ jsx47(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
AmountInput.displayName = "AmountInput";

// src/Breadcrumb/Breadcrumb.tsx
import styled42, { css as css14 } from "styled-components";
import { jsx as jsx48, jsxs as jsxs30 } from "react/jsx-runtime";
var BreadcrumbContainer = styled42.nav`
  padding: ${({ theme }) => `${theme.spacing.large} 0`};
  box-sizing: border-box;
  width: 100%;
`;
var BreadcrumbList = styled42.ol`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  align-items: center;
`;
var BreadcrumbItem = styled42.li`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
  align-items: center;
`;
var BreadcrumbLink = styled42.a`
  font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
  font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
  line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
  font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
  color: ${({ theme }) => theme.colors.neutral.text.weak};
  transition: all 0.8s;

  ${({ hasHref }) => hasHref ? css14`
          &:hover {
            text-decoration: underline;
          }
        ` : css14`
          font-family: ${({ theme }) => theme.fonts.body.s["highContrast"].fontFamily};
          font-size: ${({ theme }) => theme.fonts.body.s["highContrast"].fontSize};
          letter-spacing: ${({ theme }) => theme.fonts.body.s["highContrast"].letterSpacing};
          line-height: ${({ theme }) => theme.fonts.body.s["highContrast"].lineHeight};
          font-weight: ${({ theme }) => theme.fonts.body.s["highContrast"].fontWeight};
          color: ${({ theme }) => theme.colors.neutral.text.default};
        `}
`;
var Breadcrumb = ({ items }) => {
  if (items.length > 5) {
    throw new Error("No podes ingresar mas de 5 elementos");
  }
  return /* @__PURE__ */ jsx48(BreadcrumbContainer, {
    children: /* @__PURE__ */ jsx48(BreadcrumbList, {
      children: items && items.map((item, index) => /* @__PURE__ */ jsxs30(BreadcrumbItem, {
        children: [
          /* @__PURE__ */ jsx48(BreadcrumbLink, {
            href: index + 1 < items.length ? item.href : void 0,
            hasHref: index + 1 < items.length ? !!item.href : false,
            children: item.label
          }),
          index + 1 < items.length && /* @__PURE__ */ jsx48(Icon, {
            name: "ChevronRightIcon",
            color: "neutralWeak",
            size: "S"
          })
        ]
      }, index))
    })
  });
};
Breadcrumb.displayName = "Breadcrumb";

// src/Stepper/Stepper.tsx
import { hexToRgba as hexToRgba2 } from "@orbita-ui/brand";
import styled43, { css as css15 } from "styled-components";
import { useState as useState21 } from "react";

// src/Stepper/dictionary.ts
var dictBg = {
  complete: {
    color: "primary",
    variant: "weak",
    icon: "primary"
  },
  active: {
    color: "primary",
    variant: "default",
    icon: "neutralWeaker"
  },
  incomplete: {
    color: "neutral",
    variant: "default",
    icon: "neutralWeak"
  }
};

// src/Stepper/Stepper.tsx
import { Fragment as Fragment9, jsx as jsx49, jsxs as jsxs31 } from "react/jsx-runtime";
var StepperContainer = styled43.div`
  display: flex;
  flex-direction: ${({ vertical }) => vertical ? "row" : "column"};
  width: ${({ vertical }) => vertical ? "auto" : "100%"};
  height: ${({ vertical }) => vertical ? "100%" : "auto"};
  padding: ${({ vertical }) => vertical ? "0" : "24px 18px"};
`;
var StepsContainer = styled43.div`
  display: flex;
  flex-direction: ${({ vertical }) => vertical ? "column" : "row"};
  gap: ${({ theme }) => theme.spacing.small};
  justify-content: space-between;
  align-items: ${({ vertical }) => vertical ? "flex-start" : "center"};
  width: ${({ vertical }) => vertical ? "auto" : "100%"};
  height: ${({ vertical }) => vertical ? "100%" : "auto"};
`;
var StepItem = styled43.div`
  display: flex;
  flex-direction: ${({ vertical }) => vertical ? "column" : "row"};
  align-items: ${({ vertical }) => vertical ? "flex-start" : "center"};
  width: ${({ vertical }) => vertical ? "auto" : "100%"};
  gap: ${({ theme }) => theme.spacing.xsmall};
  box-sizing: border-box;

  &:last-child {
    width: auto;
    height: auto;
  }

  ${({ status }) => status === "complete" && css15`
      :hover {
        cursor: pointer;
        span {
          color: ${({ theme }) => theme.colors.primary.text.default};
        }
      }
    `}
`;
var IconContainer3 = styled43.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 8px;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius["4xlarge"]};
  box-sizing: border-box;
  border: ${({ theme, status }) => status === "complete" ? `${theme.borderStroke.small} solid  ${hexToRgba2(
  theme.colors.primary.border.default,
  theme.opacity[10]
)}` : "none"};
  background: ${({ theme, status }) => status === "incomplete" ? hexToRgba2(theme.colors.neutral.bg.default, theme.opacity[60]) : theme.colors[dictBg[status].color].bg[dictBg[status].variant]};
  transition: all 0.3s;
`;
var TextContainer2 = styled43.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: max-content;
  max-width: 100px;

  span {
    width: max-content;
    max-width: 124px;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm.width}px) {
    display: ${({ status }) => status !== "active" ? "none" : "flex"};
  }
`;
var LeftContainer = styled43.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
`;
var LineDivider = styled43.div`
  width: ${({ vertical }) => vertical ? "1px" : "100%"};
  margin-left: ${({ vertical }) => vertical ? "16px" : "0"};
  height: ${({ vertical }) => vertical ? "100%" : "1px"};
  background: ${({ theme, complete }) => complete ? theme.colors.primary.border.strong : theme.colors.neutral.border.default};
  transition: all 0.3s;
`;
var Stepper = ({
  steps,
  vertical,
  activeStep,
  showStep = true
}) => {
  const [countItems] = useState21(steps.length);
  const { variant } = useTheme();
  if (countItems > 5 || countItems < 2) {
    throw new Error("No podes ingresar mas de 5 elementos y menos de 2");
  }
  if (activeStep < 0 || activeStep > countItems) {
    throw new Error("El elemnto seleccionando no se encuentra en el indice");
  }
  const getStatus = (index) => activeStep === index ? "active" : activeStep > index ? "complete" : "incomplete";
  return /* @__PURE__ */ jsx49(StepperContainer, {
    vertical,
    children: /* @__PURE__ */ jsx49(StepsContainer, {
      vertical,
      children: steps.map((step, index) => /* @__PURE__ */ jsxs31(Fragment9, {
        children: [
          /* @__PURE__ */ jsx49(StepItem, {
            "data-testid": "step" + index,
            vertical,
            status: getStatus(index),
            onClick: (e) => getStatus(index) === "complete" && step.onDoneClick && step.onDoneClick(e),
            children: /* @__PURE__ */ jsxs31(LeftContainer, {
              children: [
                /* @__PURE__ */ jsx49(IconContainer3, {
                  status: getStatus(index),
                  children: /* @__PURE__ */ jsx49(Icon, {
                    name: getStatus(index) === "complete" ? "CheckIcon" : step.icon,
                    size: "S",
                    color: dictBg[getStatus(index)].icon === "neutralWeaker" && variant === "dark" ? "neutralStrong" : dictBg[getStatus(index)].icon
                  })
                }),
                /* @__PURE__ */ jsxs31(TextContainer2, {
                  status: getStatus(index),
                  children: [
                    showStep && /* @__PURE__ */ jsx49(Text, {
                      variant: "overline",
                      uppercase: true,
                      color: "neutralWeak",
                      as: "span",
                      children: `paso ${index + 1}`
                    }),
                    /* @__PURE__ */ jsx49(Text, {
                      variant: "caption",
                      color: "neutralStrong",
                      as: "span",
                      children: step.label
                    })
                  ]
                })
              ]
            })
          }, "step" + index),
          index < countItems - 1 && /* @__PURE__ */ jsx49(LineDivider, {
            complete: getStatus(index) === "complete",
            vertical
          }, "divider" + index)
        ]
      }))
    })
  });
};
Stepper.displayName = "Stepper";

// src/Stepper/useStep.tsx
import { useState as useState22 } from "react";
function useSteps(props) {
  const { index, count } = props;
  const [activeStep, setActiveStep] = useState22(index);
  const maxStep = count - 1;
  const activeStepPercent = activeStep / maxStep;
  return {
    activeStep,
    setActiveStep,
    activeStepPercent,
    isActiveStep(step) {
      return step === activeStep;
    },
    isCompleteStep(step) {
      return step < activeStep;
    },
    isIncompleteStep(step) {
      return step > activeStep;
    },
    getStatus(step) {
      if (step < activeStep)
        return "complete";
      if (step > activeStep)
        return "incomplete";
      return "active";
    },
    goToNext() {
      setActiveStep((step) => {
        return Math.min(count, step + 1);
      });
    },
    goToPrevious() {
      setActiveStep((step) => Math.max(0, step - 1));
    }
  };
}

// src/SearchInput/SearchInput.tsx
import { useRef as useRef6, useState as useState23 } from "react";
import styled44, { css as css16 } from "styled-components";
import { jsx as jsx50, jsxs as jsxs32 } from "react/jsx-runtime";
var InputWrapper5 = styled44.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  .icon-invalid {
    vertical-align: middle;
  }
`;
var InputContainer4 = styled44.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  height: 40px;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  gap: ${({ theme }) => theme.spacing.small};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.neutral.bg.weaker};
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius["3xlarge"]};
  padding: 7px 11px;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral.bg.weak};
    input {
      background: ${({ theme }) => theme.colors.neutral.bg.weak};
    }
  }

  &:focus-within {
    border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.primary.border.default}`};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme, invalid }) => invalid ? theme.focusStroke.error : theme.focusStroke.default};
  }

  ${({ invalid, theme }) => invalid && css16`
      border: ${`${theme.borderStroke.small} solid ${theme.colors.danger.border.default}`} !important;
    `}

  .clear-icon {
    cursor: pointer;
  }
`;
var InputComponent4 = styled44.input`
  width: 100%;
  border: none;
  appearance: none;
  max-height: 24px;
  font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
  font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
  line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
  font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
  padding: 0;
  color: ${({ theme }) => theme.colors.neutral.text.strong};
  caret-color: ${({ theme }) => theme.colors.primary.border.default};
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};
  box-sizing: border-box;

  ::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral.text.weak};
  }

  &:focus {
    outline: none;
  }
`;
var ContainerInput = styled44.div`
  width: 100%;
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`;
var SearchInput2 = ({
  label,
  helperText,
  errorText = void 0,
  placeholder = void 0,
  onChange,
  invalid = false,
  tabIndex,
  fullWidth = false,
  value,
  ...htmlProps
}) => {
  const inputRef = useRef6(null);
  const [haveContent, setHaveContent] = useState23(value && value.length > 0 ? true : false);
  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      setHaveContent(false);
      onChange && onChange({ target: { value: "" } });
    }
  };
  const onChangeText = (event) => {
    setHaveContent(event.target.value !== "");
  };
  return /* @__PURE__ */ jsxs32(InputWrapper5, {
    children: [
      /* @__PURE__ */ jsx50(Text, {
        variant: "caption",
        as: "span",
        color: "neutral",
        children: label
      }),
      /* @__PURE__ */ jsxs32(InputContainer4, {
        invalid,
        tabIndex,
        fullWidth,
        children: [
          /* @__PURE__ */ jsxs32(ContainerInput, {
            children: [
              /* @__PURE__ */ jsx50(Icon, {
                name: "SearchIcon",
                color: "neutralWeak"
              }),
              /* @__PURE__ */ jsx50(InputComponent4, {
                ref: inputRef,
                placeholder,
                type: "search",
                onChange,
                onKeyUp: onChangeText,
                value,
                ...htmlProps
              })
            ]
          }),
          haveContent && /* @__PURE__ */ jsx50(Icon, {
            name: "CrossIcon",
            color: "neutralWeak",
            onClick: clearInput,
            "data-testid": "clear-icon",
            className: "clear-icon"
          })
        ]
      }),
      /* @__PURE__ */ jsxs32(Text, {
        variant: "caption",
        as: "span",
        color: invalid ? "danger" : "neutralWeak",
        children: [
          invalid && errorText && /* @__PURE__ */ jsx50(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S",
            className: "icon-invalid"
          }),
          " ",
          invalid ? errorText : helperText
        ]
      })
    ]
  });
};
SearchInput2.displayName = "SearchInput";

// src/Datepicker/constants.ts
import { es } from "date-fns/locale/index.js";
var formatCaption = (month, options) => {
  const returnString = month.toLocaleString("es", { month: "long" }).charAt(0).toUpperCase() + month.toLocaleString("es", { month: "long" }).slice(1) + " " + month.toLocaleString("es", { year: "numeric" });
  return returnString;
};
var constantProps = {
  locale: es,
  showOutsideDays: true,
  formatters: { formatCaption }
};

// src/Datepicker/styles.tsx
import styled45 from "styled-components";
import { DayPicker } from "react-day-picker";
var StyledDayPicker = styled45(DayPicker)`
  position: absolute;
  transform: translate(0px, -11px);
  z-index: ${({ theme }) => theme.zindex[20]};
  display: ${({ isActive }) => isActive ? "flex" : "none"};
  margin-left: 0px;
  width: 312px;
  margin-right: 0px !important;
  font-family: 'Nunito Sans', -apple-system, '.SFNSText-Regular', 'San Francisco',
    BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  background-color: ${({ theme }) => theme.colors.neutral.bg.weaker};

  .rdp-day,
  .rdp-nav_button {
    transition: all 0.5s;

    :hover {
      background-color: ${({ theme }) => theme.colors.primary.bg.weak} !important;
    }
  }

  .rdp-day_selected {
    background-color: ${({ theme }) => theme.colors.primary.bg.default} !important;
    transition: all 0.5s;

    :hover {
      background-color: ${({ theme }) => theme.colors.primary.bg.default};
    }
  }
  .rdp-button,
  .rdp-day {
    transition: all 0.5s;

    :hover {
      background-color: ${({ theme }) => theme.colors.primary.bg.weak} !important;
    }
  }
  .rdp-day_range_start,
  .rdp-rday_range_end {
    transition: all 0.5s;

    background-color: ${({ theme }) => theme.colors.primary.bg.default};
    transition: all 0.5s;

    &:hover {
      background-color: ${({ theme }) => theme.colors.primary.bg.default} !important;
    }
  }
  .rdp-day_range_middle {
    background-color: ${({ theme }) => theme.colors.primary.bg.weak} !important;
    color: ${({ theme }) => theme.colors.neutral.text.strong} !important;
  }

  .rdp-caption_label {
    font-weight: normal;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.neutral.text.strong} !important;
  }
  .rdp-months {
    box-shadow: ${({ theme }) => theme.elevation["30"]};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    padding: ${({ theme }) => theme.spacing.medium};
  }
  .rdp-head_cell {
    color: ${({ theme }) => theme.colors.neutral.text.weak};
    font-weight: normal;
  }

  .rdp-cell {
    color: ${({ theme }) => theme.colors.neutral.text.strong};
    font-weight: 400;
    font-size: 12px;
  }

  .rdp-nav_icon {
    color: ${({ theme }) => theme.colors.neutral.ic.weak};
  }
`;
var GlobalDatepickerContainer = styled45.div`
  transition: all 0.5s;

  .rdp {
    --rdp-cell-size: 40px;
    --rdp-caption-font-size: 18px;
    --rdp-accent-color: #0000ff;
    --rdp-background-color: #e7edff;
    --rdp-accent-color-dark: #3003e1;
    --rdp-background-color-dark: #180270;
    --rdp-outline: 2px solid var(--rdp-accent-color); /* Outline border for focused elements */
    --rdp-outline-selected: 3px solid var(--rdp-accent-color); /* Outline border for focused _and_ selected elements */
    margin-top: 1em;
  }

  /* Hide elements for devices that are not screen readers */
  .rdp-vhidden {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    position: absolute !important;
    top: 0;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    border: 0 !important;
  }

  /* Buttons */
  .rdp-button_reset {
    appearance: none;
    position: relative;
    margin: 0;
    padding: 0;
    cursor: default;
    color: inherit;
    background: none;
    font: inherit;

    -moz-appearance: none;
    -webkit-appearance: none;
  }

  .rdp-button_reset:focus-visible {
    /* Make sure to reset outline only when :focus-visible is supported */
    outline: none;
  }

  .rdp-button {
    border: 2px solid transparent;
  }

  .rdp-button[disabled]:not(.rdp-day_selected) {
    opacity: 0.25;
  }

  .rdp-button:not([disabled]) {
    cursor: pointer;
  }

  .rdp-button:focus-visible:not([disabled]) {
    color: inherit;
    background-color: var(--rdp-background-color);
    border: var(--rdp-outline);
  }

  .rdp-months {
    display: flex;
  }

  .rdp-month {
    margin: 0 1em;
  }

  .rdp-month:first-child {
    margin-left: 0;
  }

  .rdp-month:last-child {
    margin-right: 0;
  }

  .rdp-table {
    margin: 0;
    max-width: calc(var(--rdp-cell-size) * 7);
    border-collapse: collapse;
  }

  .rdp-with_weeknumber .rdp-table {
    max-width: calc(var(--rdp-cell-size) * 8);
    border-collapse: collapse;
  }

  .rdp-caption {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    text-align: left;
  }

  .rdp-multiple_months .rdp-caption {
    position: relative;
    display: block;
    text-align: center;
  }

  .rdp-caption_dropdowns {
    position: relative;
    display: inline-flex;
  }

  .rdp-caption_label {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    margin: 0;
    padding: 0 0.25em;
    white-space: nowrap;
    color: currentColor;
    border: 0;
    border: 2px solid transparent;
    font-family: inherit;
  }

  .rdp-nav {
    white-space: nowrap;
  }

  .rdp-multiple_months .rdp-caption_start .rdp-nav {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }

  .rdp-multiple_months .rdp-caption_end .rdp-nav {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }

  .rdp-nav_button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--rdp-cell-size);
    height: var(--rdp-cell-size);
    padding: 0.25em;
    border-radius: 100%;
  }

  /* ---------- */
  /* Dropdowns  */
  /* ---------- */

  .rdp-dropdown_year,
  .rdp-dropdown_month {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .rdp-dropdown {
    appearance: none;
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    cursor: inherit;
    opacity: 0;
    border: none;
    background-color: transparent;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  .rdp-dropdown[disabled] {
    opacity: unset;
    color: unset;
  }

  .rdp-dropdown:focus-visible:not([disabled]) + .rdp-caption_label {
    background-color: var(--rdp-background-color);
    border: var(--rdp-outline);
    border-radius: 6px;
  }

  .rdp-dropdown_icon {
    margin: 0 0 0 5px;
  }

  .rdp-head {
    border: 0;
  }

  .rdp-head_row,
  .rdp-row {
    height: 100%;
  }

  .rdp-head_cell {
    vertical-align: middle;
    font-size: 0.75em;
    text-align: center;
    height: 100%;
    height: var(--rdp-cell-size);
    padding: 0;
    text-transform: uppercase;
  }

  .rdp-tbody {
    border: 0;
  }

  .rdp-tfoot {
    margin: 0.5em;
  }

  .rdp-cell {
    width: var(--rdp-cell-size);
    height: 100%;
    height: var(--rdp-cell-size);
    padding: 0;
    text-align: center;
  }

  .rdp-weeknumber {
    font-size: 0.75em;
  }

  .rdp-weeknumber,
  .rdp-day {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: var(--rdp-cell-size);
    max-width: var(--rdp-cell-size);
    height: var(--rdp-cell-size);
    margin: 0;
    border: 2px solid transparent;
    border-radius: 100%;
  }

  .rdp-day_today:not(.rdp-day_outside) {
    font-weight: bold;
  }

  .rdp-day_selected,
  .rdp-day_selected:focus-visible,
  .rdp-day_selected:hover {
    color: white;
    opacity: 1;
  }

  .rdp-day_outside {
    opacity: 0.5;
  }

  .rdp-day_selected:focus-visible {
    /* Since the background is the same use again the outline */
    outline: var(--rdp-outline);
    outline-offset: 2px;
    z-index: 1;
  }

  .rdp:not([dir='rtl']) .rdp-day_range_start:not(.rdp-day_range_end) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .rdp:not([dir='rtl']) .rdp-day_range_end:not(.rdp-day_range_start) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .rdp[dir='rtl'] .rdp-day_range_start:not(.rdp-day_range_end) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .rdp[dir='rtl'] .rdp-day_range_end:not(.rdp-day_range_start) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .rdp-day_range_end.rdp-day_range_start {
    border-radius: 100%;
  }

  .rdp-day_range_middle {
    border-radius: 0;
  }
`;
var GlobalContainer = styled45.div``;

// src/Datepicker/Datepicker.tsx
import { useEffect as useEffect20, useRef as useRef7, useState as useState25 } from "react";

// src/Datepicker/useShortDays.tsx
import { useEffect as useEffect18 } from "react";
var useShortDays = () => {
  useEffect18(() => {
    changeArrowDisplayOrder();
    for (let i = 0; i < ariaLabels.length; i++) {
      const element = document && document.querySelector(`[aria-label="${ariaLabels[i]}"]`);
      if (element)
        element.textContent = newValues[i];
    }
  }, []);
};
var ariaLabels = ["lunes", "martes", "mi\xE9rcoles", "jueves", "viernes", "s\xE1bado", "domingo"];
var newValues = ["L", "M", "X", "J", "V", "S", "D"];
var changeArrowDisplayOrder = () => {
  const previousArrow = document && document.querySelector('[aria-label="Go to previous month"]');
  let parentContainer = null;
  if (previousArrow)
    parentContainer = previousArrow.parentNode;
  let superParent = null;
  if (parentContainer)
    superParent = parentContainer.parentNode;
  const rdpCaption = document.getElementsByClassName("rdp-caption")[0];
  if (superParent && superParent.isSameNode(rdpCaption))
    superParent.insertBefore(previousArrow, superParent.firstChild);
};

// src/Datepicker/DatepickerDialog/DatepickerDialog.tsx
import { useEffect as useEffect19, useState as useState24 } from "react";

// src/Datepicker/DatepickerDialog/styles.tsx
import styled46 from "styled-components";
import InputMask from "react-input-mask";
var GlobalDialogContainer = styled46.div`
  display: flex;
  flex-direction: column;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
`;
var DialogContainer = styled46.div`
  width: 312;
  height: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  gap: 12px;
  transition: all 0.8s;

  background: ${({ disabled, theme }) => disabled ? theme.colors.neutral.bg.weak : theme.colors.neutral.bg.weaker};
  /* semantic colors/neutral/background/bg-weak */
  border: ${({ error, isActive, theme, disabled }) => !disabled ? !error ? isActive ? `1px solid ${theme.colors.primary.border.strong}` : `1px solid ${theme.colors.neutral.border.default}` : `1px solid ${theme.colors.danger.border.default}` : "0px"};
  border-radius: 48px;
  cursor: ${({ disabled }) => disabled ? "default" : "pointer"};
  &:hover {
    background: ${({ disabled, theme }) => theme.colors.neutral.bg.weak};
  }

  &:focus-visible {
    box-shadow: ${({ theme, error }) => error ? `0px 0px 0px 4px #FFE1E4` : `0px 0px 0px 4px ${theme.colors.blue[2]}`};
    outline: none;
  }
`;
var PlaceholderContainer = styled46.div`
  width: max-content;
`;
var LabelContainer6 = styled46.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${({ theme }) => theme.spacing["2xsmall"]};
  label {
    margin-right: ${({ theme }) => theme.spacing["2xsmall"]};
  }
`;
var IconContainer4 = styled46.div`
  margin-left: auto;
`;
var HelperContainer2 = styled46.div`
  display: flex;
  flex-direction: row;
  margin-top: ${({ theme }) => theme.spacing["2xsmall"]};
  label {
    margin-left: ${({ theme, error }) => !error ? "0px" : theme.spacing["2xsmall"]};
    margin-right: ${({ theme }) => theme.spacing["2xsmall"]};
  }
`;
var DatepickerInput = styled46(InputMask)`
  width: 100%;
  border: none !important;
  outline: none !important;
  appearance: none;
  /* max-height: 40px; */
  /* height: 40px; */
  font-family: ${({ theme }) => theme.fonts.body.s["midContrast"].fontFamily};
  font-size: ${({ theme }) => theme.fonts.body.s["midContrast"].fontSize};
  letter-spacing: ${({ theme }) => theme.fonts.body.s["midContrast"].letterSpacing};
  line-height: ${({ theme }) => theme.fonts.body.s["midContrast"].lineHeight};
  font-weight: ${({ theme }) => theme.fonts.body.s["midContrast"].fontWeight};
  padding: 0;
  color: ${({ theme }) => theme.colors.neutral.text.strong};
  caret-color: ${({ theme }) => theme.colors.primary.border.default};
  transition: all 0.8s;

  background: ${({ disabled, dialogHovered, theme }) => disabled ? theme.colors.neutral.bg.weak : dialogHovered ? theme.colors.neutral.bg.weak : theme.colors.neutral.bg.weaker};
  &:hover {
    background: ${({ disabled, theme }) => theme.colors.neutral.bg.weak};
  }
  &:focus-visible {
    border: none;
  }
`;

// src/Datepicker/DatepickerDialog/DatepickerDialog.tsx
import { jsx as jsx51, jsxs as jsxs33 } from "react/jsx-runtime";
var DatepickerDialog = ({
  isActive,
  toggleIsActive,
  error,
  label,
  additionalHelp,
  helperText,
  placeholder,
  errorMessage,
  disabled,
  selected,
  selectedRange,
  mode,
  fullWidth,
  editable,
  onCompleteTextInput,
  onCompleteTextRangeInput,
  customRangeErrorMessage = "Rango inv\xE1lido",
  onBlur
}) => {
  const [isDialogHovered, setIsDialogHovered] = useState24(false);
  const [textInputValue, setTextInputValue] = useState24("");
  const [rangeError, setRangeError] = useState24(false);
  const [onblurFlag, setonblurFlag] = useState24(false);
  useEffect19(() => {
    if (!onblurFlag) {
      setonblurFlag(true);
    } else {
      if (!isActive) {
        onBlur && onBlur();
      }
    }
  }, [isActive]);
  const formattedDate = () => {
    let returnString = "";
    if (mode === "single" && selected) {
      returnString = selected && new Date(selected).toLocaleDateString("es", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } else {
      let fromString = "", toString = "";
      if (selectedRange && selectedRange.from) {
        fromString = `${selectedRange && new Date(selectedRange.from).toLocaleDateString("es", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        })}`;
      }
      if (selectedRange && selectedRange.to) {
        toString = ` ${selectedRange && new Date(selectedRange.to).toLocaleDateString("es", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        })}`;
      }
      returnString = `${fromString} | ${toString}`;
    }
    return returnString;
  };
  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    setTextInputValue(rawValue);
    if (rawValue.length === 10) {
      const [day, month, year] = rawValue.split("/");
      const writtenDate = new Date(year, month - 1, day);
      onCompleteTextInput && onCompleteTextInput(writtenDate);
    }
  };
  const handleRangedInputChange = (e) => {
    const rawValue = e.target.value;
    setTextInputValue(rawValue);
    if (rawValue.length === 23) {
      const splitValue = rawValue.split("/");
      const [fromDay, fromMonth, complexValue, toMonth, toYear] = splitValue;
      const [fromYear, toDay] = complexValue.split("|");
      const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
      const toDate = new Date(toYear, toMonth - 1, toDay);
      if (fromDate < toDate) {
        setRangeError(false);
        onCompleteTextRangeInput && onCompleteTextRangeInput({ from: fromDate, to: toDate });
      } else {
        setRangeError(true);
      }
    }
  };
  useEffect19(() => {
    if (selectedRange && selectedRange.from && selectedRange.to && selectedRange.from < selectedRange.to) {
      setRangeError(false);
    }
    setTextInputValue(formattedDate());
  }, [selectedRange]);
  useEffect19(() => {
    setTextInputValue(formattedDate());
  }, [selected]);
  return /* @__PURE__ */ jsxs33(GlobalDialogContainer, {
    fullWidth,
    children: [
      /* @__PURE__ */ jsxs33(LabelContainer6, {
        children: [
          /* @__PURE__ */ jsx51(Text, {
            variant: "caption",
            as: "label",
            color: "neutralWeak",
            children: label
          }),
          additionalHelp && /* @__PURE__ */ jsxs33(Popover, {
            "data-testid": "label-popover",
            children: [
              /* @__PURE__ */ jsx51(PopoverTrigger, {
                children: /* @__PURE__ */ jsx51(Icon, {
                  size: "S",
                  name: "HelpIcon",
                  color: "neutralWeak"
                })
              }),
              /* @__PURE__ */ jsx51(PopoverContent, {
                subtitle: additionalHelp,
                preferredPosition: "rightCenter"
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs33(DialogContainer, {
        isActive,
        onClick: disabled ? void 0 : toggleIsActive,
        error: error || rangeError,
        disabled,
        "data-testid": "dialog-container",
        onMouseEnter: () => setIsDialogHovered(true),
        onMouseLeave: () => setIsDialogHovered(false),
        children: [
          !editable ? /* @__PURE__ */ jsx51(PlaceholderContainer, {
            children: /* @__PURE__ */ jsx51(Text, {
              variant: "body",
              color: selected || selectedRange ? "neutralStrong" : "neutralWeak",
              "data-testid": "placeholder-text",
              size: "s",
              children: selected || selectedRange ? formattedDate() : placeholder
            })
          }) : mode === "single" ? /* @__PURE__ */ jsx51(DatepickerInput, {
            "data-testid": "datepicker-text-input",
            dialogHovered: isDialogHovered,
            type: "text",
            onChange: handleInputChange,
            maskChar: null,
            value: textInputValue,
            mask: "99/99/9999",
            placeholder: placeholder || "DD/MM/AAAA",
            disabled
          }) : /* @__PURE__ */ jsx51(DatepickerInput, {
            "data-testid": "datepicker-text-input",
            dialogHovered: isDialogHovered,
            value: textInputValue,
            type: "text",
            onChange: handleRangedInputChange,
            maskChar: null,
            mask: "99/99/9999 | 99/99/9999",
            placeholder: placeholder || "DD/MM/AAAA | DD/MM/AAAA",
            disabled
          }),
          /* @__PURE__ */ jsx51(IconContainer4, {
            children: /* @__PURE__ */ jsx51(Icon, {
              name: "CalendarIcon",
              color: disabled ? "neutralWeak" : "neutralStrong",
              "data-testid": "calendar-icon"
            })
          })
        ]
      }),
      !isActive && (helperText || error || rangeError) && /* @__PURE__ */ jsxs33(HelperContainer2, {
        error: error || rangeError,
        "data-testid": "helper-cotainer",
        children: [
          (error || rangeError) && /* @__PURE__ */ jsx51(Icon, {
            name: "WarningTriangleIcon",
            size: "S",
            color: "danger",
            "data-testid": "error-icon"
          }),
          /* @__PURE__ */ jsx51(Text, {
            variant: "caption",
            as: "label",
            color: error || rangeError ? "danger" : "neutralWeak",
            "data-testid": "helper-text",
            children: error ? errorMessage : rangeError ? customRangeErrorMessage : helperText
          })
        ]
      })
    ]
  });
};
var DatepickerDialog_default = DatepickerDialog;

// src/Datepicker/Datepicker.tsx
import { jsx as jsx52, jsxs as jsxs34 } from "react/jsx-runtime";
var Datepicker = ({
  label,
  value,
  isCalendarActive = false,
  additionalHelp,
  placeholder,
  error,
  errorMessage = "Ha ocurrido un error",
  helperText,
  mode,
  disabled,
  disabledDates,
  fullWidthDialog,
  editable,
  customRangeErrorMessage = "Rango inv\xE1lido",
  onChange,
  onBlur
}) => {
  const [isActive, setIsActive] = useState25(isCalendarActive);
  const [selected, setSelected] = useState25();
  const [selectedRange, setSelectedRange] = useState25();
  const datepickerRef = useRef7(null);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState25("");
  useEffect20(() => {
    if (value) {
      mode === "single" ? setSelected(value) : setSelectedRange(value);
    }
  }, []);
  useEffect20(() => {
    setDefaultPlaceholder(
      !editable ? mode === "single" ? "Seleccionar fecha" : "Desde | Hasta" : mode === "single" ? "DD/MM/AAAA" : "DD/MM/AAAA | DD/MM/AAAA"
    );
  }, []);
  useOutsideAlerter(datepickerRef, datepickerRef, isActive, setIsActive);
  useShortDays();
  const setSelectedValue = (selected2) => {
    onChange && onChange(selected2);
    setSelected(selected2);
  };
  const setSelectedRangeValue = (selected2) => {
    onChange && onChange(selected2);
    setSelectedRange(selected2);
  };
  return /* @__PURE__ */ jsxs34(GlobalContainer, {
    ref: datepickerRef,
    "data-testid": "global-container",
    children: [
      /* @__PURE__ */ jsx52(DatepickerDialog_default, {
        placeholder: placeholder ? placeholder : defaultPlaceholder,
        mode,
        label,
        helperText,
        errorMessage,
        additionalHelp,
        toggleIsActive: () => setIsActive((value2) => !value2),
        isActive,
        error,
        disabled,
        selected,
        selectedRange,
        fullWidth: fullWidthDialog,
        editable,
        onCompleteTextInput: (date) => setSelectedValue(date),
        onCompleteTextRangeInput: (rangeOfDates) => setSelectedRangeValue(rangeOfDates),
        customRangeErrorMessage,
        onBlur
      }),
      /* @__PURE__ */ jsx52(GlobalDatepickerContainer, {
        children: /* @__PURE__ */ jsx52(StyledDayPicker, {
          isActive,
          mode,
          month: mode === "single" && selected && editable ? new Date(selected.getFullYear(), selected.getMonth()) : mode === "range" && selectedRange && editable ? new Date(selectedRange.from.getFullYear(), selectedRange.from.getMonth()) : void 0,
          selected: mode === "single" ? selected : selectedRange,
          onSelect: mode === "single" ? setSelectedValue : setSelectedRangeValue,
          disabled: disabledDates,
          "data-testid": "styled-day-picker",
          ...constantProps
        })
      })
    ]
  });
};
var Datepicker_default = Datepicker;

// src/NewFeaturePopup/NewFeaturePopup.tsx
import { useEffect as useEffect21, useState as useState27 } from "react";

// src/NewFeaturePopup/PopupPortal.tsx
import { useState as useState26, useLayoutEffect as useLayoutEffect3 } from "react";
import { createPortal as createPortal3 } from "react-dom";
var PopupPortal = ({ children, wrapperId }) => {
  const [portalElement, setPortalElement] = useState26(null);
  useLayoutEffect3(() => {
    let element = document.getElementById(wrapperId);
    let portalCreated = false;
    if (!element) {
      element = createWrapperAndAppendToBody(wrapperId);
      portalCreated = true;
    }
    setPortalElement(element);
    return () => {
      if (portalCreated && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
  }, [wrapperId]);
  const createWrapperAndAppendToBody = (elementId) => {
    const element = document.createElement("div");
    element.setAttribute("id", elementId);
    element.style.position = "fixed";
    element.style.bottom = "0";
    element.style.right = "0";
    element.style.zIndex = "4";
    document.body.appendChild(element);
    return element;
  };
  if (!portalElement)
    return null;
  return createPortal3(children, portalElement);
};
var PopupPortal_default = PopupPortal;

// src/NewFeaturePopup/styles.tsx
import styled47 from "styled-components";
var PopupContainer = styled47.div`
  display: flex;
  position: fixed;
  min-width: calc(100% - 16px);
  box-sizing: border-box;
  top: auto;
  box-shadow: ${({ theme }) => theme.elevation["40"]};
  position: relative;
  opacity: ${({ isShown }) => isShown ? 1 : 0};
  z-index: ${({ theme }) => theme.zindex[70]};
  right: ${({ theme }) => theme.spacing.large};
  bottom: 24px;
  align-items: center;
  max-width: 328px;
  min-width: 328px;
  flex-wrap: wrap;
  background-color: ${({ theme }) => theme.colors.neutral.bg.stronger};
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ease-in-out 0.3s;
`;
var CenterContentContainerNoMargin = styled47.div`
  flex-basis: min-content;
  flex-grow: 1;
`;
var CrossIconContainerNoMargin = styled47.div`
  cursor: pointer;
  flex-basis: fit-content;
  align-self: flex-start;
`;
var TitleContainer2 = styled47.div`
  margin-bottom: ${({ theme }) => theme.spacing["2xsmall"]};
`;

// src/NewFeaturePopup/NewFeaturePopup.tsx
import { jsx as jsx53, jsxs as jsxs35 } from "react/jsx-runtime";
var NewFeaturePopup = ({
  isShown,
  subtitle,
  title,
  button,
  dismissIconOnClick
}) => {
  const [isShownState, setIsShownState] = useState27(false);
  useEffect21(() => {
    setIsShownState(Boolean(isShown));
  }, [isShown]);
  return /* @__PURE__ */ jsx53(PopupPortal_default, {
    wrapperId: "new-feature-popup-portal",
    children: /* @__PURE__ */ jsxs35(PopupContainer, {
      isShown: isShownState,
      "data-testid": "newfeature-container",
      children: [
        /* @__PURE__ */ jsxs35(CenterContentContainerNoMargin, {
          children: [
            /* @__PURE__ */ jsx53(TitleContainer2, {
              children: /* @__PURE__ */ jsx53(Text, {
                bold: true,
                size: "s",
                color: "neutralWeaker",
                children: title
              })
            }),
            /* @__PURE__ */ jsx53(Text, {
              size: "s",
              color: "neutralWeaker",
              children: subtitle
            }),
            /* @__PURE__ */ jsx53(BottomButtonContainer, {
              "data-testid": "newfeature-bottomaction-container",
              children: /* @__PURE__ */ jsx53(Button, {
                size: "small",
                color: "inverted",
                variant: "outline",
                onClick: button.onClick,
                "data-testid": "newfeature-button",
                children: button.label
              })
            })
          ]
        }),
        /* @__PURE__ */ jsx53(CrossIconContainerNoMargin, {
          buttonPosition: "bottomAction",
          children: /* @__PURE__ */ jsx53(Icon, {
            onClick: () => {
              setIsShownState(false);
              dismissIconOnClick && dismissIconOnClick();
            },
            name: "CrossIcon",
            color: "neutralWeaker",
            size: "M",
            "data-testid": "newfeature-cross-icon"
          })
        })
      ]
    })
  });
};
var NewFeaturePopup_default = NewFeaturePopup;

// src/Table/Table.tsx
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useEffect as useEffect22, useState as useState28 } from "react";

// src/Table/styles.ts
import { hexToRgba as hexToRgba3, opacity } from "@orbita-ui/brand";
import styled48, { css as css17 } from "styled-components";
var TableContainer = styled48.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding-top: ${({ theme }) => theme.spacing.xlarge};
  gap: ${({ theme }) => theme.spacing.xsmall};
  box-sizing: border-box;
`;
var TableBorder = styled48.div`
  border: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  box-shadow: ${({ theme }) => theme.elevation["10"]};
  width: 100%;
  overflow-x: auto;
  table {
    width: 100%; /* Table takes full width of the container */
    border-collapse: collapse;
  }
  /* Estilos para navegadores basados en WebKit */
  &::-webkit-scrollbar {
    height: 8px; /* Ancho del scrollbar */
    background-color: ${({ theme }) => theme.colors.neutral.border.weak};
    border-radius: 80px; /* Bordes redondeados */
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.neutral.border.default}; /* Color del thumb (barra) */
    border-radius: 80px; /* Bordes redondeados */
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.neutral.border.strong}; /* Color del thumb cuando está en hover */
  }
`;
var TableHead = styled48.thead`
  tr {
    width: 100%;
    height: ${({ theme }) => theme.spacing["3xlarge"]};
    border-bottom: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};
    background: ${({ theme }) => hexToRgba3(theme.colors.neutral.bg.strong, opacity[30])};
  }

  th {
    padding: 16px 8px 12px 8px;
    vertical-align: middle;
    box-sizing: border-box;

    div {
      display: flex;
      justify-content: space-between;
      width: fit-content;
    }
    .cursor-pointer {
      cursor: pointer;
      user-select: none;
      width: 100%;
    }
    .cursor-pointer-right {
      flex-direction: row-reverse;
    }
  }
`;
var TableBody = styled48.tbody`
  tr {
    width: 100%;
    height: ${({ theme }) => theme.spacing["3xlarge"]};
    border-bottom: ${({ theme }) => `${theme.borderStroke.small} solid ${theme.colors.neutral.border.weak}`};

    ${({ wihtHover }) => wihtHover && css17`
        &:hover {
          background: ${({ theme }) => hexToRgba3(theme.colors.neutral.bg.weak, opacity[80])};
          cursor: pointer;
        }
      `}
  }

  .row-selected {
    background: ${({ theme }) => theme.colors.primary.bg.weak} !important;
  }

  td {
    font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
    font-size: ${({ theme }) => theme.fonts.body.s.midContrast.fontSize};
    letter-spacing: ${({ theme }) => theme.fonts.body.s.midContrast.letterSpacing};
    line-height: ${({ theme }) => theme.fonts.body.s.midContrast.lineHeight};
    font-weight: ${({ theme }) => theme.fonts.body.s.midContrast.fontWeight};
    color: ${({ theme }) => theme.colors.neutral.text.strong};

    padding: 12px 8px;
    vertical-align: middle;
    box-sizing: border-box;
  }
`;
var BannerContainer = styled48.div`
  display: flex;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.medium};
  justify-content: space-between;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.primary.bg.weak};
  box-sizing: border-box;

  div {
    display: flex;
    gap: ${({ theme }) => theme.spacing.xsmall};
    align-items: center;
  }
`;
var PaginatorContainer = styled48.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
`;
var PaginatorLeftContainer = styled48.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  width: 100%;
`;
var PaginatorRigthContainer = styled48.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: ${({ theme }) => theme.spacing["2xlarge"]};

  div {
    display: flex;
    align-items: center;
  }
`;
var PaginationButton = styled48.button`
  display: flex;
  width: ${({ theme }) => theme.spacing["2xlarge"]};
  height: ${({ theme }) => theme.spacing["2xlarge"]};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;

  :hover {
    background: ${({ theme }) => theme.colors.neutral.bg.weak};
  }
  :disabled {
    background: none;
    cursor: default;
  }

  ${({ selected }) => selected && css17`
      background: ${({ theme }) => theme.colors.primary.bg.weak};
    `}
`;
var CardCellContainer = styled48.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`;
var ActionCellContainer = styled48.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
`;

// src/Table/Pagination.tsx
import { jsx as jsx54, jsxs as jsxs36 } from "react/jsx-runtime";
var RowsPerPage = [
  { label: "5", value: "5" },
  { label: "10", value: "10" },
  { label: "25", value: "25" },
  { label: "50", value: "50" }
];
var Pagination = ({ table, totalElements }) => {
  const {
    getState,
    setPageSize,
    previousPage,
    getCanPreviousPage,
    nextPage,
    getCanNextPage,
    setPageIndex,
    getPageCount
  } = table;
  const calculateResults = () => {
    const pageSize = getState().pagination.pageSize;
    const pageIndex = getState().pagination.pageIndex;
    const firstNumber = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
    const lastNumber = pageIndex === 0 ? pageSize > totalElements ? totalElements : pageSize : pageSize * pageIndex + 1 > totalElements ? totalElements : pageSize * (pageIndex + 1);
    return `Resultados: ${firstNumber} - ${lastNumber} de ${totalElements}`;
  };
  const numberToArray = () => {
    const numero = getPageCount();
    const currentPage = getState().pagination.pageIndex;
    const pages = [];
    if (numero > 4) {
      if (currentPage === 0) {
        pages.push(1, 2, 3, "...", numero);
      } else {
        if (currentPage + 1 === numero) {
          pages.push(1, "...", numero - 2, numero - 1, numero);
        } else {
          pages.push(
            1,
            currentPage > 1 && "...",
            currentPage + 1,
            currentPage + 2 < numero && currentPage + 2,
            currentPage + 2 < numero - 1 && "...",
            numero
          );
        }
      }
    } else {
      for (let i = 0; i < numero; i++) {
        pages.push(i + 1);
      }
    }
    return pages;
  };
  return /* @__PURE__ */ jsxs36(PaginatorContainer, {
    children: [
      /* @__PURE__ */ jsxs36(PaginatorLeftContainer, {
        children: [
          /* @__PURE__ */ jsx54(Text, {
            size: "s",
            children: "\xCDtems por p\xE1gina:"
          }),
          /* @__PURE__ */ jsx54(Select, {
            options: RowsPerPage,
            fullWidth: true,
            defaultValue: getState().pagination.pageSize.toString(),
            onChange: (e) => setPageSize(Number(e.target.value)),
            "data-testid": "pg-size-cg"
          })
        ]
      }),
      /* @__PURE__ */ jsxs36(PaginatorRigthContainer, {
        children: [
          /* @__PURE__ */ jsx54(Text, {
            size: "s",
            children: calculateResults()
          }),
          /* @__PURE__ */ jsxs36("div", {
            children: [
              /* @__PURE__ */ jsx54(PaginationButton, {
                onClick: () => previousPage(),
                disabled: !getCanPreviousPage(),
                "data-testid": "prv-pg",
                children: /* @__PURE__ */ jsx54(Icon, {
                  name: "ChevronLeftIcon",
                  size: "S",
                  color: !getCanPreviousPage() ? "neutralWeak" : "neutralStrong"
                })
              }),
              numberToArray().map((value, index) => {
                return typeof value === "number" ? /* @__PURE__ */ jsx54(PaginationButton, {
                  selected: value === getState().pagination.pageIndex + 1,
                  onClick: () => value - 1 !== getState().pagination.pageIndex && setPageIndex(value - 1),
                  children: /* @__PURE__ */ jsx54(Text, {
                    as: "span",
                    size: "s",
                    color: value === getState().pagination.pageIndex + 1 ? "primaryStrong" : "neutralStrong",
                    children: value
                  })
                }, "btn" + index) : /* @__PURE__ */ jsx54(Text, {
                  as: "span",
                  color: "neutralWeak",
                  size: "s",
                  children: value
                });
              }),
              /* @__PURE__ */ jsx54(PaginationButton, {
                onClick: () => nextPage(),
                disabled: !getCanNextPage(),
                "data-testid": "nxt-pg",
                children: /* @__PURE__ */ jsx54(Icon, {
                  name: "ChevronRightIcon",
                  size: "S",
                  color: !getCanNextPage() ? "neutralWeak" : "neutralStrong"
                })
              })
            ]
          })
        ]
      })
    ]
  });
};

// src/DropdownButton/DropdownButton.tsx
import styled49, { keyframes as keyframes4 } from "styled-components";
import { Menu as MenuInner, MenuItem as MenuItem2 } from "@szhsin/react-menu";
import { jsx as jsx55, jsxs as jsxs37 } from "react/jsx-runtime";
var menuShow = keyframes4`
  from {
    opacity: 0;
  }
`;
var menuHide = keyframes4`
  to {
    opacity: 0;
  }
`;
var Menu2 = styled49(MenuInner)`
  .szh-menu {
    box-sizing: border-box;
    background: ${({ theme }) => theme.colors.neutral.bg.weaker};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    padding: ${({ theme }) => `${theme.spacing.xsmall} 0px`};
    box-shadow: ${({ theme }) => theme.elevation[40]};
    box-sizing: border-box;
    width: 200px;
    z-index: ${({ theme }) => theme.zindex[20]};

    &--state-opening {
      animation: ${menuShow} 0.15s ease-out;
    }

    &--state-closing {
      animation: ${menuHide} 0.2s ease-out forwards;
    }

    &__item {
      display: flex;
      padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
      align-items: center;
      gap: ${({ theme }) => theme.spacing.xsmall};
      border-radius: ${({ theme }) => theme.borderRadius["2xsmall"]};
      margin-top: ${({ theme }) => theme.spacing["2xsmall"]};
      box-sizing: border-box;
      cursor: pointer;
      transition: all 0.5s;

      &:hover {
        background: ${({ theme }) => theme.colors.neutral.bg.weak};
      }
    }
  }
`;
var DropdownButton = ({
  variant = "button",
  buttonVariant = "filled",
  buttonText,
  items,
  disabled
}) => {
  if (items.length > 4) {
    throw new Error("No podes ingresar mas de 4 items");
  }
  return /* @__PURE__ */ jsx55(Menu2, {
    align: "end",
    menuButton: ({ open }) => !disabled ? /* @__PURE__ */ jsx55("div", {
      children: /* @__PURE__ */ jsx55(Button, {
        "data-testid": "ddw-button-test",
        variant: variant === "icon" ? "ghost" : buttonVariant,
        icon: variant === "icon" ? "MoreVerticalIcon" : open ? "ChevronUpIcon" : "ChevronDownIcon",
        iconAlign: "right",
        size: variant === "icon" ? "small" : "medium",
        children: variant === "button" ? buttonText : void 0
      })
    }) : /* @__PURE__ */ jsx55(Button, {
      variant: variant === "icon" ? "ghost" : buttonVariant,
      icon: variant === "icon" ? "MoreVerticalIcon" : "ChevronDownIcon",
      iconAlign: "right",
      size: variant === "icon" ? "small" : "medium",
      disabled: true,
      children: variant === "button" ? buttonText : void 0
    }),
    portal: true,
    children: items.map((item, index) => /* @__PURE__ */ jsxs37(MenuItem2, {
      role: "button",
      onClick: item.onClick,
      children: [
        item.icon && /* @__PURE__ */ jsx55(Icon, {
          name: item.icon,
          size: "S",
          color: item.destructive ? "danger" : "neutral"
        }),
        /* @__PURE__ */ jsx55(Text, {
          color: item.destructive ? "danger" : "neutral",
          size: "s",
          as: "span",
          children: item.label
        })
      ]
    }, index + "btn"))
  });
};
DropdownButton.displayName = "DropdownButton";

// src/Table/Rows.tsx
import { jsx as jsx56, jsxs as jsxs38 } from "react/jsx-runtime";
var SelectionCell = {
  id: "selection",
  header: ({ table }) => /* @__PURE__ */ jsx56(Checkbox, {
    isChecked: table.getIsAllRowsSelected(),
    isIndeterminate: table.getIsSomeRowsSelected(),
    onChange: table.getToggleAllRowsSelectedHandler(),
    size: "s"
  }),
  cell: ({ row }) => /* @__PURE__ */ jsx56(Checkbox, {
    isChecked: row.getIsSelected(),
    isDisabled: !row.getCanSelect(),
    onChange: row.getToggleSelectedHandler(),
    size: "s"
  }),
  size: 40,
  enableSorting: false,
  meta: {
    align: "center"
  }
};
var CardCell = ({ cardName, cardNumber }) => /* @__PURE__ */ jsxs38(CardCellContainer, {
  children: [
    /* @__PURE__ */ jsx56(CardFlag, {
      name: cardName,
      size: "S"
    }),
    cardNumber && /* @__PURE__ */ jsx56(Text, {
      size: "s",
      color: "neutralStrong",
      children: cardNumber
    })
  ]
});
var DoubleTextCell = ({ children, secondaryText }) => /* @__PURE__ */ jsxs38("div", {
  children: [
    /* @__PURE__ */ jsx56(Text, {
      size: "s",
      color: "neutralStrong",
      children
    }),
    /* @__PURE__ */ jsx56(Text, {
      variant: "caption",
      children: secondaryText
    })
  ]
});
var DoubleActionCell = ({ button, items }) => /* @__PURE__ */ jsxs38(ActionCellContainer, {
  children: [
    /* @__PURE__ */ jsx56(Button, {
      size: "small",
      variant: "ghost",
      onClick: button.action,
      children: button.text
    }),
    /* @__PURE__ */ jsx56(DropdownButton, {
      variant: "icon",
      items
    })
  ]
});

// src/Table/Table.tsx
import { jsx as jsx57, jsxs as jsxs39 } from "react/jsx-runtime";
var Table = ({
  data,
  columns,
  pageIndex,
  totalElements,
  pageSize,
  pageCount,
  withPagination,
  onPaginationChange,
  withSorting,
  onSortChange,
  withSelection,
  onSelectionChange,
  useBannerSelection,
  withRowClick,
  onRowClick
}) => {
  const [pagination, setPagination] = useState28({
    pageIndex: pageIndex != null ? pageIndex : 0,
    pageSize: pageSize != null ? pageSize : 5
  });
  const [rowSelection, setRowSelection] = useState28({});
  const [sorting, setSorting] = useState28([]);
  const table = useReactTable({
    data,
    columns: Boolean(withSelection) ? [SelectionCell, ...columns] : columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount,
    state: {
      pagination,
      rowSelection,
      sorting
    },
    enableRowSelection: withSelection != null ? withSelection : false,
    enableSorting: withSorting != null ? withSorting : false,
    manualSorting: true,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting
  });
  useEffect22(() => {
    if (onPaginationChange) {
      onPaginationChange(pagination);
    }
  }, [pagination, onPaginationChange]);
  useEffect22(() => {
    if (onSelectionChange) {
      onSelectionChange(rowSelection);
    }
  }, [rowSelection, onSelectionChange]);
  useEffect22(() => {
    if (onSortChange) {
      onSortChange(sorting);
    }
  }, [sorting, onSortChange]);
  const handleRowClick = (event, row) => {
    const ignoredTags = ["button", "svg", "path", "input", "li", "span"];
    if (!ignoredTags.includes(event.target.tagName.toLowerCase())) {
      withRowClick && onRowClick && onRowClick(row);
    }
  };
  return /* @__PURE__ */ jsxs39(TableContainer, {
    children: [
      useBannerSelection && useBannerSelection.label && useBannerSelection.label.length > 0 && withSelection && Object.entries(rowSelection).length !== 0 && /* @__PURE__ */ jsxs39(BannerContainer, {
        children: [
          /* @__PURE__ */ jsxs39("div", {
            children: [
              /* @__PURE__ */ jsx57(Text, {
                children: `Tenes ${Object.entries(rowSelection).length} elementos seleccionados`
              }),
              /* @__PURE__ */ jsx57(Button, {
                size: "small",
                variant: "ghost",
                onClick: () => setRowSelection({}),
                children: "Limpiar selecci\xF3n"
              })
            ]
          }),
          /* @__PURE__ */ jsx57(Button, {
            size: "small",
            onClick: () => useBannerSelection.func(),
            children: useBannerSelection.label
          })
        ]
      }),
      /* @__PURE__ */ jsx57(TableBorder, {
        children: /* @__PURE__ */ jsxs39("table", {
          children: [
            /* @__PURE__ */ jsx57(TableHead, {
              children: table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx57("tr", {
                children: headerGroup.headers.map((header) => {
                  var _a, _b, _c;
                  return /* @__PURE__ */ jsx57("th", {
                    align: (_b = (_a = header.column.columnDef.meta) == null ? void 0 : _a.align) != null ? _b : "left",
                    children: /* @__PURE__ */ jsxs39("div", {
                      onClick: header.column.getToggleSortingHandler(),
                      role: header.column.getCanSort() ? "button" : void 0,
                      className: header.column.getCanSort() ? ((_c = header.column.columnDef.meta) == null ? void 0 : _c.align) === "right" ? "cursor-pointer cursor-pointer-right" : "cursor-pointer" : "",
                      children: [
                        /* @__PURE__ */ jsx57(Text, {
                          variant: "caption",
                          color: "neutralStrong",
                          as: "span",
                          children: flexRender(header.column.columnDef.header, header.getContext())
                        }),
                        header.column.getIsSorted() && /* @__PURE__ */ jsx57(Icon, {
                          name: header.column.getIsSorted() === "asc" ? "ArrowUpIcon" : "ArrowDownIcon",
                          size: "S",
                          color: "neutralStrong"
                        })
                      ]
                    })
                  }, header.id);
                })
              }, headerGroup.id))
            }),
            /* @__PURE__ */ jsx57(TableBody, {
              wihtHover: withRowClick,
              children: table.getRowModel().rows.map((row) => /* @__PURE__ */ jsx57("tr", {
                className: row.getIsSelected() ? "row-selected" : "",
                onClick: (event) => handleRowClick(event, row.original),
                children: row.getVisibleCells().map((cell) => {
                  var _a, _b;
                  return /* @__PURE__ */ jsx57("td", {
                    style: {
                      width: cell.column.getSize()
                    },
                    align: (_b = (_a = cell.column.columnDef.meta) == null ? void 0 : _a.align) != null ? _b : "left",
                    children: flexRender(cell.column.columnDef.cell, cell.getContext())
                  }, cell.id);
                })
              }, row.id))
            })
          ]
        })
      }),
      table !== void 0 && withPagination && /* @__PURE__ */ jsx57(Pagination, {
        table,
        totalElements: totalElements != null ? totalElements : 0
      })
    ]
  });
};
Table.displayName = "Table";

// src/FileUploader/FileUploader.tsx
import { useEffect as useEffect23 } from "react";
import { useDropzone } from "react-dropzone";

// src/FileUploader/styles.tsx
import styled50 from "styled-components";
var UploaderContainer = styled50.div`
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: ${({ fullWidth }) => fullWidth ? "100%" : "312px"};
  border: ${({ theme, isDragActive }) => isDragActive ? theme.colors.primary.border.default : theme.colors.neutral.border.default}
    2px ${({ isDragActive }) => isDragActive ? "solid" : "dashed"};
  border-radius: ${({ theme }) => theme.spacing.medium};
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.xlarge};
  background-color: ${({ isDragActive, theme, disabled }) => disabled ? theme.colors.neutral.bg.weak : isDragActive ? theme.colors.primary.bg.weak : theme.colors.neutral.bg.weaker};

  a {
    color: ${({ theme }) => theme.colors.info.text.default};
  }
`;
var IconContainer5 = styled50.div`
  > svg {
    padding: 4px;
    background-color: ${({ theme, disabled }) => disabled ? theme.colors.neutral.bg.weak : theme.colors.primary.bg.weak};
    border-radius: ${({ theme }) => theme.spacing.xsmall};
  }
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`;
var GlobalContainer2 = styled50.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;
`;
var DropzoneMessageContainer = styled50.div`
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2xsmall"]};
`;
var FileItemContainer = styled50.div`
  flex-direction: row;
  display: flex;
  box-sizing: border-box;
  align-items: center;
  min-width: 210px;
  max-width: 312px;
  /* width: max-content; */
  height: 60px;
  padding: ${({ theme }) => theme.spacing.small};
  align-items: center;
  background-color: ${({ theme, error }) => !error ? theme.colors.neutral.bg.weaker : theme.colors.danger.bg.weak};
  border: 1px solid
    ${({ theme, error }) => !error ? theme.colors.neutral.border.default : theme.colors.danger.border.default};
  border-radius: ${({ theme }) => theme.spacing.xsmall};
`;
var FileItemContent = styled50.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xsmall};
  align-items: flex-start;
  svg:first-of-type {
    margin-bottom: auto;
  }
`;
var FilenameContainer = styled50.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 176px;
`;
var FileItemTexts = styled50.div`
  /*  display: flex;
  flex-direction: column; */
  margin-bottom: auto;
  align-self: flex-start;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
var RightIconContainer = styled50.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-left: auto;
  :hover {
    cursor: ${({ withAction }) => withAction ? "pointer" : "default"};
  }
`;

// src/FileUploader/functions.tsx
var fileTypeCorrelationObject = {
  ".pdf": {
    mimeType: "application/pdf",
    typesArray: [".pdf"]
  },
  ".png": {
    mimeType: "image/png",
    typesArray: [".png"]
  },
  ".jpeg": {
    mimeType: "image/jpeg",
    typesArray: [".jpeg", ".jpg"]
  },
  ".jpg": {
    mimeType: "image/jpeg",
    typesArray: [".jpeg", ".jpg"]
  },
  ".gif": {
    mimeType: "image/gif",
    typesArray: [".gif"]
  },
  ".doc": {
    mimeType: "application/msword",
    typesArray: [".doc"]
  },
  ".docx": {
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    typesArray: [".docx"]
  },
  ".txt": {
    mimeType: "text/plain",
    typesArray: [".txt"]
  },
  ".rtf": {
    mimeType: "application/rtf",
    typesArray: [".rtf"]
  },
  ".zip": {
    mimeType: "application/zip",
    typesArray: [".zip"]
  },
  ".rar": {
    mimeType: "application/vnd.rar",
    typesArray: [".rar"]
  },
  ".mp3": {
    mimeType: "audio/mpeg",
    typesArray: [".mp3"]
  },
  ".mpeg": {
    mimeType: "video/mpeg",
    typesArray: [".mpeg"]
  },
  ".mp4": {
    mimeType: "video/mp4",
    typesArray: [".mp4"]
  },
  ".wav": {
    mimeType: "audio/wav",
    typesArray: [".wav"]
  },
  ".csv": {
    mimeType: "text/csv",
    typesArray: [".csv"]
  },
  ".xml": {
    mimeType: "application/xml",
    typesArray: [".xml"]
  },
  ".xlsx": {
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    typesArray: [".xlsx"]
  },
  ".xls": {
    mimeType: "application/vnd.ms-excel",
    typesArray: [".xls"]
  }
};
var returnAcceptedFiletypeObject = (fileTypesArray) => {
  let returnObject = {};
  fileTypesArray.forEach((fileType) => {
    const correlationObjectByKey = fileTypeCorrelationObject[fileType];
    returnObject = {
      ...returnObject,
      [correlationObjectByKey.mimeType]: correlationObjectByKey.typesArray
    };
  });
  return returnObject;
};

// src/FileUploader/FileUploader.tsx
import { jsx as jsx58, jsxs as jsxs40 } from "react/jsx-runtime";
var FileUploader = ({
  disabled,
  error,
  errorMessage,
  requirementsDescription,
  handleSelectedFiles,
  handleRejectedFiles,
  handleAcceptedFiles,
  openDialog,
  maxFileSize,
  multipleFiles,
  maxFileNumber,
  fullWidth,
  acceptedFileTypes
}) => {
  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive, open } = useDropzone({
    disabled,
    multiple: multipleFiles,
    maxSize: maxFileSize,
    maxFiles: maxFileNumber,
    onDropAccepted: handleAcceptedFiles,
    onDropRejected: handleRejectedFiles,
    accept: acceptedFileTypes && acceptedFileTypes[0] ? returnAcceptedFiletypeObject(acceptedFileTypes) : void 0
  });
  useEffect23(() => {
    handleSelectedFiles && handleSelectedFiles(acceptedFiles, fileRejections);
  }, [acceptedFiles, fileRejections]);
  useEffect23(() => {
    if (openDialog)
      open();
  }, [openDialog]);
  return /* @__PURE__ */ jsxs40(GlobalContainer2, {
    children: [
      /* @__PURE__ */ jsxs40(UploaderContainer, {
        ...getRootProps({ className: "dropzone" }),
        isDragActive,
        disabled,
        fullWidth,
        "data-testid": "uploader-container-fileuploader",
        children: [
          /* @__PURE__ */ jsx58(IconContainer5, {
            "data-testid": "share-icon-container-fileuploader",
            children: /* @__PURE__ */ jsx58(Icon, {
              name: "ShareIcon",
              size: "M",
              color: !disabled ? "primary" : "neutralWeak",
              "data-testid": "share-icon-fileuploader"
            })
          }),
          /* @__PURE__ */ jsxs40(Text, {
            color: disabled ? "neutralWeak" : "neutralStrong",
            "data-testid": "select-file-text-fileuploader",
            children: [
              /* @__PURE__ */ jsx58("a", {
                children: "Seleccion\xE1"
              }),
              " un archivo o arrastralo"
            ]
          }),
          /* @__PURE__ */ jsx58(Text, {
            variant: "caption",
            size: "s",
            color: "neutralWeak",
            children: requirementsDescription
          }),
          /* @__PURE__ */ jsx58("input", {
            ...getInputProps()
          })
        ]
      }),
      error && /* @__PURE__ */ jsxs40(DropzoneMessageContainer, {
        children: [
          /* @__PURE__ */ jsx58(Icon, {
            name: "WarningTriangleIcon",
            color: "danger",
            size: "S"
          }),
          /* @__PURE__ */ jsx58(Text, {
            variant: "caption",
            size: "s",
            color: "danger",
            children: errorMessage
          })
        ]
      })
    ]
  });
};
FileUploader.displayName = "FileUploader";

// src/FileUploader/FileItem.tsx
import { jsx as jsx59, jsxs as jsxs41 } from "react/jsx-runtime";
var FileItem = ({
  fileName,
  fileSize,
  status = "uploading",
  onClickDelete,
  onClickRetry,
  errorMessage = "Error al cargar el archivo"
}) => {
  return /* @__PURE__ */ jsxs41(FileItemContainer, {
    error: status === "error",
    children: [
      /* @__PURE__ */ jsxs41(FileItemContent, {
        children: [
          /* @__PURE__ */ jsx59(Icon, {
            name: "FileIcon",
            size: "S",
            color: status === "error" ? "danger" : "neutralWeak"
          }),
          /* @__PURE__ */ jsxs41(FileItemTexts, {
            children: [
              /* @__PURE__ */ jsx59(FilenameContainer, {
                children: /* @__PURE__ */ jsx59(Text, {
                  variant: "caption",
                  size: "s",
                  role: "p",
                  color: status === "error" ? "danger" : "neutralStrong",
                  children: fileName
                })
              }),
              /* @__PURE__ */ jsx59(Text, {
                variant: "caption",
                color: status === "error" ? "danger" : "neutralWeak",
                children: status !== "error" ? fileSize : errorMessage
              })
            ]
          })
        ]
      }),
      status === "uploading" && /* @__PURE__ */ jsxs41(RightIconContainer, {
        children: [
          /* @__PURE__ */ jsx59(Spinner, {
            size: "small"
          }),
          /* @__PURE__ */ jsx59(Button, {
            icon: "DeleteIcon",
            variant: "ghost",
            color: "primary",
            onClick: onClickDelete
          })
        ]
      }),
      status === "error" && /* @__PURE__ */ jsxs41(RightIconContainer, {
        withAction: true,
        children: [
          /* @__PURE__ */ jsx59(Icon, {
            name: "ReplayIcon",
            color: "danger",
            size: "S",
            onClick: onClickRetry
          }),
          /* @__PURE__ */ jsx59(Button, {
            icon: "DeleteIcon",
            color: "destructive",
            variant: "ghost",
            onClick: onClickDelete
          })
        ]
      }),
      status === "success" && /* @__PURE__ */ jsxs41(RightIconContainer, {
        children: [
          /* @__PURE__ */ jsx59(Icon, {
            size: "M",
            name: "CheckCircleFilledIcon",
            color: "success"
          }),
          /* @__PURE__ */ jsx59(Button, {
            icon: "DeleteIcon",
            color: "primary",
            variant: "ghost",
            onClick: onClickDelete
          })
        ]
      })
    ]
  });
};
FileItem.displayName = "FileItem";

// src/Ilustration/Ilustration.tsx
import { useEffect as useEffect24, useState as useState29 } from "react";
import { jsx as jsx60 } from "react/jsx-runtime";
var getIlustrationUrl = (name, size, variant) => {
  return getCDNUrl(`assets/images/ilustrations/${variant}/${size}/${name}.svg`);
};
var Ilustration = ({ name, size = "medium" }) => {
  const { variant } = useTheme();
  const [ilustLink, setIlustLink] = useState29("");
  if (!name)
    return null;
  useEffect24(() => {
    const link = getIlustrationUrl(name, size, variant);
    setIlustLink(link);
  }, [name, size]);
  return /* @__PURE__ */ jsx60("img", {
    src: ilustLink,
    alt: name
  });
};
Ilustration.displayName = "Ilustration";

// src/Skeleton/styles.tsx
import styled51, { css as css18, keyframes as keyframes5 } from "styled-components";
var fadeIn = () => keyframes5`
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
`;
var fadeOut = () => keyframes5`
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  `;
var loading = () => keyframes5`
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: -100% 50%;
    }
  `;
var Wrapper2 = styled51.div`
  position: relative;
`;
var SkeletonDiv = styled51.div`
  border-radius: ${({ theme, borderRadius }) => theme.borderRadius[borderRadius]};
  top: 0;
  left: 0;
  animation: ${loading()} 1.5s infinite linear;
  background-size: 200% 100%;
  &.fade-out {
    animation: ${fadeOut()} 0.2s ease forwards;
    position: absolute;
  }

  height: ${({ height }) => height};
  width: ${({ width }) => width};
  background-color: ${({ theme }) => theme.colors.neutral.bg.default};
  background-image: ${({ theme }) => `linear-gradient(90deg, ${theme.colors.neutral.bg.default}, ${theme.colors.neutral.bg.weaker}, ${theme.colors.neutral.bg.default})`};

  ${({ type }) => type === "circle" && css18`
      border-radius: 50%;
    `}
`;
var Content = styled51.div`
  animation: ${fadeIn()} 1s ease forwards;
  &.fade-in {
    animation: ${fadeIn()} 1s ease forwards;
  }
`;

// src/Skeleton/Skeleton.tsx
import { jsx as jsx61, jsxs as jsxs42 } from "react/jsx-runtime";
var Skeleton = ({
  height,
  width,
  type = "line",
  isLoading = true,
  children,
  borderRadius = "small"
}) => {
  return /* @__PURE__ */ jsxs42(Wrapper2, {
    children: [
      /* @__PURE__ */ jsx61(SkeletonDiv, {
        "data-testId": "skeleton-animation",
        height,
        width,
        type,
        borderRadius,
        className: isLoading ? "" : "fade-out"
      }),
      !isLoading && /* @__PURE__ */ jsx61(Content, {
        "data-testId": "skeleton-content",
        children
      })
    ]
  });
};
Skeleton.displayName = "Skeleton";
export {
  Accordion,
  AccordionItem,
  Alert_default as Alert,
  AmountInput,
  Avatar,
  Badge,
  Brand,
  Breadcrumb,
  Button,
  CardCell,
  CardFlag,
  Checkbox,
  Chip,
  CodeInput_default as CodeInput,
  Container,
  Datepicker_default as Datepicker,
  Dot,
  DoubleActionCell,
  DoubleTextCell,
  DropdownButton,
  FileItem,
  FileUploader,
  Grid_default as Grid,
  Header,
  Heading,
  Icon,
  Ilustration,
  Isotype,
  Menu,
  Modal,
  ModalConfirmation,
  Multiselect,
  NewFeaturePopup_default as NewFeaturePopup,
  NotificationBadge,
  PasswordInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Provider,
  RadioGroup,
  Radiobutton,
  SearchInput2 as SearchInput,
  Select,
  SideSheet_default as SideSheet,
  Skeleton,
  Spinner,
  Stepper,
  Table,
  Tabs,
  Text,
  TextArea,
  TextInput,
  TextLink,
  ToastPortal_default as ToastPortal,
  Toogle,
  Tooltip,
  useSteps,
  useTheme
};
